import React, { useState, useEffect, useRef, useCallback } from 'react';
import './index.css';
import UploadArea from './components/UploadArea';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { detectDeviceType, readImageBitmap, detectDeviceTypesForFiles, getDeviceName, getSpecificDeviceName } from './lib/device';
import { detectMobileScreenshot, buildMobileScreenshotPrompt } from './lib/mobileScreenshotHandler';
// import { stitchImagesIntoArtboard } from './lib/imageStitching';
import MockupCanvas from './components/MockupCanvas';
import ErrorBoundary from './components/ErrorBoundary';
import PricingPage from './components/PricingPage';
import UpdatesPage from './components/UpdatesPage';
import TermsPage from './components/TermsPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import SuccessPage from './components/SuccessPage';
import ProfilePicture from './components/ProfilePicture';
import { UserProvider, useUser } from './components/UserContext';
import { NotificationProvider, useNotifications } from './components/NotificationSystem';
import { DarkModeProvider, useDarkMode } from './components/DarkModeContext';
import DarkModeToggle from './components/ui/dark-mode-toggle';
import { Switch } from './components/ui/switch';
import CardStack from './components/CardStack';
import AutoCarousel from './components/AutoCarousel';
import { auth, db, storage } from './lib/firebase';
import { signInWithPopup, signInWithRedirect, signOut, GoogleAuthProvider, getRedirectResult } from 'firebase/auth';
import { collection, addDoc, query, where, onSnapshot, doc, deleteDoc, updateDoc, orderBy, limit, startAfter, serverTimestamp, getDoc } from 'firebase/firestore';
import { uploadImageToStorage, blobToBase64 } from './lib/storage';
import { redirectToCheckout, PLAN_CONFIGS } from './lib/stripe';
import confetti from 'canvas-confetti';
import { trackEvent } from './lib/analytics';
import { validateEmail, validateRequired, validateLength, sanitizeString } from './lib/errorHandler';
import { INPUT_LIMITS, PIPELINES, API_ENDPOINTS, MODELS, getModelCredits, getModelDisplayName } from './config/constants';

// Feedback Modal component - defined outside to prevent recreation
const FeedbackModal = ({ 
  showFeedbackModal, 
  setShowFeedbackModal,
  feedbackCategory, 
  setFeedbackCategory, 
  feedbackName, 
  setFeedbackName, 
  feedbackEmail, 
  setFeedbackEmail, 
  feedbackSubject, 
  setFeedbackSubject, 
  hasFeedbackText, 
  setHasFeedbackText, 
  feedbackLoading, 
  setFeedbackLoading, 
  user, 
  showError, 
  showSuccess,
  setSuccessModalData,
  setShowSuccessModal
}) => {
  const textareaRef = useRef(null);
  
  const handleSubmit = async () => {
    const currentValue = textareaRef.current?.value || '';
    
    try {
      // Validate mandatory fields with proper validation
      validateRequired(feedbackName.trim(), 'Name');
      validateRequired(feedbackEmail.trim(), 'Email');
      validateRequired(currentValue.trim(), 'Message');
      
      // Validate email format
      if (!validateEmail(feedbackEmail.trim())) {
        showError('Please enter a valid email address');
        return;
      }
      
      // Validate length limits
      validateLength(feedbackName.trim(), 'Name', INPUT_LIMITS.NAME_MAX_LENGTH);
      validateLength(feedbackEmail.trim(), 'Email', INPUT_LIMITS.EMAIL_MAX_LENGTH);
      validateLength(currentValue.trim(), 'Message', INPUT_LIMITS.FEEDBACK_MESSAGE_MAX_LENGTH);
      validateLength(feedbackSubject.trim(), 'Subject', INPUT_LIMITS.SUBJECT_MAX_LENGTH);
      
    } catch (error) {
      showError(error.message);
      return;
    }
    
    setFeedbackLoading(true);
    try {
      // Check if user is authenticated
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      console.log('Submitting feedback:', { userId: user.uid, category: feedbackCategory });
      
      // Save to Firestore with sanitized data
      const feedbackData = {
        userId: user.uid,
        userEmail: sanitizeString(feedbackEmail.trim()) || user.email,
        userName: sanitizeString(feedbackName.trim()) || user.displayName || 'Anonymous',
        userDisplayName: sanitizeString(feedbackName.trim()) || user.displayName || 'Anonymous',
        category: feedbackCategory,
        subject: sanitizeString(feedbackSubject.trim()),
        message: sanitizeString(currentValue.trim()),
        timestamp: new Date(),
        status: 'new'
      };
      
      console.log('Saving to Firestore:', feedbackData);
      
      // Add timeout to prevent hanging (increased to 30 seconds)
      const firestorePromise = addDoc(collection(db, 'feedback'), feedbackData);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Firestore operation timed out')), 30000)
      );
      
      try {
        await Promise.race([firestorePromise, timeoutPromise]);
        console.log('Successfully saved to Firestore');
      } catch (firestoreError) {
        console.warn('Firestore save failed, continuing with email only:', firestoreError);
        console.warn('Firestore error details:', firestoreError.message, firestoreError.code);
        
        // Check if it's a network/connection error and provide helpful info
        if (firestoreError.message.includes('400') || firestoreError.message.includes('Bad Request')) {
          console.warn('Firestore connection issue detected - this may be a temporary network problem');
        }
        
        // Check for specific Firestore errors
        if (firestoreError.code === 'unavailable' || firestoreError.code === 'deadline-exceeded') {
          console.warn('Firestore service temporarily unavailable');
        }
        
        // Continue with email notification even if Firestore fails
      }
      
      // Send email notification
      try {
        console.log('Sending email notification...');
        const emailResponse = await fetch('/api/send-feedback-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(feedbackData),
        });
        
        if (!emailResponse.ok) {
          throw new Error(`Email service returned ${emailResponse.status}`);
        }
        
        console.log('Email notification sent successfully');
      } catch (emailError) {
        console.warn('Failed to send email notification:', emailError);
        // Don't fail the whole operation if email fails
      }
      
      // Show success modal with headline and body text
      const successModalData = {
        headline: "Feedback submitted successfully!",
        text: "Thank you for your input. We'll review your feedback and get back to you if needed."
      };
      setSuccessModalData(successModalData);
      setShowSuccessModal(true);
      
      setShowFeedbackModal(false);
      setFeedbackCategory('feature');
      setHasFeedbackText(false);
      setFeedbackName('');
      setFeedbackEmail('');
      setFeedbackSubject('');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showError(`Failed to submit feedback: ${error.message}`);
    } finally {
      setFeedbackLoading(false);
    }
  };
  
  if (!showFeedbackModal) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-surface-3 rounded-lg p-6 w-full max-w-md mx-4 relative">
        <h3 className="text-lg font-semibold text-foreground mb-2">Feature Requests</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Do you have suggestions for improvement, found an error, or other criticism?
        </p>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-foreground mb-2">
            Category
          </label>
          <Select value={feedbackCategory} onValueChange={setFeedbackCategory}>
            <SelectTrigger className="w-full" tabIndex={0}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[70]">
              <SelectItem value="feature">Feature Request</SelectItem>
              <SelectItem value="bug">Bug Report</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-foreground mb-2">
            Name*
          </label>
          <input
            type="text"
            value={feedbackName}
            onChange={(e) => setFeedbackName(e.target.value)}
            placeholder="Your name"
            tabIndex={1}
            className="w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-surface-2 text-foreground border border-input"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-foreground mb-2">
            E-mail*
          </label>
          <input
            type="email"
            value={feedbackEmail}
            onChange={(e) => setFeedbackEmail(e.target.value)}
            placeholder="Your email"
            tabIndex={2}
            className="w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-surface-2 text-foreground border border-input"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-foreground mb-2">
            Subject
          </label>
          <input
            type="text"
            value={feedbackSubject}
            onChange={(e) => setFeedbackSubject(e.target.value)}
            placeholder="Brief subject line"
            tabIndex={3}
            className="w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-surface-2 text-foreground border border-input"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-foreground mb-2">
            Your message*
          </label>
          <textarea
            ref={textareaRef}
            onChange={(e) => setHasFeedbackText(e.target.value.trim().length > 0)}
            placeholder="Describe your suggestion, bug report, or feedback..."
            tabIndex={4}
            className="w-full h-32 p-3 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-ring bg-surface-2 text-foreground"
            style={{ 
              direction: 'ltr', 
              textAlign: 'left',
              unicodeBidi: 'embed'
            }}
            dir="ltr"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
        </div>
        
        <div className="flex gap-3">
          <Button
            onClick={() => {
              setShowFeedbackModal(false);
              setFeedbackCategory('feature');
              setHasFeedbackText(false);
              setFeedbackName('');
              setFeedbackEmail('');
              setFeedbackSubject('');
            }}
            variant="outline"
            className="flex-1"
            disabled={feedbackLoading}
            tabIndex={5}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
            disabled={!hasFeedbackText || !feedbackName.trim() || !feedbackEmail.trim() || feedbackLoading}
            tabIndex={6}
          >
            {feedbackLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                <span>Submitting...</span>
              </div>
            ) : (
              'Submit'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Safari detection utility
const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

// Utility function to validate image URLs
const isValidImageUrl = (url) => {
  if (!url) return false;
  
  // In development, allow blob URLs and localhost URLs
  if (process.env.NODE_ENV !== 'production') {
    return url.startsWith('blob:') || url.startsWith('data:') || url.startsWith('http');
  }
  
  // In production, block blob URLs and localhost URLs
  if (url.startsWith('blob:')) return false;
  if (url.includes('localhost') || url.includes('127.0.0.1')) return false;
  
  // Allow only valid HTTP/HTTPS URLs or data URLs
  return url.startsWith('http') || url.startsWith('data:');
};

const CATEGORIES = [
  { value: 'auto', label: 'Auto' },
  { value: 'color', label: 'Color' },
  { value: 'custom', label: 'Custom' },
  { value: 'simple', label: 'Simple' },
  { value: 'office', label: 'Office' },
  { value: 'fiori', label: 'Studio' },
  { value: 'stones', label: 'Concrete' },
  { value: 'gamer', label: 'Gamer' },
  { value: 'view', label: 'View' },
  { value: 'ocean', label: 'Ocean' },
  { value: 'designer', label: 'Designer' },
  { value: 'coffee', label: 'Café' },
  { value: 'bar', label: 'Bar' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'business', label: 'Business' },
  { value: 'artsy', label: 'Music' },
  { value: 'abstract', label: 'Abstract' },
  { value: 'sport', label: 'Sport' },
  { value: 'nature', label: 'Nature' },
  { value: 'luxury', label: 'Luxury' },
  { value: 'space', label: 'Space' },
  { value: 'octopus', label: 'Octopus' },
  { value: 'paraglide', label: 'Paraglide' },
  { value: 'rooftop', label: 'Rooftop' },
  { value: 'car', label: 'Car' },
  { value: 'skatepark', label: 'Skatepark' },
  { value: 'duck', label: 'Duck' },
  { value: 'rave', label: 'Rave' },
  { value: 'garage', label: 'Garage' },
  { value: 'store', label: 'Store' },
  { value: 'paris', label: 'Paris' },
  { value: 'nyc', label: 'NYC' },
  { value: 'tokyo', label: 'Tokyo' },
];


function AppContent({ onShowCookiePreferences, showSuccessModal, setShowSuccessModal, successModalData, setSuccessModalData, showSuccessPage, setShowSuccessPage }) {
  const { user, userData, loading, mockupCount, deductMockup, addMockups, isNewUser, clearNewUserFlag } = useUser();
  const { showMockupGenerated, showMockupExhausted, showError, showSuccess } = useNotifications();
  const { isDarkMode } = useDarkMode();
  const imageRef = useRef(null);
  
  // Initialize Google Analytics consent on app load
  useEffect(() => {
    try {
      const cookieConsent = localStorage.getItem('cookieConsent');
      if (cookieConsent && window.gtag) {
        const consent = JSON.parse(cookieConsent);
        window.gtag('consent', 'update', {
          analytics_storage: consent.analytics ? 'granted' : 'denied'
        });
      } else if (window.gtag) {
        // Default to analytics enabled if no consent stored
        window.gtag('consent', 'update', {
          analytics_storage: 'granted'
        });
      }
    } catch (error) {
      console.warn('Error initializing Google Analytics consent:', error);
      // Default to analytics enabled on error
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted'
        });
      }
    }
  }, []);

  // Show welcome modal for new users
  useEffect(() => {
    if (isNewUser && user) {
      const welcomeModalData = {
        credits: 3,
        headline: "Welcome to RandomMockups!",
        text: "3 credits added to your account. You can use them for generating new mockups or editing existing ones."
      };
      setSuccessModalData(welcomeModalData);
      setTimeout(() => {
        setShowSuccessModal(true);
        clearNewUserFlag(); // Clear the flag so it doesn't show again
      }, 100);
    }
  }, [isNewUser, user, setSuccessModalData, setShowSuccessModal, clearNewUserFlag]);

  // No scroll effects - static image to prevent flickering

  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [generatedMockups, setGeneratedMockups] = useState([]);
  
  // Global drag & drop state
  const [isGlobalDragging, setIsGlobalDragging] = useState(false);
  const globalDragCounter = useRef(0);
  
  
  // Filter problematic URLs (blob URLs from localhost, invalid URLs)
  useEffect(() => {
    // Helper function to check if URL is problematic
    const isProblematicUrl = (url) => {
      if (!url) return false;
      // Check if it's a blob URL from localhost (most common issue)
      if (url.startsWith('blob:http://localhost') || url.startsWith('blob:https://localhost')) {
        return true;
      }
      // Check if it's any blob URL in production
      if (process.env.NODE_ENV === 'production' && url.startsWith('blob:')) {
        return true;
      }
      // Check for localhost in any URL in production
      if (process.env.NODE_ENV === 'production' && (url.includes('localhost') || url.includes('127.0.0.1'))) {
        return true;
      }
      return false;
    };
    
    // Check for problematic URLs in both url and imageUrl fields
    const problematicMockups = generatedMockups.filter(m => {
      const hasProblematicUrl = isProblematicUrl(m.url) || isProblematicUrl(m.imageUrl);
      return hasProblematicUrl;
    });
    
    if (problematicMockups.length > 0) {
      console.warn('⚠️ Found mockups with problematic URLs:', problematicMockups.length);
      problematicMockups.forEach(m => {
        console.warn('  - Mockup ID:', m.id, 'URL:', m.url?.substring(0, 50), 'imageUrl:', m.imageUrl?.substring(0, 50));
      });
      
      // Filter out mockups with problematic URLs
      console.log('🧹 Filtering out', problematicMockups.length, 'mockups with problematic URLs');
      setGeneratedMockups(prev => prev.filter(mockup => {
        const urlOk = !mockup.url || !isProblematicUrl(mockup.url);
        const imageUrlOk = !mockup.imageUrl || !isProblematicUrl(mockup.imageUrl);
        return urlOk && imageUrlOk;
      }));
    }
  }, [generatedMockups]);
  const [category, setCategory] = useState('auto');
  const [pipeline, setPipeline] = useState(() => {
    // Always default to V1 (Original pipeline)
    // Since Experimental is hidden, force V1 even if V3 was previously selected
    const saved = localStorage.getItem('pipelineSelection');
    if (saved === PIPELINES.V3) {
      // Reset to V1 if Experimental was previously selected
      localStorage.setItem('pipelineSelection', PIPELINES.V1);
      return PIPELINES.V1;
    }
    return saved === PIPELINES.V1 ? PIPELINES.V1 : PIPELINES.V1;
  });
  const [isPipelineExpanded, setIsPipelineExpanded] = useState(false);
  const [isStyleExpanded, setIsStyleExpanded] = useState(false);
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [isExportExpanded, setIsExportExpanded] = useState(false);
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);
  const [isModelExpanded, setIsModelExpanded] = useState(false);
  const [selectedModel, setSelectedModel] = useState(() => {
    const saved = localStorage.getItem('selectedModel');
    // Migrate from removed Nano Banana Pro to Nano Banana 2
    if (saved === 'gemini-3-pro-image-preview' || saved === 'gemini-3-pro-image') {
      localStorage.setItem('selectedModel', MODELS.GEMINI_3_1_FLASH.id);
      return MODELS.GEMINI_3_1_FLASH.id;
    }
    return saved || MODELS.GEMINI_3_1_FLASH.id;
  });
  const [fullPrompt, setFullPrompt] = useState('');
  const [processing, setProcessing] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [expandedMockupDetails, setExpandedMockupDetails] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [mockupZoomLevels] = useState(new Map());
  const [showEditPromptModal, setShowEditPromptModal] = useState(false);
  const [editingMockup, setEditingMockup] = useState(null);
  const [deviceType, setDeviceType] = useState('desktop');
  const [deviceTypes, setDeviceTypes] = useState([]);

  // Format device type for display
  const getDeviceTypeDisplayName = (type) => {
    switch (type) {
      case 'desktop': return 'Desktop';
      case 'ultrawide': return 'Wide';
      case 'tablet': return 'Tablet';
      case 'mobile': return 'Mobile';
      default: return 'Desktop';
    }
  };

  // Format view angle for display
  const getViewAngleDisplayName = (angle) => {
    switch (angle) {
      case 'right-angled': return 'Right';
      case 'left-angled': return 'Left';
      case 'frontal': return 'Front';
      case 'isometric': return 'Isometric';
      default: return 'Right';
    }
  };
  
  // Zoom handlers
  const handleZoomIn = async (mockupId) => {
    // Find the mockup from generatedMockups
    const mockup = generatedMockups.find(m => m.id === mockupId);
    if (!mockup) {
      showError('Mockup not found');
      return;
    }

    if (!user || !userData) {
      showError('Please sign in to zoom mockups');
      return;
    }

    // Check if user has enough mockups for the selected model
    const requiredCredits = getModelCredits(selectedModel);
    if (userData.mockupsRemaining < requiredCredits) {
      showError(`Insufficient credits. Need ${requiredCredits} credit${requiredCredits > 1 ? 's' : ''} to zoom in.`);
      return;
    }

    setProcessing(true);
    setOpenDropdown(null);

    // Create a placeholder mockup in the feed at the exact position where the real one will appear
    const placeholderId = `processing-placeholder-zoom-in-${Date.now()}`;
    const placeholderStableKey = `placeholder-zoom-in-${Date.now()}-${mockup.category || 'custom'}-${mockup.deviceType || 'desktop'}`;
    const placeholderMockup = {
      id: placeholderId,
      stableKey: placeholderStableKey,
      url: null,
      imageUrl: null,
      imageData: null,
      category: mockup.category || 'custom',
      deviceType: mockup.deviceType || 'desktop',
      showDevice: mockup.showDevice !== undefined ? mockup.showDevice : true,
      showBokeh: mockup.showBokeh !== undefined ? mockup.showBokeh : true,
      showHands: mockup.showHands !== undefined ? mockup.showHands : true,
      showNotch: mockup.showNotch !== undefined ? mockup.showNotch : false,
      viewAngle: mockup.viewAngle || 'right-angled',
      aspectRatio: mockup.aspectRatio || '4:3',
      fileCount: mockup.fileCount || 1,
      pipeline: mockup.pipeline || pipeline,
      timestamp: new Date(),
      createdAt: new Date(),
      isPlaceholder: true
    };
    
    // Add placeholder to feed immediately at the top
    setGeneratedMockups(prev => [placeholderMockup, ...prev]);
    console.log('✅ Added placeholder mockup for zoom in:', placeholderId);

    try {
      // Convert mockup image URL to File/Blob
      let imageFile;
      const imageUrl = mockup.url || mockup.imageUrl || mockup.imageData;

      if (imageUrl.startsWith('data:')) {
        // Base64 data URL
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        imageFile = new File([blob], 'mockup.png', { type: 'image/png' });
      } else if (imageUrl.startsWith('blob:')) {
        // Blob URL
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        imageFile = new File([blob], 'mockup.png', { type: 'image/png' });
      } else {
        // Firebase Storage URL or other URL
        const response = await fetch(imageUrl);
        if (!response.ok) throw new Error('Failed to fetch image');
        const blob = await response.blob();
        imageFile = new File([blob], 'mockup.png', { type: 'image/png' });
      }

      // Generate zoom-in prompt
      const zoomInPrompt = `ZOOM IN OPERATION: Crop and enlarge the central focus area of this image. 
      
PRIMARY OBJECTIVE - ZOOM IN:
- Crop the image to focus on the central subject (device and its immediate surroundings)
- Enlarge the cropped area to fill the entire canvas while maintaining aspect ratio
- Preserve all details, sharpness, and quality of the original image
- Keep the device and screen content as the main focal point
- Maintain professional composition and framing

PRESERVE EXACTLY:
- All device details, materials, and textures
- All screen content (text, UI elements, colors, layouts)
- Lighting and shadows on the device
- Color accuracy and contrast

DO NOT:
- Add new elements or objects
- Change the environment or background significantly
- Modify screen content
- Distort or stretch the image unnaturally

The result should look like a closer, more detailed view of the original mockup, as if the camera moved closer to the subject.`;

      // Call enhance API with zoom-in prompt
      const enhanced = await callServerEnhanceAPI(imageFile, { 
        extraPrompt: zoomInPrompt.trim(),
        aspectRatio: mockup.aspectRatio || '4:3'
      });

      // Convert blob to base64
      const base64Image = await blobToBase64(enhanced);

      // Create URL
      let newMockupUrl;
      if (user && db) {
        newMockupUrl = base64Image;
      } else {
        newMockupUrl = URL.createObjectURL(enhanced);
      }

      // Validate URL
      if (!isValidImageUrl(newMockupUrl)) {
        throw new Error('Generated invalid image URL');
      }

      // Deduct credits immediately after successful generation (before saving to Firestore)
      const creditCost = getModelCredits(selectedModel);
      if (user) {
        console.log(`💳 Deducting ${creditCost} credit(s) for zoom in...`);
        const success = await deductMockup(creditCost);
        if (success) {
          console.log('✅ Credits deducted successfully');
          trackEvent('zoom_mockup', {
            action: 'zoom_in',
            cost: creditCost,
            model: selectedModel
          });
        } else {
          console.error('❌ Failed to deduct credits');
          throw new Error('Failed to deduct credits');
        }
      }

      // Create new mockup
      const tempId = `temp-${Date.now()}`;
      const stableKey = `mockup-${Date.now()}-${mockup.category || 'custom'}-${mockup.deviceType || 'desktop'}`;

      const newMockup = {
        id: tempId,
        stableKey: stableKey,
        url: newMockupUrl,
        imageUrl: newMockupUrl,
        imageData: base64Image,
        category: mockup.category || 'custom',
        deviceType: mockup.deviceType || 'desktop',
        showDevice: mockup.showDevice !== undefined ? mockup.showDevice : true,
        showBokeh: mockup.showBokeh !== undefined ? mockup.showBokeh : true,
        showHands: mockup.showHands !== undefined ? mockup.showHands : true,
        showNotch: mockup.showNotch !== undefined ? mockup.showNotch : false,
        viewAngle: mockup.viewAngle || 'right-angled',
        aspectRatio: mockup.aspectRatio || '4:3',
        fileCount: mockup.fileCount || 1,
        pipeline: mockup.pipeline || pipeline,
        timestamp: new Date(),
        createdAt: new Date(),
        modelName: selectedModel,
        isEdited: true,
        originalMockupId: mockup.id,
        zoomAction: 'zoom_in'
      };

      // Replace placeholder with real mockup in the same position
      console.log('✅ Replacing placeholder mockup with real mockup for zoom in');
      setGeneratedMockups(prev => {
        const index = prev.findIndex(m => m.id === placeholderId || m.stableKey === placeholderStableKey);
        if (index !== -1) {
          // Replace placeholder at the same position
          const updated = [...prev];
          updated[index] = {
            ...newMockup,
            stableKey: prev[index].stableKey || newMockup.stableKey // Preserve placeholder's stableKey
          };
          return updated;
        } else {
          // If placeholder not found, just add to top
          return [newMockup, ...prev];
        }
      });

      // Save to Firestore if authenticated
      if (user && db) {
        try {
          const docRef = await addDoc(collection(db, 'mockups'), {
            userId: user.uid,
            imageUrl: newMockupUrl,
            imageData: base64Image,
            category: newMockup.category,
            deviceType: newMockup.deviceType,
            showDevice: newMockup.showDevice,
            showBokeh: newMockup.showBokeh,
            showHands: newMockup.showHands,
            showNotch: newMockup.showNotch,
            viewAngle: newMockup.viewAngle,
            aspectRatio: newMockup.aspectRatio,
            fileCount: newMockup.fileCount,
            pipeline: newMockup.pipeline,
            modelName: newMockup.modelName,
            isEdited: true,
            originalMockupId: mockup.id,
            zoomAction: 'zoom_in',
            createdAt: serverTimestamp(),
            timestamp: new Date()
          });

          // Update local mockup with Firestore ID (replace placeholder if still exists)
          setGeneratedMockups(prev => {
            // First try to find by placeholder
            const placeholderIndex = prev.findIndex(m => m.id === placeholderId || m.stableKey === placeholderStableKey);
            if (placeholderIndex !== -1) {
              const updated = [...prev];
              updated[placeholderIndex] = {
                ...updated[placeholderIndex],
                id: docRef.id
              };
              return updated;
            }
            // Otherwise find by tempId
            return prev.map(m => m.id === tempId ? { ...m, id: docRef.id } : m);
          });

          // Upload to Firebase Storage
          try {
            const storageUrl = await uploadImageToStorage(enhanced, user.uid, docRef.id);
            await updateDoc(doc(db, 'mockups', docRef.id), {
              imageUrl: storageUrl
            });
            setGeneratedMockups(prev => {
              // Find by Firestore ID or placeholder
              const index = prev.findIndex(m => m.id === docRef.id || m.id === placeholderId || m.stableKey === placeholderStableKey);
              if (index !== -1) {
                const updated = [...prev];
                updated[index] = {
                  ...updated[index],
                  url: storageUrl,
                  imageUrl: storageUrl,
                  id: docRef.id
                };
                return updated;
              }
              return prev.map(m => m.id === docRef.id ? { ...m, url: storageUrl, imageUrl: storageUrl } : m);
            });
          } catch (storageError) {
            console.warn('Failed to upload to storage, using base64:', storageError);
          }
        } catch (error) {
          console.error('Failed to save to Firestore:', error);
        }
      }

      // Reset state
      setProcessing(false);

      // Show success
      showMockupGenerated();
    } catch (error) {
      console.error('Zoom in failed:', error);
      showError(error.message || 'Failed to zoom in mockup');
      
      // Clean up placeholder on error
      setGeneratedMockups(prev => prev.filter(m => m.id !== placeholderId && !m.isPlaceholder));
      
      setProcessing(false);
    }
  };

  const handleZoomOut = async (mockupId) => {
    // Find the mockup from generatedMockups
    const mockup = generatedMockups.find(m => m.id === mockupId);
    if (!mockup) {
      showError('Mockup not found');
      return;
    }

    if (!user || !userData) {
      showError('Please sign in to zoom mockups');
      return;
    }

    // Check if user has enough mockups for the selected model
    const requiredCredits = getModelCredits(selectedModel);
    if (userData.mockupsRemaining < requiredCredits) {
      showError(`Insufficient credits. Need ${requiredCredits} credit${requiredCredits > 1 ? 's' : ''} to zoom out.`);
      return;
    }

    setProcessing(true);
    setOpenDropdown(null);

    // Create a placeholder mockup in the feed at the exact position where the real one will appear
    const placeholderId = `processing-placeholder-zoom-out-${Date.now()}`;
    const placeholderStableKey = `placeholder-zoom-out-${Date.now()}-${mockup.category || 'custom'}-${mockup.deviceType || 'desktop'}`;
    const placeholderMockup = {
      id: placeholderId,
      stableKey: placeholderStableKey,
      url: null,
      imageUrl: null,
      imageData: null,
      category: mockup.category || 'custom',
      deviceType: mockup.deviceType || 'desktop',
      showDevice: mockup.showDevice !== undefined ? mockup.showDevice : true,
      showBokeh: mockup.showBokeh !== undefined ? mockup.showBokeh : true,
      showHands: mockup.showHands !== undefined ? mockup.showHands : true,
      showNotch: mockup.showNotch !== undefined ? mockup.showNotch : false,
      viewAngle: mockup.viewAngle || 'right-angled',
      aspectRatio: mockup.aspectRatio || '4:3',
      fileCount: mockup.fileCount || 1,
      pipeline: mockup.pipeline || pipeline,
      timestamp: new Date(),
      createdAt: new Date(),
      isPlaceholder: true
    };
    
    // Add placeholder to feed immediately at the top
    setGeneratedMockups(prev => [placeholderMockup, ...prev]);
    console.log('✅ Added placeholder mockup for zoom out:', placeholderId);

    try {
      // Convert mockup image URL to File/Blob
      let imageFile;
      const imageUrl = mockup.url || mockup.imageUrl || mockup.imageData;

      if (imageUrl.startsWith('data:')) {
        // Base64 data URL
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        imageFile = new File([blob], 'mockup.png', { type: 'image/png' });
      } else if (imageUrl.startsWith('blob:')) {
        // Blob URL
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        imageFile = new File([blob], 'mockup.png', { type: 'image/png' });
      } else {
        // Firebase Storage URL or other URL
        const response = await fetch(imageUrl);
        if (!response.ok) throw new Error('Failed to fetch image');
        const blob = await response.blob();
        imageFile = new File([blob], 'mockup.png', { type: 'image/png' });
      }

      // Generate zoom-out prompt - invent surrounding environment (DRAMATIC ZOOM OUT)
      const zoomOutPrompt = `ZOOM OUT OPERATION: Dramatically expand the view to show MUCH MORE of the surrounding environment. This is an EXTREME zoom out - show significantly more context around the device.
      
PRIMARY OBJECTIVE - DRAMATIC ZOOM OUT:
- EXPAND THE VIEW DRAMATICALLY - show 2-3x more of the surrounding environment than the original
- Create a MUCH WIDER field of view as if the camera moved significantly further away (like switching from a 50mm to a 24mm lens)
- Keep the device and screen content as the central focal point, but make it appear smaller relative to the expanded frame
- Invent and generate a realistic, contextually appropriate environment that makes sense
- Maintain the same style, lighting, and atmosphere as the original image
- The device should occupy approximately 30-40% of the frame (down from filling most of it)

ENVIRONMENT GENERATION GUIDELINES - BE AGGRESSIVE:
- Analyze the existing environment in the image (indoor/outdoor, style, colors, lighting)
- EXTEND THE ENVIRONMENT SIGNIFICANTLY in all directions - show much more context
- Add contextually appropriate elements generously:
  * If indoor: extend the room dramatically, add furniture, decor, architectural elements, walls, windows, ceiling details, floor details that match the style
  * If outdoor: extend the landscape extensively, add natural elements, sky, ground, horizon, or urban context that fits
  * If minimalist: keep the extended environment clean and minimal but show much more space around
  * If professional: add office or workspace elements, desks, chairs, walls, windows, lighting fixtures that make sense
  * Show background elements, midground context, and foreground details
- Maintain consistent lighting direction and quality throughout the expanded scene
- Preserve the color palette and mood of the original
- Create depth with multiple layers: foreground, midground, background

PRESERVE EXACTLY:
- All device details, materials, and textures (device should look identical, just smaller in frame)
- All screen content (text, UI elements, colors, layouts) - pixel perfect
- The device's position and orientation (centered, same angle)
- Lighting and shadows on the device itself

DO NOT:
- Modify screen content
- Change the device's appearance
- Alter the fundamental style or atmosphere
- Add elements that clash with the existing environment

The result should look like a DRAMATICALLY wider-angle view of the same scene - as if you stepped back 5-10 feet from the original position. The device should be clearly visible and prominent, but now shown in a much more expansive, complete environment with significantly more context visible around it.`;

      // Call enhance API with zoom-out prompt
      const enhanced = await callServerEnhanceAPI(imageFile, { 
        extraPrompt: zoomOutPrompt.trim(),
        aspectRatio: mockup.aspectRatio || '4:3'
      });

      // Convert blob to base64
      const base64Image = await blobToBase64(enhanced);

      // Create URL
      let newMockupUrl;
      if (user && db) {
        newMockupUrl = base64Image;
      } else {
        newMockupUrl = URL.createObjectURL(enhanced);
      }

      // Validate URL
      if (!isValidImageUrl(newMockupUrl)) {
        throw new Error('Generated invalid image URL');
      }

      // Deduct credits immediately after successful generation (before saving to Firestore)
      const creditCost = getModelCredits(selectedModel);
      if (user) {
        console.log(`💳 Deducting ${creditCost} credit(s) for zoom out...`);
        const success = await deductMockup(creditCost);
        if (success) {
          console.log('✅ Credits deducted successfully');
          trackEvent('zoom_mockup', {
            action: 'zoom_out',
            cost: creditCost,
            model: selectedModel
          });
        } else {
          console.error('❌ Failed to deduct credits');
          throw new Error('Failed to deduct credits');
        }
      }

      // Create new mockup
      const tempId = `temp-${Date.now()}`;
      const stableKey = `mockup-${Date.now()}-${mockup.category || 'custom'}-${mockup.deviceType || 'desktop'}`;

      const newMockup = {
        id: tempId,
        stableKey: stableKey,
        url: newMockupUrl,
        imageUrl: newMockupUrl,
        imageData: base64Image,
        category: mockup.category || 'custom',
        deviceType: mockup.deviceType || 'desktop',
        showDevice: mockup.showDevice !== undefined ? mockup.showDevice : true,
        showBokeh: mockup.showBokeh !== undefined ? mockup.showBokeh : true,
        showHands: mockup.showHands !== undefined ? mockup.showHands : true,
        showNotch: mockup.showNotch !== undefined ? mockup.showNotch : false,
        viewAngle: mockup.viewAngle || 'right-angled',
        aspectRatio: mockup.aspectRatio || '4:3',
        fileCount: mockup.fileCount || 1,
        pipeline: mockup.pipeline || pipeline,
        timestamp: new Date(),
        createdAt: new Date(),
        modelName: selectedModel,
        isEdited: true,
        originalMockupId: mockup.id,
        zoomAction: 'zoom_out'
      };

      // Replace placeholder with real mockup in the same position
      console.log('✅ Replacing placeholder mockup with real mockup for zoom out');
      setGeneratedMockups(prev => {
        const index = prev.findIndex(m => m.id === placeholderId || m.stableKey === placeholderStableKey);
        if (index !== -1) {
          // Replace placeholder at the same position
          const updated = [...prev];
          updated[index] = {
            ...newMockup,
            stableKey: prev[index].stableKey || newMockup.stableKey // Preserve placeholder's stableKey
          };
          return updated;
        } else {
          // If placeholder not found, just add to top
          return [newMockup, ...prev];
        }
      });

      // Save to Firestore if authenticated
      if (user && db) {
        try {
          const docRef = await addDoc(collection(db, 'mockups'), {
            userId: user.uid,
            imageUrl: newMockupUrl,
            imageData: base64Image,
            category: newMockup.category,
            deviceType: newMockup.deviceType,
            showDevice: newMockup.showDevice,
            showBokeh: newMockup.showBokeh,
            showHands: newMockup.showHands,
            showNotch: newMockup.showNotch,
            viewAngle: newMockup.viewAngle,
            aspectRatio: newMockup.aspectRatio,
            fileCount: newMockup.fileCount,
            pipeline: newMockup.pipeline,
            modelName: newMockup.modelName,
            isEdited: true,
            originalMockupId: mockup.id,
            zoomAction: 'zoom_out',
            createdAt: serverTimestamp(),
            timestamp: new Date()
          });

          // Update local mockup with Firestore ID (replace placeholder if still exists)
          setGeneratedMockups(prev => {
            // First try to find by placeholder
            const placeholderIndex = prev.findIndex(m => m.id === placeholderId || m.stableKey === placeholderStableKey);
            if (placeholderIndex !== -1) {
              const updated = [...prev];
              updated[placeholderIndex] = {
                ...updated[placeholderIndex],
                id: docRef.id
              };
              return updated;
            }
            // Otherwise find by tempId
            return prev.map(m => m.id === tempId ? { ...m, id: docRef.id } : m);
          });

          // Upload to Firebase Storage
          try {
            const storageUrl = await uploadImageToStorage(enhanced, user.uid, docRef.id);
            await updateDoc(doc(db, 'mockups', docRef.id), {
              imageUrl: storageUrl
            });
            setGeneratedMockups(prev => {
              // Find by Firestore ID or placeholder
              const index = prev.findIndex(m => m.id === docRef.id || m.id === placeholderId || m.stableKey === placeholderStableKey);
              if (index !== -1) {
                const updated = [...prev];
                updated[index] = {
                  ...updated[index],
                  url: storageUrl,
                  imageUrl: storageUrl,
                  id: docRef.id
                };
                return updated;
              }
              return prev.map(m => m.id === docRef.id ? { ...m, url: storageUrl, imageUrl: storageUrl } : m);
            });
          } catch (storageError) {
            console.warn('Failed to upload to storage, using base64:', storageError);
          }
        } catch (error) {
          console.error('Failed to save to Firestore:', error);
        }
      }

      // Reset state
      setProcessing(false);

      // Show success
      showMockupGenerated();
    } catch (error) {
      console.error('Zoom out failed:', error);
      showError(error.message || 'Failed to zoom out mockup');
      
      // Clean up placeholder on error
      setGeneratedMockups(prev => prev.filter(m => m.id !== placeholderId && !m.isPlaceholder));
      
      setProcessing(false);
    }
  };

  const getZoomLevel = (mockupId) => {
    return mockupZoomLevels.get(mockupId) || 1.0;
  };

  // Edit with Prompt handlers
  const handleEditWithPrompt = (mockup) => {
    setEditingMockup(mockup);
    setShowEditPromptModal(true);
    setOpenDropdown(null);
  };

  const handleEditPromptGenerate = async () => {
    // Get value from textarea ref (uncontrolled component)
    const textarea = document.querySelector('[data-edit-prompt-textarea]');
    const promptValue = textarea?.value?.trim() || '';
    if (!editingMockup || !promptValue) return;

    if (!user || !userData) {
      showError('Please sign in to edit mockups');
      return;
    }

    // Check if user has enough mockups for the selected model
    const requiredCredits = getModelCredits(selectedModel);
    if (userData.mockupsRemaining < requiredCredits) {
      showError(`Insufficient credits. Need ${requiredCredits} credit${requiredCredits > 1 ? 's' : ''} to edit with prompt.`);
      setShowEditPromptModal(false);
      return;
    }

    setProcessing(true);
    setShowEditPromptModal(false);

    // Create a placeholder mockup in the feed at the exact position where the real one will appear
    const placeholderId = `processing-placeholder-edit-${Date.now()}`;
    const placeholderStableKey = `placeholder-edit-${Date.now()}-${editingMockup.category || 'custom'}-${editingMockup.deviceType || 'desktop'}`;
    const placeholderMockup = {
      id: placeholderId,
      stableKey: placeholderStableKey,
      url: null,
      imageUrl: null,
      imageData: null,
      category: editingMockup.category || 'custom',
      deviceType: editingMockup.deviceType || 'desktop',
      showDevice: editingMockup.showDevice !== undefined ? editingMockup.showDevice : true,
      showBokeh: editingMockup.showBokeh !== undefined ? editingMockup.showBokeh : true,
      showHands: editingMockup.showHands !== undefined ? editingMockup.showHands : true,
      showNotch: editingMockup.showNotch !== undefined ? editingMockup.showNotch : false,
      viewAngle: editingMockup.viewAngle || 'right-angled',
      aspectRatio: editingMockup.aspectRatio || '4:3',
      fileCount: editingMockup.fileCount || 1,
      pipeline: editingMockup.pipeline || pipeline,
      timestamp: new Date(),
      createdAt: new Date(),
      isPlaceholder: true
    };
    
    // Add placeholder to feed immediately at the top
    setGeneratedMockups(prev => [placeholderMockup, ...prev]);
    console.log('✅ Added placeholder mockup for edit with prompt:', placeholderId);

    try {
      // Convert mockup image URL to File/Blob
      let imageFile;
      const imageUrl = editingMockup.url || editingMockup.imageUrl || editingMockup.imageData;

      if (imageUrl.startsWith('data:')) {
        // Base64 data URL
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        imageFile = new File([blob], 'mockup.png', { type: 'image/png' });
      } else if (imageUrl.startsWith('blob:')) {
        // Blob URL
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        imageFile = new File([blob], 'mockup.png', { type: 'image/png' });
      } else {
        // Firebase Storage URL or other URL
        const response = await fetch(imageUrl);
        if (!response.ok) throw new Error('Failed to fetch image');
        const blob = await response.blob();
        imageFile = new File([blob], 'mockup.png', { type: 'image/png' });
      }

      // Call enhance API with custom prompt
      const enhanced = await callServerEnhanceAPI(imageFile, { 
        extraPrompt: promptValue.trim(),
        aspectRatio: editingMockup.aspectRatio || '4:3'
      });

      // Convert blob to base64
      const base64Image = await blobToBase64(enhanced);

      // Create URL
      let newMockupUrl;
      if (user && db) {
        newMockupUrl = base64Image;
      } else {
        newMockupUrl = URL.createObjectURL(enhanced);
      }

      // Validate URL
      if (!isValidImageUrl(newMockupUrl)) {
        throw new Error('Generated invalid image URL');
      }

      // Deduct credits immediately after successful generation (before saving to Firestore)
      // This ensures credits are deducted even if Firestore save fails
      const creditCost = getModelCredits(selectedModel);
      if (user) {
        console.log(`💳 Deducting ${creditCost} credit(s) for edit with prompt...`);
        const success = await deductMockup(creditCost);
        if (success) {
          console.log('✅ Credits deducted successfully');
          trackEvent('edit_mockup_with_prompt', {
            cost: creditCost,
            model: selectedModel
          });
        } else {
          console.error('❌ Failed to deduct credits');
          throw new Error('Failed to deduct credits');
        }
      }

      // Create new mockup
      const tempId = `temp-${Date.now()}`;
      const stableKey = `mockup-${Date.now()}-${editingMockup.category || 'custom'}-${editingMockup.deviceType || 'desktop'}`;

      const newMockup = {
        id: tempId,
        stableKey: stableKey,
        url: newMockupUrl,
        imageUrl: newMockupUrl,
        imageData: base64Image,
        category: editingMockup.category || 'custom',
        deviceType: editingMockup.deviceType || 'desktop',
        showDevice: editingMockup.showDevice !== undefined ? editingMockup.showDevice : true,
        showBokeh: editingMockup.showBokeh !== undefined ? editingMockup.showBokeh : true,
        showHands: editingMockup.showHands !== undefined ? editingMockup.showHands : true,
        showNotch: editingMockup.showNotch !== undefined ? editingMockup.showNotch : false,
        viewAngle: editingMockup.viewAngle || 'right-angled',
        aspectRatio: editingMockup.aspectRatio || '4:3',
        fileCount: editingMockup.fileCount || 1,
        pipeline: editingMockup.pipeline || pipeline,
        timestamp: new Date(),
        createdAt: new Date(),
        modelName: selectedModel,
        isEdited: true,
        originalMockupId: editingMockup.id
      };

      // Replace placeholder with real mockup in the same position
      console.log('✅ Replacing placeholder mockup with real mockup for edit with prompt');
      setGeneratedMockups(prev => {
        const index = prev.findIndex(m => m.id === placeholderId || m.stableKey === placeholderStableKey);
        if (index !== -1) {
          // Replace placeholder at the same position
          const updated = [...prev];
          updated[index] = {
            ...newMockup,
            stableKey: prev[index].stableKey || newMockup.stableKey // Preserve placeholder's stableKey
          };
          return updated;
        } else {
          // If placeholder not found, just add to top
          return [newMockup, ...prev];
        }
      });

      // Save to Firestore if authenticated
      if (user && db) {
        try {
          const docRef = await addDoc(collection(db, 'mockups'), {
            userId: user.uid,
            imageUrl: newMockupUrl,
            imageData: base64Image,
            category: newMockup.category,
            deviceType: newMockup.deviceType,
            showDevice: newMockup.showDevice,
            showBokeh: newMockup.showBokeh,
            showHands: newMockup.showHands,
            showNotch: newMockup.showNotch,
            viewAngle: newMockup.viewAngle,
            aspectRatio: newMockup.aspectRatio,
            fileCount: newMockup.fileCount,
            pipeline: newMockup.pipeline,
            modelName: newMockup.modelName,
            isEdited: true,
            originalMockupId: editingMockup.id,
            createdAt: serverTimestamp(),
            timestamp: new Date()
          });

          // Update local mockup with Firestore ID (replace placeholder if still exists)
          setGeneratedMockups(prev => {
            // First try to find by placeholder
            const placeholderIndex = prev.findIndex(m => m.id === placeholderId || m.stableKey === placeholderStableKey);
            if (placeholderIndex !== -1) {
              const updated = [...prev];
              updated[placeholderIndex] = {
                ...updated[placeholderIndex],
                id: docRef.id
              };
              return updated;
            }
            // Otherwise find by tempId
            return prev.map(m => m.id === tempId ? { ...m, id: docRef.id } : m);
          });

          // Upload to Firebase Storage
          try {
            const storageUrl = await uploadImageToStorage(enhanced, user.uid, docRef.id);
            await updateDoc(doc(db, 'mockups', docRef.id), {
              imageUrl: storageUrl
            });
            setGeneratedMockups(prev => {
              // Find by Firestore ID or placeholder
              const index = prev.findIndex(m => m.id === docRef.id || m.id === placeholderId || m.stableKey === placeholderStableKey);
              if (index !== -1) {
                const updated = [...prev];
                updated[index] = {
                  ...updated[index],
                  url: storageUrl,
                  imageUrl: storageUrl,
                  id: docRef.id
                };
                return updated;
              }
              return prev.map(m => m.id === docRef.id ? { ...m, url: storageUrl, imageUrl: storageUrl } : m);
            });
          } catch (storageError) {
            console.warn('Failed to upload to storage, using base64:', storageError);
          }
        } catch (error) {
          console.error('Failed to save to Firestore:', error);
        }
      }

      // Reset state
      setEditingMockup(null);
      setProcessing(false);

      // Show success
      showMockupGenerated();
    } catch (error) {
      console.error('Edit with prompt failed:', error);
      showError(error.message || 'Failed to edit mockup with prompt');
      
      // Clean up placeholder on error
      setGeneratedMockups(prev => prev.filter(m => m.id !== placeholderId && !m.isPlaceholder));
      
      setProcessing(false);
      setEditingMockup(null);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
      if (showUserDropdown && !event.target.closest('.dropdown-container')) {
        setShowUserDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown, showUserDropdown]);
  const [deviceNames, setDeviceNames] = useState([]);
  const [showDevice, setShowDevice] = useState(true);
  const [showBokeh, setShowBokeh] = useState(true);
  const [showHands, setShowHands] = useState(true);
  const [showNotch, setShowNotch] = useState(false);
  const [aspectRatio, setAspectRatio] = useState('4:3');
  const [viewAngle, setViewAngle] = useState('right-angled');
  const [authLoading, setAuthLoading] = useState(false);
  const [headerAuthLoading, setHeaderAuthLoading] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showUpdates, setShowUpdates] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [showColorModal, setShowColorModal] = useState(false);
  const [selectedColor, setSelectedColor] = useState('blue');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mockupToDelete, setMockupToDelete] = useState(null);
  const [showDeleteImageModal, setShowDeleteImageModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null); // { mockup, imageType }
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  // const [feedbackMessage, setFeedbackMessage] = useState(''); // Removed - using uncontrolled textarea
  const [feedbackCategory, setFeedbackCategory] = useState('feature');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [hasFeedbackText, setHasFeedbackText] = useState(false);
  const [feedbackName, setFeedbackName] = useState('');
  const [feedbackEmail, setFeedbackEmail] = useState('');
  const [feedbackSubject, setFeedbackSubject] = useState('');
  const deleteModalRef = useRef(null);
  const deleteImageModalRef = useRef(null);
  
  const [enhancingMockups, setEnhancingMockups] = useState(new Map()); // Map of mockupId -> resolution ('2k' or '4k')
  const sidebarScrollRef = useRef(null);
  const [isSidebarOverflowing, setIsSidebarOverflowing] = useState(false);
  const isMountedRef = useRef(true);
  const processingRedirectRef = useRef(false); // Track if we're processing redirect result
  const authOperationInProgressRef = useRef(false); // Track if any auth operation is in progress
  
  // Pagination state
  const [lastVisibleDoc, setLastVisibleDoc] = useState(null);
  const [hasMoreMockups, setHasMoreMockups] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [usingOrderBy, setUsingOrderBy] = useState(true); // Track if we're using orderBy or simple query

  // Handle URL-based routing
  useEffect(() => {
    const handleRoute = () => {
      const path = window.location.pathname;
      if (path === '/pricing') {
        trackEvent('view_pricing', {
          source: 'landing'
        });
        setShowPricing(true);
        setShowUpdates(false);
        setShowTerms(false);
        setShowPrivacy(false);
      } else if (path === '/updates') {
        setShowUpdates(true);
        setShowPricing(false);
        setShowTerms(false);
        setShowPrivacy(false);
      } else if (path === '/terms') {
        setShowTerms(true);
        setShowPricing(false);
        setShowUpdates(false);
        setShowPrivacy(false);
      } else if (path === '/privacy') {
        setShowPrivacy(true);
        setShowPricing(false);
        setShowUpdates(false);
        setShowTerms(false);
      } else {
        setShowPricing(false);
        setShowUpdates(false);
        setShowTerms(false);
        setShowPrivacy(false);
      }
    };

    // Check initial route
    handleRoute();

    // Listen for navigation changes
    window.addEventListener('popstate', handleRoute);
    
    return () => {
      window.removeEventListener('popstate', handleRoute);
    };
  }, []);



  const canGenerate = (!!file || files.length > 0) && !processing;
  const shouldShowGenerateButton = (!!file || files.length > 0);

  // Check for success page on load - show confetti modal instead of boring page
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      // Handle successful payment - show confetti success modal
      console.log('Payment successful for session:', sessionId);
      
      // Get credits from plan config if available in metadata
      // The metadata should have originalPriceId which we can use to get credits
      const originalPriceId = urlParams.get('plan') || urlParams.get('originalPriceId');
      let credits = null;
      
      // Map of plan IDs to credits (from stripe-config.js PLAN_CONFIGS)
      const planCreditsMap = {
        'small-pack': 5,
        'medium-pack': 15,
        'big-pack': 50,
        'premium-pack': 200,
        'enterprise-pack': 800
      };
      
      if (originalPriceId && planCreditsMap[originalPriceId]) {
        credits = planCreditsMap[originalPriceId];
      }
      
      // Clear URL parameters for clean navigation
      window.history.replaceState({}, '', window.location.pathname);
      
      // Show beautiful confetti success modal
      const modalData = {
        credits: credits,
        headline: "Payment Successful! 🎉",
        text: credits 
          ? `Your ${credits} credits have been added to your account! You can start generating mockups right away.`
          : "Your credits are being added to your account. You can start generating mockups right away!"
      };
      setSuccessModalData(modalData);
      setShowSuccessModal(true);
      
      // Don't show the boring success page
      setShowSuccessPage(false);
    }
  }, [setShowSuccessPage, setSuccessModalData, setShowSuccessModal]);

  // Detect if the left sidebar content overflows vertically.
  useEffect(() => {
    const checkOverflow = () => {
      if (!isMountedRef.current) return;
      const el = sidebarScrollRef.current;
      if (!el) return;
      try {
        setIsSidebarOverflowing(el.scrollHeight > el.clientHeight + 2);
      } catch (error) {
        console.warn('Error checking sidebar overflow:', error);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [file, files, viewAngle, category, showDevice, showBokeh, showHands, showNotch, aspectRatio, processing, isStyleExpanded, isSettingsExpanded, isExportExpanded, isPromptExpanded]);

  // Handle Safari redirect result
  useEffect(() => {
    const handleRedirectResult = async () => {
      if (!auth) return;
      
      // Prevent concurrent calls
      if (processingRedirectRef.current) {
        console.log('🌐 Redirect result already being processed - skipping');
        return;
      }
      
      // Only check for redirect result if we actually initiated a redirect
      const expectingRedirect = sessionStorage.getItem('safariAuthRedirect');
      if (!expectingRedirect) {
        console.log('🌐 No redirect expected - skipping getRedirectResult check');
        return;
      }
      
      // Check if there's an auth operation in progress
      if (authOperationInProgressRef.current) {
        console.log('🌐 Auth operation in progress - waiting before checking redirect result');
        // Wait a bit and try again
        setTimeout(() => {
          if (!processingRedirectRef.current) {
            handleRedirectResult();
          }
        }, 2000);
        return;
      }
      
      processingRedirectRef.current = true;
      
      try {
        const result = await getRedirectResult(auth);
        if (result && result.user) {
          console.log('🌐 Safari redirect authentication successful:', result.user);
          // Clear any URL parameters after successful redirect
          if (window.location.search) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
          // Clear the redirect flag
          sessionStorage.removeItem('safariAuthRedirect');
          // Reset auth operation flag since redirect completed
          authOperationInProgressRef.current = false;
          // The UserContext will handle the auth state change automatically
          // No need to manually set user state here
        } else {
          console.log('🌐 Redirect was expected but no result found');
          // Clear the flag anyway to avoid infinite loops
          sessionStorage.removeItem('safariAuthRedirect');
          authOperationInProgressRef.current = false;
        }
      } catch (error) {
        // Handle Firebase internal assertion errors gracefully
        if (error.message && error.message.includes('INTERNAL ASSERTION FAILED')) {
          console.warn('🌐 Firebase internal assertion error (likely from concurrent auth operations) - ignoring');
          // Clear the flag to prevent repeated attempts
          sessionStorage.removeItem('safariAuthRedirect');
          authOperationInProgressRef.current = false;
          // Don't log as error, this is expected in some edge cases
        } else {
          console.error('🌐 Safari redirect result error:', error);
          // Clear the flag to prevent repeated attempts
          sessionStorage.removeItem('safariAuthRedirect');
          authOperationInProgressRef.current = false;
        }
      } finally {
        processingRedirectRef.current = false;
      }
    };

    // Only run redirect result handling once on mount for Safari
    if (isSafari()) {
      console.log('🌐 Safari detected - checking for redirect result');
      handleRedirectResult();
    }
  }, []);

  // Global error handler for Safari "Pending promise" errors
  useEffect(() => {
    const handleGlobalError = (event) => {
      const error = event.error || event.reason;
      if (error && error.message && 
          (error.message.includes('Pending promise was never set') ||
           error.message.includes('INTERNAL ASSERTION FAILED'))) {
        console.warn('🌐 Safari: Caught Firebase internal assertion error (concurrent auth operations) - ignoring');
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleGlobalError);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleGlobalError);
    };
  }, []);


  // Authentication functions
  const signInWithGoogle = async () => {
    // Prevent multiple simultaneous authentication attempts
    if (authLoading || authOperationInProgressRef.current) {
      console.log('Authentication already in progress, please wait...');
      return;
    }

    if (!auth) {
      console.error('❌ Firebase auth not available');
      alert('Authentication is not available. Please check your Firebase configuration.');
      return;
    }

    setAuthLoading(true);
    authOperationInProgressRef.current = true;
    
    // Set a timeout to automatically reset loading state after 5 seconds for Safari
    const timeoutId = setTimeout(() => {
      console.log('Authentication timeout - resetting loading state');
      setAuthLoading(false);
      authOperationInProgressRef.current = false;
    }, 5000);
    
    try {
      const provider = new GoogleAuthProvider();
      // Add custom parameters to the provider
      provider.addScope('email');
      provider.addScope('profile');
      
      // Force Google to show account selection screen
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      // Check if Safari and use popup method with enhanced handling
      if (isSafari()) {
        console.log('🌐 Safari detected - using enhanced popup method');
        try {
          // For Safari, we'll use popup but with better error handling
          const result = await signInWithPopup(auth, provider);
          clearTimeout(timeoutId);
          authOperationInProgressRef.current = false;
          console.log('🌐 Safari popup authentication successful');
          return result.user;
        } catch (popupError) {
          // Only try redirect for specific errors, not user cancellation
          if (popupError.code === 'auth/popup-closed-by-user' || 
              popupError.code === 'auth/cancelled-popup-request' ||
              popupError.code === 'auth/user-cancelled') {
            console.log('🌐 User closed popup - not trying redirect');
            clearTimeout(timeoutId);
            authOperationInProgressRef.current = false;
            setAuthLoading(false);
            return; // User intentionally cancelled, don't force redirect
          }
          
          console.warn('🌐 Safari popup failed, trying redirect as fallback:', popupError);
          // Wait longer to allow Firebase to clean up popup promise completely
          // Increased from 500ms to 2000ms to ensure Firebase has time to clean up
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Fallback to redirect if popup fails (but not for user cancellation)
          try {
            sessionStorage.setItem('safariAuthRedirect', 'true');
            await signInWithRedirect(auth, provider);
            clearTimeout(timeoutId);
            // Don't reset authOperationInProgressRef here - redirect will handle it
            return;
          } catch (redirectError) {
            console.error('🌐 Both Safari popup and redirect failed:', redirectError);
            sessionStorage.removeItem('safariAuthRedirect'); // Clean up flag
            authOperationInProgressRef.current = false;
            throw redirectError;
          }
        }
      } else {
        const result = await signInWithPopup(auth, provider);
        clearTimeout(timeoutId); // Clear timeout on successful authentication
        authOperationInProgressRef.current = false;
        return result.user;
      }
    } catch (error) {
      console.error('Error signing in with Google:', error);
      authOperationInProgressRef.current = false;
      
      // Handle specific error cases - don't show errors for user cancellations
      if (error.code === 'auth/popup-closed-by-user' || 
          error.code === 'auth/cancelled-popup-request' ||
          error.code === 'auth/user-cancelled') {
        console.log('User cancelled authentication - this is normal behavior');
        clearTimeout(timeoutId);
        setAuthLoading(false);
        return; // Don't show error for user cancellation
      } else if (error.code === 'auth/unauthorized-domain') {
        console.error('Domain not authorized. Please add localhost to Firebase authorized domains.');
        alert('Authentication error: Domain not authorized. Please contact support.');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        console.error('Account exists with different credential');
        alert('An account already exists with this email using a different sign-in method.');
      } else if (error.code === 'auth/popup-blocked') {
        if (isSafari()) {
          console.log('🌐 Safari: Popup blocked, trying redirect method...');
          alert('🌐 Safari: Popup was blocked. Please:\n1. Go to Safari → Preferences → Websites → Pop-up Windows\n2. Set this site to "Allow"\n3. Or try the redirect method below');
        } else {
          console.log('Popup blocked, trying redirect method...');
        }
        // Fallback to redirect method
        try {
          // Wait a bit before trying redirect to ensure popup is cleaned up
          await new Promise(resolve => setTimeout(resolve, 1000));
          const redirectProvider = new GoogleAuthProvider();
          redirectProvider.addScope('email');
          redirectProvider.addScope('profile');
          sessionStorage.setItem('safariAuthRedirect', 'true');
          await signInWithRedirect(auth, redirectProvider);
          clearTimeout(timeoutId);
          // Don't reset authOperationInProgressRef here - redirect will handle it
          return; // The redirect will handle the rest
        } catch (redirectError) {
          console.error('Redirect also failed:', redirectError);
          sessionStorage.removeItem('safariAuthRedirect');
          authOperationInProgressRef.current = false;
          if (isSafari()) {
            alert('🌐 Safari: Both popup and redirect failed. Please:\n1. Clear Safari cache and cookies\n2. Disable "Prevent cross-site tracking" in Safari settings\n3. Try in a private browsing window');
          } else {
            alert('Authentication failed: Please allow popups or try again.');
          }
        }
      } else if (error.code === 'auth/popup-closed-by-user') {
        console.log('Popup was closed by user - this might be due to positioning issues');
        alert('The popup window may have appeared in an awkward position. Please try again and make sure the popup window is visible and accessible.');
      } else {
        console.error('Authentication error:', error.message);
        if (isSafari() && error.message && error.message.includes('Pending promise')) {
          alert(`🌐 Safari: Authentication timed out. Please try:\n1. Clear Safari cache and cookies\n2. Disable "Prevent cross-site tracking" in Safari settings\n3. Try in a private browsing window`);
        } else if (isSafari()) {
          alert(`🌐 Safari Authentication failed: ${error.message}\n\nTry:\n1. Clear Safari cache and cookies\n2. Disable "Prevent cross-site tracking"\n3. Try in a private browsing window`);
        } else {
          alert(`Authentication failed: ${error.message}`);
        }
      }
      
      // Don't re-throw the error to prevent React error boundaries from catching it
      return null;
    } finally {
      clearTimeout(timeoutId);
      setAuthLoading(false);
    }
  };

  const signInWithGoogleHeader = async () => {
    // Prevent multiple simultaneous authentication attempts
    if (headerAuthLoading) {
      console.log('Header authentication already in progress, please wait...');
      return;
    }

    if (!auth) {
      console.error('❌ Firebase auth not available');
      alert('Authentication is not available. Please check your Firebase configuration.');
      return;
    }

    setHeaderAuthLoading(true);
    authOperationInProgressRef.current = true;
    
    // Set a timeout to automatically reset loading state after 5 seconds for Safari
    const timeoutId = setTimeout(() => {
      console.log('Header authentication timeout - resetting loading state');
      setHeaderAuthLoading(false);
      authOperationInProgressRef.current = false;
    }, 5000);
    
    try {
      const provider = new GoogleAuthProvider();
      // Add custom parameters to the provider
      provider.addScope('email');
      provider.addScope('profile');
      
      // Force Google to show account selection screen
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      // Check if Safari and use popup method with enhanced handling
      if (isSafari()) {
        console.log('🌐 Safari detected - using enhanced popup method');
        try {
          // For Safari, we'll use popup but with better error handling
          const result = await signInWithPopup(auth, provider);
          clearTimeout(timeoutId);
          authOperationInProgressRef.current = false;
          console.log('🌐 Safari popup authentication successful');
          return result.user;
        } catch (popupError) {
          // Only try redirect for specific errors, not user cancellation
          if (popupError.code === 'auth/popup-closed-by-user' || 
              popupError.code === 'auth/cancelled-popup-request' ||
              popupError.code === 'auth/user-cancelled') {
            console.log('🌐 User closed popup - not trying redirect');
            clearTimeout(timeoutId);
            authOperationInProgressRef.current = false;
            setHeaderAuthLoading(false);
            return; // User intentionally cancelled, don't force redirect
          }
          
          console.warn('🌐 Safari popup failed, trying redirect as fallback:', popupError);
          // Wait longer to allow Firebase to clean up popup promise completely
          // Increased from 500ms to 2000ms to ensure Firebase has time to clean up
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Fallback to redirect if popup fails (but not for user cancellation)
          try {
            sessionStorage.setItem('safariAuthRedirect', 'true');
            await signInWithRedirect(auth, provider);
            clearTimeout(timeoutId);
            // Don't reset authOperationInProgressRef here - redirect will handle it
            return null; // Will be handled by getRedirectResult
          } catch (redirectError) {
            console.error('🌐 Both popup and redirect failed:', redirectError);
            sessionStorage.removeItem('safariAuthRedirect'); // Clean up flag
            authOperationInProgressRef.current = false;
            throw redirectError;
          }
        }
      } else {
        // For non-Safari browsers, use popup method
        console.log('🌐 Non-Safari browser - using popup method');
        const result = await signInWithPopup(auth, provider);
        clearTimeout(timeoutId);
        authOperationInProgressRef.current = false;
        console.log('🌐 Popup authentication successful');
        return result.user;
      }
    } catch (error) {
      console.error('❌ Authentication error:', error);
      authOperationInProgressRef.current = false;
      clearTimeout(timeoutId);
      
      if (error.code === 'auth/popup-closed-by-user' || 
          error.code === 'auth/cancelled-popup-request' ||
          error.code === 'auth/user-cancelled') {
        console.log('User cancelled authentication - this is normal behavior');
        setHeaderAuthLoading(false);
        return; // Don't show error for user cancellation
      } else if (error.code === 'auth/unauthorized-domain') {
        console.error('Domain not authorized. Please add localhost to Firebase authorized domains.');
        alert('Authentication error: Domain not authorized. Please contact support.');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        console.error('Account exists with different credential');
        alert('An account already exists with the same email address but different sign-in credentials. Please try signing in with a different method.');
      } else if (error.code === 'auth/network-request-failed') {
        console.error('Network request failed');
        alert('Network error. Please check your internet connection and try again.');
      } else if (error.code === 'auth/too-many-requests') {
        console.error('Too many requests');
        alert('Too many failed attempts. Please try again later.');
      } else {
        console.error('Unexpected authentication error:', error);
        alert(`Authentication failed: ${error.message}`);
      }
      
      // Don't re-throw the error to prevent React error boundaries from catching it
      return null;
    } finally {
      clearTimeout(timeoutId);
      setHeaderAuthLoading(false);
      // Only reset if not doing redirect (redirect will handle it)
      if (!sessionStorage.getItem('safariAuthRedirect')) {
        authOperationInProgressRef.current = false;
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Handle purchase
  const handlePurchase = async (plan, billingPeriod = 'one-time') => {
    console.log('handlePurchase called with plan:', plan.id, 'processing:', processing);
    
    // Prevent duplicate execution in React StrictMode
    if (processing) {
      console.log('Already processing, skipping duplicate call');
      return;
    }
    
    try {
      const config = PLAN_CONFIGS[plan.id];
      if (!config) {
        throw new Error('Invalid plan selected');
      }

      // Handle free packs directly without Stripe
      if (plan.price === '$0') {
        console.log('Processing free pack, setting processing to true');
        setProcessing(true);
        const success = await addMockups(config.mockups);
        if (success) {
          console.log('Mockups added successfully, showing success modal');
          const modalData = {
            credits: config.mockups,
            headline: `${config.mockups} credits added to your account!`,
            text: 'Successfully added credits to your account. You can use them for generating new mockups or editing existing ones.'
          };
          console.log('Setting success modal data:', modalData);
          setSuccessModalData(modalData);
          // Use setTimeout to ensure state is set after any potential re-renders
          setTimeout(() => {
            setShowSuccessModal(true);
            console.log('Success modal state set to true');
          }, 100);
        } else {
          console.log('Failed to add mockups, showing error notification');
          showError('Failed to add mockups. Please try again.');
        }
        console.log('Setting processing to false');
        setProcessing(false);
        return;
      }

      if (!config.priceId) {
        throw new Error('Stripe is not configured. Please contact support.');
      }

      await redirectToCheckout(plan.id, user.uid, user.email, billingPeriod);
    } catch (error) {
      console.error('Purchase error:', error);
      if (error.message.includes('Stripe is not configured')) {
        showError('Payment system is not configured. Please contact support.');
      } else {
        showError('Failed to start checkout. Please try again.');
      }
    }
  };

  const handleDeleteClick = (mockup) => {
    setMockupToDelete(mockup);
    setShowDeleteModal(true);
  };


  const handleDeleteConfirm = async () => {
    if (mockupToDelete) {
      console.log('🗑️ Deleting mockup:', mockupToDelete.id, 'Type:', typeof mockupToDelete.id);
      
      // Close modal immediately for better UX
      setShowDeleteModal(false);
      setMockupToDelete(null);
      
      // Remove from Firebase first if user is authenticated
      if (user && db && mockupToDelete.id) {
        try {
          await deleteDoc(doc(db, 'mockups', mockupToDelete.id));
          console.log('✅ Mockup deleted from Firebase successfully');
        } catch (error) {
          console.error('❌ Error deleting mockup from Firebase:', error);
          // Still remove from local state even if Firebase deletion fails
        }
      }
      
      // Remove from local state immediately for better UX
      setGeneratedMockups(prev => {
        const filtered = prev.filter(m => m.id !== mockupToDelete.id);
        console.log('🗑️ Local state update - removed mockup, remaining:', filtered.length);
        return filtered;
      });
      
      // Ensure modal is closed (backup)
      setTimeout(() => {
        setShowDeleteModal(false);
        setMockupToDelete(null);
      }, 100);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setMockupToDelete(null);
  };

  const handleEnhanceClick = async (mockup, resolution) => {
    if (!user || !userData) {
      showError('Please sign in to enhance mockups');
      return;
    }

    const creditCost = resolution === '4k' ? 2 : 1;
    if (userData.mockupsRemaining < creditCost) {
      showError(`Insufficient credits. Need ${creditCost} credits for ${resolution.toUpperCase()} enhancement.`);
      return;
    }

    // Add to enhancing map with resolution
    setEnhancingMockups(prev => new Map(prev).set(mockup.id, resolution));

    try {
      console.log(`🚀 Starting enhancement for mockup ${mockup.id} to ${resolution.toUpperCase()}...`);
      
      // Send the image URL to the backend - let the server handle fetching
      // This bypasses CORS issues since server-to-server requests don't have CORS restrictions
      const formData = new FormData();
      
      // Send the image URL or base64 data to the backend
      if (mockup.imageData) {
        console.log('📤 Sending base64 image data to backend');
        // Convert base64 to blob and send
        const response = await fetch(mockup.imageData);
        const imageBlob = await response.blob();
        formData.append('image', imageBlob, 'mockup.png');
      } else if (mockup.imageUrl || mockup.url) {
        console.log('📤 Sending image URL to backend:', mockup.imageUrl || mockup.url);
        // Send URL - backend will fetch it
        formData.append('imageUrl', mockup.imageUrl || mockup.url);
      } else {
        throw new Error('No image data or URL available');
      }
      
      formData.append('resolution', resolution);

      console.log(`🚀 Calling upscale API (${resolution.toUpperCase()})...`);

      // Call upscale API
      const response = await fetch('/api/upscale', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${errorText}`);
      }

      const enhancedBlob = await response.blob();
      console.log('✅ Enhanced image received:', enhancedBlob.size, 'bytes');
      
      // Store enhanced images in Firebase Storage (too large for Firestore)
      let enhancedImageUrl = null;
      let enhancedImageData = null;
      
      if (storage && user) {
        try {
          console.log('📦 Uploading enhanced image to Firebase Storage...');
          enhancedImageUrl = await uploadImageToStorage(enhancedBlob, user.uid, `enhanced_${mockup.id}_${resolution}.png`);
          console.log('✅ Uploaded to Firebase Storage:', enhancedImageUrl);
          
          // Store base64 data for smaller enhanced images to avoid CORS issues
          if (enhancedBlob.size < 500 * 1024) { // 500KB threshold
            enhancedImageData = await blobToBase64(enhancedBlob);
            console.log('✅ Storing base64 data for small enhanced image to avoid CORS issues');
          }
        } catch (uploadError) {
          console.error('Upload error, using base64 fallback:', uploadError);
          // Fallback to base64 only if storage fails
          enhancedImageData = await blobToBase64(enhancedBlob);
        }
      } else {
        // No storage available, use base64
        console.log('📦 Converting to base64 (no storage)...');
        enhancedImageData = await blobToBase64(enhancedBlob);
      }

      // Update mockup in Firebase
      if (db && mockup.id) {
        console.log('💾 Updating Firestore document...');
        const mockupRef = doc(db, 'mockups', mockup.id);
        await updateDoc(mockupRef, {
          enhancedImageUrl: enhancedImageUrl,
          enhancedImageData: enhancedImageData,
          enhancedResolution: resolution,
          isEnhanced: true,
          enhancedAt: new Date()
        });
        console.log('✅ Firestore updated');
      }

      // Deduct credits
      console.log(`💳 Deducting ${creditCost} credit(s)...`);
      const success = await deductMockup(creditCost);
      if (success) {
        console.log('✅ Credits deducted successfully');
        
        // Track successful enhancement
        trackEvent('enhance_mockup', {
          resolution: resolution,
          cost: creditCost
        });
        
        // Don't show success modal for 2K and 4K enhancements
        if (resolution !== '2k' && resolution !== '4k') {
          showSuccess(`Mockup enhanced to ${resolution.toUpperCase()}! ${creditCost} credit${creditCost > 1 ? 's' : ''} used.`);
        }
      }

    } catch (error) {
      console.error('❌ Enhancement error:', error);
      showError(`Enhancement failed: ${error.message || 'Please try again.'}`);
    } finally {
      // Remove from enhancing map
      setEnhancingMockups(prev => {
        const newMap = new Map(prev);
        newMap.delete(mockup.id);
        return newMap;
      });
    }
  };

  // Handle deleting individual images (enhanced or original) - show confirmation
  const handleDeleteImageClick = (mockup, imageType) => {
    setImageToDelete({ mockup, imageType });
    setShowDeleteImageModal(true);
  };

  // Confirm and delete individual image
  const confirmDeleteImage = async () => {
    if (!db || !imageToDelete) return;

    try {
      const { mockup, imageType } = imageToDelete;
      const mockupRef = doc(db, 'mockups', mockup.id);
      
      if (imageType === 'enhanced') {
        // Delete enhanced image, keep original
        await updateDoc(mockupRef, {
          enhancedImageUrl: null,
          enhancedImageData: null,
          enhancedResolution: null,
          isEnhanced: false,
          enhancedAt: null
        });
        showSuccess('Enhanced image deleted');
      } else if (imageType === 'original') {
        // If there's an enhanced version, make it the new original
        if (mockup.enhancedImageUrl || mockup.enhancedImageData) {
          await updateDoc(mockupRef, {
            url: mockup.enhancedImageUrl || mockup.url,
            imageData: mockup.enhancedImageData || mockup.imageData,
            enhancedImageUrl: null,
            enhancedImageData: null,
            enhancedResolution: null,
            isEnhanced: false,
            enhancedAt: null
          });
          showSuccess('Original replaced with enhanced version');
        } else {
          // No enhanced version, delete the whole mockup
          // Store mockup reference before clearing state
          const mockupToDeleteNow = mockup;
          
          // Clear image delete modal state first
          setShowDeleteImageModal(false);
          setImageToDelete(null);
          
          // Wait a moment for state to clear, then open regular delete modal
          setTimeout(() => {
            handleDeleteClick(mockupToDeleteNow);
          }, 50);
          return;
        }
      }
      
      setShowDeleteImageModal(false);
      setImageToDelete(null);
    } catch (error) {
      console.error('Error deleting image:', error);
      showError('Failed to delete image');
    }
  };

  // const checkSubscriptionStatus = async (userId) => {
  //   // TODO: Check if user has an active subscription
  //   // This will determine credit allocation and limits
  //   return {
  //     isActive: false, // Placeholder: no active subscription
  //     planType: 'free', // Placeholder: free plan
  //     monthlyCredits: 10 // Placeholder: 10 credits per month for free users
  //   };
  // };

  // Authentication is now handled by UserContext

  // Helper function to convert blob to base64 (moved to storage.js)

  // Cleanup blob URLs when component unmounts or mockups change
  useEffect(() => {
    return () => {
      // Cleanup blob URLs to prevent memory leaks
      generatedMockups.forEach(mockup => {
        if (mockup.url && mockup.url.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(mockup.url);
          } catch (error) {
            console.warn('Failed to revoke blob URL:', error);
          }
        }
      });
    };
  }, [generatedMockups]);

  // Component cleanup
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Clear localhost URLs on app startup in production
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' && window.location.origin !== 'http://localhost:3000') {
      console.log('🧹 App startup: Clearing any cached localhost URLs...');
      setGeneratedMockups(prev => {
        const filtered = prev.filter(mockup => 
          !mockup.url || !mockup.url.includes('localhost')
        );
        const removed = prev.length - filtered.length;
        if (removed > 0) {
          console.log('🧹 App startup cleanup: Removed', removed, 'mockups with localhost URLs');
        }
        return filtered;
      });
    }
  }, []);

  // Load user's mockups from Firestore when user is authenticated
  useEffect(() => {
    if (!user) {
      setGeneratedMockups([]);
      setLastVisibleDoc(null);
      setHasMoreMockups(false);
      setUsingOrderBy(true);
      return;
    }
    
    // Clear any mockups with localhost URLs in production
    if (window.location.origin !== 'http://localhost:3000') {
      console.log('🧹 Production environment detected, clearing localhost URLs...');
      setGeneratedMockups(prev => {
        const filtered = prev.filter(mockup => 
          !mockup.url || !mockup.url.includes('localhost')
        );
        const removed = prev.length - filtered.length;
        if (removed > 0) {
          console.log('🧹 Production cleanup: Removed', removed, 'mockups with localhost URLs');
        }
        return filtered;
      });
    }
    
    if (!db) {
      console.warn('⚠️ Firestore not available, skipping mockup loading');
      return;
    }

    const mockupsRef = collection(db, 'mockups');
    const q = query(
      mockupsRef,
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('🔥 Firebase listener triggered, snapshot size:', snapshot.size);
      console.log('🔥 User ID:', user.uid);
      console.log('🔥 Snapshot changes:', snapshot.docChanges().map(change => ({
        type: change.type,
        docId: change.doc.id,
        hasImageUrl: !!change.doc.data().imageUrl,
        hasImageData: !!change.doc.data().imageData
      })));
      setUsingOrderBy(true); // Successfully using orderBy
      
      // Get current local mockups to merge intelligently
      setGeneratedMockups(prevMockups => {
        const firebaseMockups = [];
        const firebaseDocIds = new Set();
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          // Prefer Firebase Storage URL over base64 for display when both exist
          let imageUrl = data.imageUrl || data.imageData;
          
          // Log for debugging
          if (!imageUrl) {
            console.warn('⚠️ Mockup document missing imageUrl and imageData:', doc.id, 'Full data:', Object.keys(data));
          } else {
            console.log('📸 Loading mockup:', doc.id, 'imageUrl type:', imageUrl.substring(0, 30));
          }
          
          // Explicitly reject blob URLs from localhost (common issue from development)
          if (imageUrl && (imageUrl.startsWith('blob:http://localhost') || imageUrl.startsWith('blob:https://localhost'))) {
            console.warn('🚫 Skipping mockup with localhost blob URL:', doc.id, imageUrl.substring(0, 50));
            return; // Skip this mockup entirely
          }
          
          // Use comprehensive URL validation
          if (!isValidImageUrl(imageUrl)) {
            console.log('🚫 Invalid URL detected, attempting fallback:', doc.id, imageUrl?.substring(0, 50));
            
            // Try to use imageData as fallback if it's valid
            if (data.imageData && isValidImageUrl(data.imageData)) {
              imageUrl = data.imageData;
              console.log('✅ Using valid imageData as fallback for doc:', doc.id);
            } else {
              console.error('❌ No valid image URL available for doc:', doc.id, 'imageUrl:', imageUrl?.substring(0, 50), 'imageData:', data.imageData ? 'present' : 'null');
              // Don't skip - log error but still try to display if we have any URL
              // This prevents valid mockups from being filtered out due to transient validation issues
              if (!imageUrl) {
                return; // Only skip if we truly have no URL at all
              }
            }
          }
          
          // Final validation check - but be more lenient
          if (!isValidImageUrl(imageUrl)) {
            console.error('❌ Final validation failed for doc:', doc.id, 'URL type:', typeof imageUrl, 'URL preview:', imageUrl?.substring(0, 50));
            // Only skip if we have absolutely no valid URL
            if (!imageUrl || (imageUrl.startsWith('blob:') && !imageUrl.includes('localhost'))) {
              return; // Skip this mockup entirely
            }
            // Otherwise, try to use it anyway (might be a false positive from validation)
          }
          
          // Check if this mockup is already displayed locally with a base64 URL
          // If so, preserve the base64 URL to prevent reload
          const existingMockup = prevMockups.find(m => m.id === doc.id);
          const shouldPreserveBase64 = existingMockup && 
            existingMockup.url && 
            existingMockup.url.startsWith('data:') &&
            imageUrl && 
            imageUrl.startsWith('http');
          
          const displayUrl = shouldPreserveBase64 ? existingMockup.url : imageUrl;
          
          const mockup = {
            id: doc.id,
            ...data,
            stableKey: data.stableKey || `firebase-${doc.id}`, // Use existing stableKey or create one from doc ID
            url: displayUrl, // Preserve base64 URL if already displayed to prevent reload
            imageUrl: displayUrl, // Preserve base64 URL if already displayed to prevent reload
            storageUrl: imageUrl.startsWith('http') ? imageUrl : null, // Store Storage URL separately
            // Preserve imageData for enhancement capability
            imageData: data.imageData
          };
          firebaseMockups.push(mockup);
          firebaseDocIds.add(doc.id);
        });
        
        console.log('✅ Loaded mockups from Firebase:', firebaseMockups.length);
        console.log('✅ Firebase mockup IDs:', firebaseMockups.map(m => m.id));
        console.log('✅ Firebase mockup URLs:', firebaseMockups.map(m => m.url?.substring(0, 50)));
        
        // For authenticated users, Firebase is the single source of truth
        // Only keep temporary local mockups (numeric IDs) that aren't in Firebase
        // CRITICAL: Remove any mockups that match Firebase document IDs to prevent duplicates
        const localMockups = prevMockups.filter(mockup => {
          // First check: If this mockup ID exists in Firebase, remove it (Firebase is source of truth)
          if (firebaseDocIds.has(mockup.id)) {
            console.log('🚫 Filtering out local mockup - Firebase already has it:', mockup.id);
            return false;
          }
          
          // Check if it's a temporary mockup (temp- prefix or processing-placeholder- prefix)
          const isTemporary = mockup.id && typeof mockup.id === 'string' && 
            (mockup.id.startsWith('temp-') || mockup.id.startsWith('processing-placeholder-'));
          const isPlaceholder = mockup.isPlaceholder === true;
          const notInFirebase = !firebaseDocIds.has(mockup.id);
          
          // Use comprehensive URL validation
          const hasValidUrl = isValidImageUrl(mockup.url);
          
          // Debug logging for invalid URL filtering
          if (mockup.url && !isValidImageUrl(mockup.url)) {
            console.log('🚫 Filtering out mockup with invalid URL:', mockup.id, mockup.url?.substring(0, 50));
            return false; // Explicitly filter out invalid URLs
          }
          
          // Remove placeholders (they should have been replaced by now)
          if (isPlaceholder) {
            console.log('🚫 Filtering out placeholder mockup:', mockup.id);
            return false;
          }
          
          // Keep only temporary mockups that aren't in Firebase and have valid URLs
          const shouldKeep = isTemporary && notInFirebase && hasValidUrl;
          if (!shouldKeep && isTemporary) {
            console.log('🚫 Filtering out temporary mockup:', mockup.id, 'isTemporary:', isTemporary, 'notInFirebase:', notInFirebase, 'hasValidUrl:', hasValidUrl);
          }
          return shouldKeep;
        });
        
        // Combine Firebase mockups with remaining local mockups
        const mergedMockups = [...firebaseMockups, ...localMockups];
        
        console.log('🔥 Firebase listener - Final merge:', {
          firebaseCount: firebaseMockups.length,
          localCount: localMockups.length,
          totalCount: mergedMockups.length,
          firebaseMockupIds: firebaseMockups.map(m => m.id),
          localMockupIds: localMockups.map(m => m.id)
        });
        
        // Track the last visible document for pagination
        const lastDoc = snapshot.docs[snapshot.docs.length - 1];
        setLastVisibleDoc(lastDoc);
        
        // If we got 100 items, there might be more
        setHasMoreMockups(snapshot.size === 100);
        
        console.log('📊 Merged mockups:', mergedMockups.length, '(Firebase:', firebaseMockups.length, ', Local:', localMockups.length, ')');
        console.log('📊 Merged mockups details:', mergedMockups.map(m => ({ id: m.id, url: m.url, imageUrl: m.imageUrl })));
        
        // Log state update
        console.log('🔄 State update - setting generatedMockups to:', mergedMockups.length, 'mockups');
        
        return mergedMockups;
      });
    }, (error) => {
      console.error('❌ Firebase listener error:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);
      // If it's an index error, try a simpler query without orderBy
      if (error.code === 'failed-precondition') {
        console.error('❌ Firestore index required! Please create the index:');
        console.error('❌ Collection: mockups');
        console.error('❌ Fields: userId (Ascending), createdAt (Descending)');
        console.error('❌ Click the link in the error to create it, or go to Firebase Console > Firestore > Indexes');
        console.log('🔄 Falling back to simple query without orderBy...');
        setUsingOrderBy(false); // Using simple query without orderBy
        // Use a higher limit and try to get all mockups since we can't order them
        // This ensures we get the newest ones even without orderBy
        const simpleQuery = query(
          mockupsRef,
          where('userId', '==', user.uid),
          limit(200) // Increased limit to ensure we get newer mockups
        );
        
        const simpleUnsubscribe = onSnapshot(simpleQuery, (snapshot) => {
          console.log('Simple Firebase listener triggered, snapshot size:', snapshot.size);
          
          // Check for new/modified documents
          const changes = snapshot.docChanges();
          const newDocs = changes.filter(c => c.type === 'added');
          const modifiedDocs = changes.filter(c => c.type === 'modified');
          
          if (newDocs.length > 0) {
            console.log('🆕 New mockups detected:', newDocs.map(c => c.doc.id));
          }
          if (modifiedDocs.length > 0) {
            console.log('🔄 Modified mockups:', modifiedDocs.map(c => c.doc.id));
          }
          
          console.log('Simple Firebase listener - snapshot changes:', changes.map(change => ({
            type: change.type,
            docId: change.doc.id,
            hasImageUrl: !!change.doc.data().imageUrl,
            hasImageData: !!change.doc.data().imageData
          })));
          
          // Get current local mockups to merge intelligently
          setGeneratedMockups(prevMockups => {
            const firebaseMockups = [];
            const firebaseDocIds = new Set();
            
            snapshot.forEach((doc) => {
              const data = doc.data();
              // Prefer Firebase Storage URL over base64 for display when both exist
              let imageUrl = data.imageUrl || data.imageData;
              
              // Log for debugging - but only for new/changed docs to reduce noise
              if (snapshot.docChanges().some(change => change.doc.id === doc.id)) {
                if (!imageUrl) {
                  console.warn('⚠️ Mockup document missing imageUrl and imageData (simple query):', doc.id);
                } else {
                  console.log('📸 Loading mockup (simple):', doc.id, 'imageUrl:', imageUrl.substring(0, 30));
                }
              }
              
              // Explicitly reject blob URLs from localhost (common issue from development)
              if (imageUrl && (imageUrl.startsWith('blob:http://localhost') || imageUrl.startsWith('blob:https://localhost'))) {
                console.warn('🚫 Skipping mockup with localhost blob URL (simple query):', doc.id, imageUrl.substring(0, 50));
                return; // Skip this mockup entirely
              }
              
              // Validate URL - but be more lenient
              if (!isValidImageUrl(imageUrl)) {
                console.log('🚫 Invalid URL in simple query, attempting fallback:', doc.id, imageUrl?.substring(0, 50));
                
                // Try to use imageData as fallback if it's valid
                if (data.imageData && isValidImageUrl(data.imageData)) {
                  imageUrl = data.imageData;
                  console.log('✅ Using valid imageData as fallback for doc (simple query):', doc.id);
                } else {
                  console.error('❌ No valid image URL available for doc (simple query):', doc.id);
                  // Only skip if we truly have no URL at all
                  if (!imageUrl) {
                    return; // Skip this mockup entirely
                  }
                }
              }
              
              // Check if this mockup is already displayed locally with a base64 URL
              // If so, preserve the base64 URL to prevent reload
              const existingMockup = prevMockups.find(m => m.id === doc.id);
              const shouldPreserveBase64 = existingMockup && 
                existingMockup.url && 
                existingMockup.url.startsWith('data:') &&
                imageUrl && 
                imageUrl.startsWith('http');
              
              const displayUrl = shouldPreserveBase64 ? existingMockup.url : imageUrl;
              
              const mockup = {
                id: doc.id,
                ...data,
                stableKey: data.stableKey || `firebase-${doc.id}`, // Use existing stableKey or create one from doc ID
                url: displayUrl, // Preserve base64 URL if already displayed to prevent reload
                imageUrl: displayUrl, // Preserve base64 URL if already displayed to prevent reload
                storageUrl: imageUrl.startsWith('http') ? imageUrl : null, // Store Storage URL separately
                // Preserve imageData for enhancement capability
                imageData: data.imageData
              };
              firebaseMockups.push(mockup);
              firebaseDocIds.add(doc.id);
            });
            
            console.log('✅ Loaded mockups from simple Firebase query:', firebaseMockups.length);
            console.log('✅ Simple query mockup IDs:', firebaseMockups.map(m => m.id).slice(0, 10));
            
            // Merge Firebase mockups with existing local state
            // Keep local mockups that don't have Firebase equivalents (temporary ones)
            // Filter out placeholders - they should have been replaced by now
            const localMockups = prevMockups.filter(mockup => {
              const isPlaceholder = mockup.isPlaceholder === true;
              const notInFirebase = !firebaseDocIds.has(mockup.id);
              const isTemporary = mockup.id && typeof mockup.id === 'string' && 
                (mockup.id.startsWith('temp-') || mockup.id.startsWith('processing-placeholder-'));
              
              // Remove placeholders
              if (isPlaceholder) {
                console.log('🚫 Filtering out placeholder mockup (simple query):', mockup.id);
                return false;
              }
              
              // Keep temporary mockups that aren't in Firebase
              return notInFirebase && isTemporary;
            });
            
            // Combine Firebase mockups with remaining local mockups
            const mergedMockups = [...firebaseMockups, ...localMockups];
            
            // Track the last visible document for pagination
            const lastDoc = snapshot.docs[snapshot.docs.length - 1];
            setLastVisibleDoc(lastDoc);
            
            // If we got 200 items, there might be more
            setHasMoreMockups(snapshot.size === 200);
            
            // Sort manually since we can't use orderBy - CRITICAL to ensure newest first
            // Handle both Firestore Timestamp and Date objects
            mergedMockups.sort((a, b) => {
              let aTime, bTime;
              
              // Handle Firestore Timestamp
              if (a.createdAt?.toDate) {
                aTime = a.createdAt.toDate();
              } else if (a.createdAt instanceof Date) {
                aTime = a.createdAt;
              } else if (a.createdAt) {
                aTime = new Date(a.createdAt);
              } else {
                // Fallback: use document ID timestamp if available, or epoch
                aTime = new Date(0);
              }
              
              if (b.createdAt?.toDate) {
                bTime = b.createdAt.toDate();
              } else if (b.createdAt instanceof Date) {
                bTime = b.createdAt;
              } else if (b.createdAt) {
                bTime = new Date(b.createdAt);
              } else {
                // Fallback: use document ID timestamp if available, or epoch
                bTime = new Date(0);
              }
              
              return bTime.getTime() - aTime.getTime(); // Newest first
            });
            
            console.log('✅ Merged mockups (simple):', mergedMockups.length, '(Firebase:', firebaseMockups.length, ', Local:', localMockups.length, ')');
            console.log('✅ Top 5 mockup IDs (newest first):', mergedMockups.slice(0, 5).map(m => ({ 
              id: m.id, 
              createdAt: m.createdAt?.toDate?.()?.toISOString() || m.createdAt || 'missing',
              imageUrl: m.imageUrl?.substring(0, 50)
            })));
            
            // Log all mockup IDs to verify what we're getting
            const allIds = Array.from(firebaseDocIds);
            console.log('✅ All Firebase mockup IDs loaded:', allIds.slice(0, 10));
            console.log('✅ Total mockups in Firebase:', allIds.length);
            
            // CRITICAL: Check if we're missing any recent mockups
            // If the limit is 200 but we only got fewer, that's fine
            // But if we got exactly 200, we might be missing newer ones
            if (snapshot.size === 200) {
              console.warn('⚠️ WARNING: Got maximum 200 mockups - might be missing newer ones. Please create the Firestore index!');
            }
            
            return mergedMockups;
          });
        }, (simpleError) => {
          console.error('Error with simple query:', simpleError);
        });
        
        return () => simpleUnsubscribe();
      }
    });

    return () => unsubscribe();
  }, [user]);

  // Load more mockups (pagination)
  const loadMoreMockups = async () => {
    if (!user || !db || !lastVisibleDoc || loadingMore) {
      return;
    }

    setLoadingMore(true);
    
    try {
      const mockupsRef = collection(db, 'mockups');
      
      // Use the same query approach as the initial load
      let q;
      if (usingOrderBy) {
        console.log('Loading more with orderBy query...');
        q = query(
          mockupsRef,
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc'),
          startAfter(lastVisibleDoc),
          limit(100)
        );
      } else {
        console.log('Loading more with simple query...');
        q = query(
          mockupsRef,
          where('userId', '==', user.uid),
          startAfter(lastVisibleDoc),
          limit(100)
        );
      }

      const { getDocs } = await import('firebase/firestore');
      const snapshot = await getDocs(q);
      
      console.log('Loaded more mockups:', snapshot.size);
      
      const newMockups = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        let imageUrl = data.imageData || data.imageUrl;
        
        // Explicitly reject blob URLs from localhost (common issue from development)
        if (imageUrl && (imageUrl.startsWith('blob:http://localhost') || imageUrl.startsWith('blob:https://localhost'))) {
          console.warn('🚫 Skipping mockup with localhost blob URL (loadMore):', doc.id, imageUrl.substring(0, 50));
          return; // Skip this mockup entirely
        }
        
        // Validate URL
        if (!isValidImageUrl(imageUrl)) {
          console.log('🚫 Invalid URL in loadMore, skipping:', doc.id, imageUrl?.substring(0, 50));
          return; // Skip this mockup entirely
        }
        
        // Check if this mockup is already displayed locally with a base64 URL
        // If so, preserve the base64 URL to prevent reload
        const existingMockup = generatedMockups.find(m => m.id === doc.id);
        const shouldPreserveBase64 = existingMockup && 
          existingMockup.url && 
          existingMockup.url.startsWith('data:') &&
          imageUrl && 
          imageUrl.startsWith('http');
        
        const displayUrl = shouldPreserveBase64 ? existingMockup.url : imageUrl;
        
        newMockups.push({
          id: doc.id,
          ...data,
          url: displayUrl, // Preserve base64 URL if already displayed to prevent reload
          imageUrl: displayUrl, // Preserve base64 URL if already displayed to prevent reload
          storageUrl: imageUrl.startsWith('http') ? imageUrl : null, // Store Storage URL separately
          imageData: data.imageData
        });
      });
      
      // If using simple query without orderBy, sort manually
      if (!usingOrderBy) {
        newMockups.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bTime - aTime;
        });
      }
      
      // Update last visible document
      const lastDoc = snapshot.docs[snapshot.docs.length - 1];
      setLastVisibleDoc(lastDoc);
      
      // Check if there are more mockups
      setHasMoreMockups(snapshot.size === 100);
      
      // Append new mockups to existing ones
      setGeneratedMockups(prev => [...prev, ...newMockups]);
    } catch (error) {
      console.error('Error loading more mockups:', error);
      showError('Failed to load more mockups');
    } finally {
      setLoadingMore(false);
    }
  };

  // Focus delete modal when it opens
  useEffect(() => {
    if (showDeleteModal && deleteModalRef.current) {
      deleteModalRef.current.focus();
    }
  }, [showDeleteModal]);


  useEffect(() => {
    if (showDeleteImageModal && deleteImageModalRef.current) {
      deleteImageModalRef.current.focus();
    }
  }, [showDeleteImageModal]);

  // Helper function to sort files by size (desktop images last)
  function sortFilesBySize(fileArray) {
    return fileArray.sort((a, b) => {
      // Sort by file size - smaller files first (mobile/tablet), larger files last (desktop)
      return a.size - b.size;
    });
  }

  // Helper function to sort files by device type (mobile first, then desktop)
  // function sortFilesByDeviceType(fileArray, deviceTypes) {
  //   return fileArray.sort((a, b) => {
  //     const indexA = fileArray.indexOf(a);
  //     const indexB = fileArray.indexOf(b);
  //     const deviceTypeA = deviceTypes[indexA];
  //     const deviceTypeB = deviceTypes[indexB];
  //     
  //     // Mobile first, then tablet, then desktop
  //     const order = { mobile: 0, tablet: 1, desktop: 2 };
  //     return order[deviceTypeA] - order[deviceTypeB];
  //   });
  // }

  async function handleFileSelected(f) {
    try {
      console.log('handleFileSelected called with:', f);
      if (f === null) {
        // Handle delete case
        setFile(null);
        setFiles([]);
        setDeviceTypes([]);
        setDeviceNames([]);
        return;
      }
      
      // Validate file type
      if (!f.type.startsWith('image/')) {
        console.error('Invalid file type:', f.type);
        alert('Please select an image file.');
        return;
      }
      
      setFile(f);
      setFiles([]); // Clear multiple files when single file is selected
      const { width, height } = await readImageBitmap(f);
      const detectedDeviceType = detectDeviceType(width, height);
      setDeviceType(detectedDeviceType);
      setDeviceTypes([detectedDeviceType]);
      setDeviceNames([getDeviceName(detectedDeviceType)]);
      console.log('File processed successfully:', f.name, 'Device type:', detectedDeviceType);
      
      // Track upload event
      trackEvent('upload_image', {
        file_count: 1,
        device_type: detectedDeviceType
      });
      // Don't set imageUrl here - only show thumbnail in upload area
    } catch (error) {
      console.error('Error handling file selection:', error);
      alert('Error processing file. Please try again.');
      // Reset state on error
      setFile(null);
      setFiles([]);
      setDeviceTypes([]);
      setDeviceNames([]);
    }
  }

  async function handleFilesSelected(fileArray) {
    try {
      console.log('handleFilesSelected called with:', fileArray.length, 'files');
      console.log('File names:', fileArray.map(f => f.name));
      
      // Sort files by size (desktop images last)
      const sortedFiles = sortFilesBySize([...fileArray]);
      console.log('Sorted files by size:', sortedFiles.map(f => ({ name: f.name, size: f.size })));
      
      // Set new files (this will replace existing files)
      setFiles(sortedFiles);
      setFile(null); // Clear single file when multiple files are selected
      
      // For 5+ files, automatically set to isometric view and no device
      if (fileArray.length >= 5) {
        setViewAngle('isometric');
        setShowDevice(false);
      }
      
      // Detect device types for all files
      try {
        const { deviceTypes: detectedDeviceTypes, deviceNames: detectedDeviceNames } = await detectDeviceTypesForFiles(sortedFiles);
        setDeviceTypes(detectedDeviceTypes);
        setDeviceNames(detectedDeviceNames);
        
        // Set the primary device type (for backward compatibility)
        if (detectedDeviceTypes.length > 0) {
          setDeviceType(detectedDeviceTypes[0]);
        }
        
        console.log('Detected device types:', detectedDeviceTypes);
        console.log('Detected device names:', detectedDeviceNames);
        
        // Track upload event for multiple files
        trackEvent('upload_image', {
          file_count: fileArray.length,
          device_type: detectedDeviceTypes.length > 0 ? detectedDeviceTypes[0] : 'mixed'
        });
      } catch (error) {
        console.error('Error detecting device types:', error);
        // Fallback to desktop
        setDeviceType('desktop');
        setDeviceTypes(['desktop']);
        setDeviceNames(['Desktop']);
        
        // Track upload event with fallback device type
        trackEvent('upload_image', {
          file_count: fileArray.length,
          device_type: 'desktop'
        });
      }
    } catch (error) {
      console.error('Error handling multiple files selection:', error);
      // Reset state on error
      setFiles([]);
      setFile(null);
      setDeviceTypes([]);
      setDeviceNames([]);
    }
  }

  async function handleAppendFiles(fileArray) {
    console.log('handleAppendFiles called with:', fileArray.length, 'files');
    console.log('New file names:', fileArray.map(f => f.name));
    
    // Get existing files
    const existingFiles = files.length > 0 ? files : (file ? [file] : []);
    const combinedFiles = [...existingFiles, ...fileArray];
    
    console.log('Combined files:', combinedFiles.length, 'total');
    console.log('All file names:', combinedFiles.map(f => f.name));
    
    // Sort files by size (desktop images last)
    const sortedFiles = sortFilesBySize([...combinedFiles]);
    console.log('Sorted combined files by size:', sortedFiles.map(f => ({ name: f.name, size: f.size })));
    
    // Clear single file state and set combined files
    setFile(null);
    setFiles(sortedFiles);
    
    // For 5+ files, automatically set to isometric view and no device
    if (sortedFiles.length >= 5) {
      setViewAngle('isometric');
      setShowDevice(false);
    }
    
    // Detect device types for all files
    try {
      const { deviceTypes: detectedDeviceTypes, deviceNames: detectedDeviceNames } = await detectDeviceTypesForFiles(sortedFiles);
      setDeviceTypes(detectedDeviceTypes);
      setDeviceNames(detectedDeviceNames);
      
      // Set the primary device type (for backward compatibility)
      if (detectedDeviceTypes.length > 0) {
        setDeviceType(detectedDeviceTypes[0]);
      }
      
      console.log('Detected device types:', detectedDeviceTypes);
      console.log('Detected device names:', detectedDeviceNames);
    } catch (error) {
      console.error('Error detecting device types:', error);
      // Fallback to desktop
      setDeviceType('desktop');
      setDeviceTypes(['desktop']);
      setDeviceNames(['Desktop']);
    }
  }

  function handleRemoveFile(index) {
    console.log('Removing file at index:', index);
    const newFiles = files.filter((_, i) => i !== index);
    console.log('New files after removal:', newFiles.length);
    
    if (newFiles.length === 0) {
      setFiles([]);
      setFile(null);
    } else if (newFiles.length === 1) {
      setFile(newFiles[0]);
      setFiles([]);
    } else {
      setFiles(newFiles);
      setFile(null);
    }
  }

  function handleReorderFiles(newFiles) {
    console.log('Reordering files:', newFiles.length, 'files');
    console.log('New order:', newFiles.map(f => f.name));
    setFiles(newFiles);
    setFile(null);
  }

  // Global drag & drop handlers for the entire interface
  const handleGlobalDragEnter = useCallback((e) => {
    e.preventDefault();
    globalDragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsGlobalDragging(true);
    }
  }, []);

  const handleGlobalDragLeave = useCallback((e) => {
    e.preventDefault();
    globalDragCounter.current--;
    if (globalDragCounter.current === 0) {
      setIsGlobalDragging(false);
    }
  }, []);

  const handleGlobalDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleGlobalDrop = useCallback((e) => {
    e.preventDefault();
    globalDragCounter.current = 0;
    setIsGlobalDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (droppedFiles.length === 0) return;
    
    console.log('Global drop:', droppedFiles.length, 'files');
    
    // Use existing file handlers
    if (file || files.length > 0) {
      handleAppendFiles(droppedFiles);
    } else if (droppedFiles.length === 1) {
      handleFileSelected(droppedFiles[0]);
    } else {
      handleFilesSelected(droppedFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, files]);

  // const autoCategory = useMemo(() => category, [category]);

  const buildExtraPrompt = useCallback((selectedCategory, selectedDeviceType, showDeviceInMockup, selectedViewAngle, fileCount = 1, showBokehInMockup = true, showHandsInMockup = true, showNotchInMockup = false, selectedAspectRatio = '4:3', customFullPrompt = '') => {
  // If custom full prompt is provided, return it directly
  if (customFullPrompt && customFullPrompt.trim() !== '') {
    return customFullPrompt;
  }
  
  const isMobile = selectedDeviceType === 'mobile';
  const isTablet = selectedDeviceType === 'tablet';
  const isUltrawide = selectedDeviceType === 'ultrawide';
  let deviceName = isMobile ? 'iPhone 17 Pro' : isTablet ? 'iPad Pro' : isUltrawide ? 'Apple Pro Display XDR' : 'MacBook Pro';
  
  // Add Dynamic Island notch for iPhone when showNotchInMockup is true
  if (isMobile && showNotchInMockup) {
    deviceName = 'iPhone 17 Pro with Dynamic Island notch';
  } else if (isMobile && !showNotchInMockup) {
    deviceName = 'iPhone 17 Pro without Dynamic Island notch';
  }
    
    // Define view angle descriptions - Enhanced with cinematic terminology
    const getViewAngleDescription = (angle) => {
      switch (angle) {
        case 'left-angled':
          return 'cinematic left three-quarter angle composition with dramatic perspective and professional depth';
        case 'frontal':
          return 'heroic frontal straight-on composition with centered positioning and authoritative presence';
        case 'isometric':
          return 'dynamic isometric perspective composition with 3D depth, proper angles, and architectural precision';
        case 'right-angled':
        default:
          return 'cinematic three-quarter angle composition with professional perspective and dramatic depth';
      }
    };

    // Optimized prompt for cinematic mockup generation
    const screenPreservationInstruction = `TASK: Create a cinematic mockup by adding a ${deviceName} frame and professional environment around the existing screen content.

🚨 CRITICAL: SCREEN CONTENT PRESERVATION (HIGHEST PRIORITY) 🚨
The uploaded screenshot must be preserved with ABSOLUTE PIXEL-PERFECT FIDELITY. This is image compositing, not generation.

PRESERVE EXACTLY - ZERO MODIFICATIONS:
- All text: characters, fonts, sizes, weights, colors, spacing, kerning
- All UI elements: buttons, icons, shapes, borders, shadows, gradients
- All images and graphics: colors, contrast, brightness, saturation
- All layouts: positioning, alignment, spacing, proportions
- All visual properties: exact color values, opacity, effects

ABSOLUTELY DO NOT:
- Regenerate, redraw, or recreate any screen content
- Apply filters, enhancements, sharpening, or denoising to screen
- Adjust colors, contrast, or brightness on screen content
- Modify text rendering, fonts, or typography
- Clean up artifacts or compression on screen content
- Change aspect ratio or crop screenshot content

CINEMATIC ENHANCEMENT (Device Frame & Environment Only):
Add a realistic ${deviceName} frame with authentic materials, precise bezels, and accurate proportions. Apply professional three-point lighting system:
- Key light: Primary illumination at 45-degree angle
- Fill light: Soft secondary light to reduce harsh shadows
- Rim light: Edge lighting for depth and separation

Use professional composition with shallow depth of field (50mm lens at f/2.8) for cinematic bokeh. Create dramatic visual depth with:
- Foreground interest: Subtle elements adding depth
- Midground subject: Device as hero element
- Background context: Environment supporting the narrative

INTELLIGENT CONTEXT ANALYSIS:
Analyze the screenshot to identify:
- Industry and business type
- Brand identity and color palette
- Target audience and use case
- Visual theme and aesthetic style

Create an environment that authentically matches the content's purpose, using brand colors subtly in the environment, appropriate props, and contextual elements that enhance rather than distract.

COMPOSITION:
${selectedAspectRatio === '3:4' ? 'Portrait' : selectedAspectRatio === '1:1' ? 'Square' : 'Landscape'} orientation. Fill entire canvas with complete scene. Show entire device with breathing room. Use ${getViewAngleDescription(selectedViewAngle)}. Fill entire canvas - no black bars or letterboxing.

${!showHandsInMockup ? `NO HANDS: Position device statically without human elements.` : ''}`;

    // Consolidated conditional instructions
    let conditionalInstructions = '';
    
    // Hands instructions
    if (showHandsInMockup) {
      conditionalInstructions += `
HANDS: Show hands naturally holding the device with realistic positioning and grip. 
Include natural hand gestures and interaction. Use professional lifestyle photography with hands in frame.`;
    }
    
    // Notch instructions for mobile devices
    if (isMobile && !showNotchInMockup) {
      conditionalInstructions += `

NOTCH: Use older iPhone design without Dynamic Island notch. Show traditional iPhone bezel design.`;
    }
    
    // Helper function to get style prompt
    const getStylePrompt = (category, showDevice) => {
      const bokehEnhancement = showBokehInMockup ? ' with cinematic bokeh background using professional camera with wide aperture lens (f/1.4-f/2.8) for dreamy, soft background blur, enhanced depth of field, and premium photography aesthetic' : '';
      
      if (!showDevice) {
        return `Use commercial catalog aesthetic with shallow depth of field and realistic texture fidelity. High-end, clean, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter.${bokehEnhancement}`;
      }
      
      switch (category) {
        case 'simple':
          return `Create premium commercial catalog aesthetic with cinematic shallow depth of field (50mm lens at f/2.8) and photorealistic texture fidelity. Use dramatic three-point lighting with soft key light, subtle fill, and rim lighting for depth. High-end, clean, aspirational mood with editorial photography quality. Sharp focus, perfect color balance, no distractions or clutter. Professional product photography with magazine-quality composition and golden hour warmth.${bokehEnhancement}`;
        case 'color':
          const getColorInfo = (color) => {
            // Check if it's a hex color
            if (color && color.startsWith('#')) {
              return { hex: color, name: color };
            }
            // Predefined colors
            const colorMap = {
              blue: { hex: '#3B82F6', name: 'blue' },
              red: { hex: '#EF4444', name: 'red' },
              green: { hex: '#10B981', name: 'green' },
              yellow: { hex: '#F59E0B', name: 'yellow' },
              purple: { hex: '#8B5CF6', name: 'purple' },
              pink: { hex: '#EC4899', name: 'pink' },
              orange: { hex: '#F97316', name: 'orange' },
              cyan: { hex: '#06B6D4', name: 'cyan' },
              teal: { hex: '#14B8A6', name: 'teal' },
              indigo: { hex: '#6366F1', name: 'indigo' },
              rose: { hex: '#F43F5E', name: 'rose' },
              emerald: { hex: '#059669', name: 'emerald' }
            };
            return colorMap[color] || colorMap.blue;
          };
          const colorInfo = getColorInfo(selectedColor);
          return `Create a clean, solid color background environment using the exact color ${colorInfo.hex} (${colorInfo.name}). The device should be levitating/floating in mid-air with a realistic soft shadow cast beneath it. Use professional studio lighting with the entire background as a uniform ${colorInfo.hex} solid color - no gradients, no fog, no atmospheric effects - just a clean, flat ${colorInfo.name} background. The device should have professional product photography lighting that makes it stand out against the ${colorInfo.name} background. Show realistic soft drop shadow beneath the levitating device. Commercial catalog aesthetic with shallow depth of field.${bokehEnhancement}`;
        case 'office':
          return `Create premium professional office environment with sophisticated corporate cinematography. Use clean, modern three-point lighting with soft key light at 45-degree angle, subtle fill light, and rim lighting for depth. Shot with 85mm lens at f/2.8 for shallow depth of field. Editorial business photography with clean lines, modern architecture, and executive-level sophistication. Professional mood with crisp accents and golden hour warmth.${bokehEnhancement}`;
        case 'fiori':
          return `Use Studio design system aesthetic with clean, modern UI elements and professional lighting. Sharp focus, perfect color balance, modern design mood with soft studio key light and crisp accents.${bokehEnhancement}`;
        case 'stones':
          return `Use concrete, textured aesthetic with natural stone surfaces and organic lighting. Sharp focus, perfect color balance, natural mood with soft studio key light and crisp accents.${bokehEnhancement}`;
        case 'gamer':
          return `Create premium gaming aesthetic with dynamic RGB lighting and energetic atmosphere. Use dramatic multi-colored lighting with vibrant cyan, purple, and pink accents. Shot with 50mm lens at f/1.4 for shallow depth of field and bokeh effects. Professional esports photography with high-energy composition, gaming peripherals, and cinematic rim lighting. Sharp focus, perfect color balance, gaming mood with crisp accents.${bokehEnhancement}`;
        case 'view':
          return `Use scenic view aesthetic with natural outdoor lighting and environmental atmosphere. Sharp focus, perfect color balance, natural mood with soft studio key light and crisp accents.${bokehEnhancement}`;
        case 'ocean':
          return `Use ocean-themed aesthetic with blue tones and aquatic atmosphere. Sharp focus, perfect color balance, oceanic mood with soft studio key light and crisp accents.${bokehEnhancement}`;
        case 'designer':
          return `Use designer aesthetic with creative lighting and artistic atmosphere. Sharp focus, perfect color balance, creative mood with soft studio key light and crisp accents.${bokehEnhancement}`;
        case 'coffee':
          return `Create premium coffee shop aesthetic with warm, inviting cinematography. Use golden hour lighting with soft key light, warm fill light, and subtle rim lighting for depth. Shot with 50mm lens at f/2.8 for shallow depth of field. Professional lifestyle photography with editorial quality, warm amber tones, and premium café atmosphere. Cozy mood with crisp accents and natural bokeh.${bokehEnhancement}`;
        case 'business':
          return `Use premium business professional aesthetic with clean, corporate lighting and sophisticated professional atmosphere. Sharp focus, perfect color balance, professional mood with soft studio key light and crisp accents. Editorial business photography with executive-level sophistication and premium corporate aesthetic.${bokehEnhancement}`;
        case 'artsy':
          return `Use music venue aesthetic with dynamic lighting and musical atmosphere. Include recording studio elements, musical instruments, sound equipment, and vibrant stage lighting. Sharp focus, perfect color balance, musical mood with dynamic studio lighting and crisp accents.${bokehEnhancement}`;
        case 'abstract':
          return `Use creative, expressive, and modern UI design themes with inventive lighting and professional, design-forward mood. Focus on imaginative and stylized interpretation rather than realism. Sharp focus, perfect color balance, modern aesthetic with soft studio key light and crisp accents.${bokehEnhancement}`;
        case 'sport':
          return `Use premium bold lighting and vibrant color balance to evoke an active, competitive atmosphere. Captures the excitement and sharpness found in sports-related contexts. Sharp focus, perfect color balance, energetic mood with soft studio key light and crisp accents. Professional sports photography with dynamic action, high-energy composition, and editorial athletic aesthetic.${bokehEnhancement}`;
        case 'nature':
          return `Use premium outdoor aesthetics rooted in natural environments with cinematic natural lighting, organic textures from stone, wood, and water, and scenic environmental moods. Promotes tranquility and connection to the natural world. Sharp focus, perfect color balance, natural mood with soft studio key light and crisp accents. Professional nature photography with golden hour lighting, natural depth, and editorial outdoor aesthetic.${bokehEnhancement}`;
        case 'luxury':
          return `${screenPreservationInstruction}Create a luxury environment with opulent materials: polished marble surfaces, brushed gold accents, crystal chandeliers casting warm light, and rich velvet textures. Use dramatic chiaroscuro lighting with deep shadows and golden highlights. Shot with 85mm lens at f/1.4 for ultra-shallow depth of field. Professional composition with cinematic depth. Include luxury elements like vintage champagne glasses, designer watches, premium leather goods, elegant table settings with fine china, crystal wine glasses, and fresh flowers. Include background elements like wine cellars, open kitchens, and sophisticated diners. Color grading with warm golden tones, rich contrast, and warm amber tones with soft shadows.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'space':
          return `${screenPreservationInstruction}Create a futuristic space station environment with sleek metallic surfaces, holographic displays, and ambient blue lighting. Include advanced technology elements like holographic displays, floating panels, and energy conduits. Use cool blue and white lighting with subtle neon accents. Professional composition with shallow depth of field. Include space station elements like control panels, viewports showing starfields, and futuristic architecture. Color grading with cool blues, whites, and subtle cyan highlights.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'octopus':
          return `${screenPreservationInstruction}Create a cinematic underwater scene with deep oceanic blues and mysterious lighting filtering through water. Include highly detailed, photorealistic octopus tentacles with natural texture, subtle color variations, and organic movement patterns. Show tentacles with authentic sucker details, natural curvature, and lifelike positioning. Include flowing seaweed, bioluminescent particles, and marine life silhouettes. Use dramatic underwater lighting with blue-green tones and ethereal glow effects. Include coral formations and realistic underwater atmosphere. Professional underwater photography with cinematic depth, dramatic lighting, and premium marine aesthetic. Color grading with deep blues and teal accents.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'paraglide':
          return `${screenPreservationInstruction}Create a high-altitude adventure scene with vast mountain landscapes and dramatic cloud formations. Include paragliding equipment, harnesses, and safety gear. Use natural lighting with dramatic sky conditions and mountain shadows. Include elements like wind-blown fabric, mountain peaks, and endless sky. Color grading with cool blues and natural earth tones.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'rooftop':
          return `${screenPreservationInstruction}Create an urban rooftop scene with city skyline backdrop and golden hour lighting. Include modern rooftop elements like potted plants, outdoor furniture, and city views. Use shallow depth of field, capturing the urban atmosphere. Include elements like string lights, city traffic below, and architectural details. Color grading with warm sunset tones and urban contrast.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'car':
          return `${screenPreservationInstruction}Create a cinematic automotive showroom scene with dramatic lighting reminiscent of luxury car commercials. Include sleek luxury automobile elements: polished metallic surfaces, leather seats, chrome details, and glossy paint reflections. Use professional automotive photography lighting with dramatic rim lights and key lights at 45-degree angles, shot with 85mm lens at f/1.4 for shallow depth of field. Include showroom elements like spotlights, reflective floors, and modern architecture. Create a high-performance, aspirational atmosphere with cool blue and warm amber accent lighting. Color grading with deep blacks, metallic highlights, and cinematic contrast.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'bar':
          return `${screenPreservationInstruction}Create an upscale cocktail bar environment with moody, atmospheric lighting like a premium spirits advertisement. Include sophisticated bar elements: crystal glassware, premium liquor bottles with amber backlighting, polished mahogany bar surfaces, brass fixtures, and leather bar stools. Use cinematic low-key lighting with warm tungsten practicals and subtle neon accents, shot with 50mm lens at f/1.8 for dreamy bokeh. Include atmospheric elements like smoke wisps, ice cubes catching light, garnishes, and bottles with dramatic backlighting. Create an intimate, sophisticated atmosphere with warm golden hour tones mixed with cool blue shadows. Color grading with rich amber warmth, deep shadows, and elegant contrast.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'restaurant':
          return `${screenPreservationInstruction}Create an elegant fine dining restaurant scene with warm, inviting cinematography like a premium culinary magazine. Include sophisticated restaurant elements: pristine white tablecloths, polished silverware, wine glasses catching candlelight, fresh plated cuisine with artistic presentation, elegant table settings, and soft ambient lighting from overhead pendants. Use warm, diffused lighting with golden hour tones and soft shadows, shot with 50mm lens at f/1.8 for shallow depth of field. Include culinary elements like fresh ingredients, artisanal bread baskets, sommelier wine selections, open kitchen glimpses with chef stations, and soft bokeh from ambient candles. Create a refined, sophisticated, appetizing atmosphere with warm rustic tones. Color grading with warm amber lighting, soft highlights, rich wood tones, and that editorial food photography aesthetic.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'skatepark':
          return `${screenPreservationInstruction}Create a dynamic urban skatepark scene with energetic, action-sports cinematography. Include authentic skate culture elements: concrete ramps, graffiti art, worn grip tape, skateboard decks, urban textures, and street art. Use dramatic natural lighting with strong shadows and golden hour sun rays, shot with 24mm wide-angle lens at f/2.8 to capture the environment. Include action elements like motion blur, dust particles in sunbeams, urban decay, and vibrant street art colors. Create a rebellious, youthful, high-energy atmosphere with saturated colors and gritty urban aesthetics. Color grading with punchy contrast, vibrant graffiti colors, and film-like grain for authentic street photography feel.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'duck':
          return `${screenPreservationInstruction}Create a playful, quirky scene featuring a large bright yellow rubber duck positioned prominently next to the computer device. The rubber duck should be oversized and whimsical, creating a fun contrast with the technology. Use bright, cheerful lighting with soft shadows, shot with 50mm lens at f/2.8 for professional product photography. Include playful elements: clean modern desk surface, subtle bathroom/spa aesthetic touches, water droplets, and bright clean backgrounds. Create a lighthearted, creative, unexpected atmosphere with vibrant yellow as the hero color. Professional lifestyle photography with editorial quality, clean composition, and premium product aesthetic. Color grading with bright, saturated yellows, clean whites, and playful contrast. The duck should be a key focal point that adds personality and humor to the composition.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'rave':
          return `${screenPreservationInstruction}Create an electrifying electronic music festival scene with intense, dynamic lighting like a concert film. Include authentic rave elements: laser beams cutting through atmospheric haze, LED panels, strobe effects, DJ equipment, crowds in motion, and dramatic fog machines. Use dramatic multi-colored lighting with vibrant neon RGB effects—electric blues, hot pinks, laser greens, and UV purples, shot with 35mm lens at f/1.4 to capture the energy. Include festival elements like light trails, bokeh from stage lights, silhouettes in colored fog, and dynamic energy. Create an exhilarating, high-energy, futuristic atmosphere with pulsing colors and movement. Color grading with saturated neon colors, deep contrast, and cinematic bloom effects for that festival night aesthetic.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'garage':
          return `${screenPreservationInstruction}Create a gritty automotive workshop scene with industrial, documentary-style cinematography. Include authentic garage elements: tool chests with worn tools, oil stains on concrete floors, engine parts, work lights hanging from ceiling, vintage automotive posters, and industrial shelving. Use dramatic directional lighting from work lamps and garage door sunlight streaming in, shot with 35mm lens at f/2.0 for depth. Include workshop elements like mechanic's hands with tools, spare parts, vintage motorcycles or cars in background, and authentic wear and tear. Create a hardworking, authentic, masculine atmosphere with industrial aesthetics. Color grading with desaturated industrial tones, warm work-light orange, cool shadows, and gritty texture overlays for that workshop documentary feel.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'paris':
          return `${screenPreservationInstruction}Create a romantic Parisian scene with cinematic European aesthetic like a luxury travel film. Include iconic Paris elements: wrought iron Juliet balconies, classic bistro furniture, fresh croissants and coffee, Eiffel Tower visible in dreamy background bokeh, cobblestone textures, and vintage Parisian architecture. Use soft, diffused natural lighting reminiscent of French cinema's golden hour, shot with 50mm lens at f/1.4 for creamy bokeh. Include atmospheric elements like morning mist, café culture, fresh flowers, vintage books, and classic French design. Create a romantic, elegant, timeless atmosphere with European sophistication. Color grading with warm sepia undertones, soft contrast, muted pastels, and that classic French film aesthetic.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'nyc':
          return `${screenPreservationInstruction}Create a bold New York City scene with gritty urban cinematography like a blockbuster film. Include iconic NYC elements: yellow taxi cabs, steam rising from manhole covers, towering skyscrapers, Times Square neon signs, fire escapes, and urban architecture. Use dramatic lighting with strong contrast between artificial city lights and natural light, shot with 35mm lens at f/2.0 to capture the urban energy. Include metropolitan elements like coffee cups, subway maps, city newspapers, street photography aesthetic, and constant motion. Create an energetic, fast-paced, ambitious atmosphere with urban grit and metropolitan sophistication. Color grading with high contrast, desaturated with pops of neon, deep blacks, and that New York street photography look with film grain.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'tokyo':
          return `${screenPreservationInstruction}Create a futuristic Tokyo scene with cyberpunk-meets-modern-Japan cinematography like a sci-fi film. Include authentic Tokyo elements: vibrant neon kanji signs, vending machines glowing in the night, sakura blossoms, traditional paper lanterns mixed with modern tech, and reflective rain-slicked streets. Use dramatic neon lighting with pink, blue, and purple accents mixed with traditional warm lantern light, shot with 35mm lens at f/1.4 for neon bokeh. Include Japanese elements like bullet trains in motion blur, anime-style aesthetics, technology contrasting with tradition, and vertical neon text. Create a high-tech, mysterious, culturally rich atmosphere blending ancient tradition with cutting-edge future. Color grading with saturated neon colors (hot pink, electric blue, neon purple), cinematic teal shadows, and that Tokyo-night cyberpunk aesthetic.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'store':
          return `${screenPreservationInstruction}Create a minimalist Apple Store environment with clean, modern retail cinematography like an iconic tech product launch. Include signature Apple Store elements: seamless white walls, blonde wooden tables displaying products in perfect alignment, glowing Apple logos, large glass windows with natural light flooding in, and geometric simplicity. Use bright, even lighting with soft natural illumination and subtle spotlights on products, shot with 50mm lens at f/2.8 for crisp clarity. Include retail elements like precisely arranged product displays, minimalist signage with Helvetica typography, clean architectural lines, floating glass staircases, and the distinctive warm glow of backlit displays. Create an innovative, aspirational, premium atmosphere with perfect symmetry and breathing room. Color grading with clean whites, warm wood tones, subtle cool highlights, and that signature Apple retail aesthetic with immaculate precision.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
        case 'custom':
          return (customPrompt || 'Use commercial catalog aesthetic with shallow depth of field and realistic texture fidelity. High-end, clean, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter.') + bokehEnhancement;
        default:
          return `Use commercial catalog aesthetic with shallow depth of field and realistic texture fidelity. High-end, clean, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter.${bokehEnhancement}`;
      }
    };
    
    // Helper function to get view prompt
    const getViewPrompt = (viewAngle) => {
      switch (viewAngle) {
        case 'left-angled':
          return 'Use left three-quarter angle composition with proper perspective and depth.';
        case 'frontal':
          return 'Use frontal straight-on composition with centered positioning.';
        case 'isometric':
          return 'Use isometric perspective composition with 3D depth and proper angles.';
        case 'right-angled':
        default:
          return 'Use three-quarter angle composition with proper perspective and depth.';
      }
    };
    
    const viewAngleDesc = getViewAngleDescription(selectedViewAngle);

    // Handle multiple files - create single composite image with multiple devices
    if (fileCount >= 2) {
      // Auto-select isometric view for 5+ files
      const effectiveViewAngle = fileCount >= 5 ? 'isometric' : selectedViewAngle;
      const effectiveViewAngleDesc = getViewAngleDescription(effectiveViewAngle);
      
      // Apply style and view settings to multiple files
      const stylePrompt = getStylePrompt(selectedCategory, showDeviceInMockup);
      const viewPrompt = getViewPrompt(effectiveViewAngle);
      
      if (!showDeviceInMockup) {
        // No device case for multiple files - use category-specific styles
        const getNoDeviceStylePrompt = (category) => {
          const bokehEnhancement = showBokehInMockup ? ' with cinematic bokeh background using professional camera with wide aperture lens (f/1.4-f/2.8) for dreamy, soft background blur, enhanced depth of field, and premium photography aesthetic' : '';
          switch (category) {
            case 'simple':
              return `Use commercial catalog aesthetic with shallow depth of field and realistic texture fidelity. High-end, clean, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter.${bokehEnhancement}`;
            case 'color':
              const getColorInfoMulti = (color) => {
                if (color && color.startsWith('#')) {
                  return { hex: color, name: color };
                }
                const colorMap = {
                  blue: { hex: '#3B82F6', name: 'blue' },
                  red: { hex: '#EF4444', name: 'red' },
                  green: { hex: '#10B981', name: 'green' },
                  yellow: { hex: '#F59E0B', name: 'yellow' },
                  purple: { hex: '#8B5CF6', name: 'purple' },
                  pink: { hex: '#EC4899', name: 'pink' },
                  orange: { hex: '#F97316', name: 'orange' },
                  cyan: { hex: '#06B6D4', name: 'cyan' },
                  teal: { hex: '#14B8A6', name: 'teal' },
                  indigo: { hex: '#6366F1', name: 'indigo' },
                  rose: { hex: '#F43F5E', name: 'rose' },
                  emerald: { hex: '#059669', name: 'emerald' }
                };
                return colorMap[color] || colorMap.blue;
              };
              const colorInfoMulti = getColorInfoMulti(selectedColor);
              return `Create a clean, solid color background environment using the exact color ${colorInfoMulti.hex} (${colorInfoMulti.name}). The devices/screens should be levitating/floating in mid-air with realistic soft shadows cast beneath them. Use professional studio lighting with the entire background as a uniform ${colorInfoMulti.hex} solid color - no gradients, no fog, no atmospheric effects - just a clean, flat ${colorInfoMulti.name} background. The devices should have professional product photography lighting. Show realistic soft drop shadows beneath each levitating element. Commercial catalog aesthetic with shallow depth of field.${bokehEnhancement}`;
            case 'office':
              return `Use professional office environment aesthetic with clean, modern lighting and corporate atmosphere. Sharp focus, perfect color balance, professional mood with soft studio key light and crisp accents.${bokehEnhancement}`;
            case 'fiori':
              return `Use Studio design system aesthetic with clean, modern UI elements and professional lighting. Sharp focus, perfect color balance, modern design mood with soft studio key light and crisp accents.${bokehEnhancement}`;
            case 'stones':
              return `Use concrete, textured aesthetic with natural stone surfaces and organic lighting. Sharp focus, perfect color balance, natural mood with soft studio key light and crisp accents.${bokehEnhancement}`;
            case 'gamer':
              return `Use gaming aesthetic with dynamic lighting and energetic atmosphere. Sharp focus, perfect color balance, gaming mood with soft studio key light and crisp accents.${bokehEnhancement}`;
            case 'view':
              return `Use scenic view aesthetic with natural outdoor lighting and environmental atmosphere. Sharp focus, perfect color balance, natural mood with soft studio key light and crisp accents.${bokehEnhancement}`;
            case 'ocean':
              return `Use ocean-themed aesthetic with blue tones and aquatic atmosphere. Sharp focus, perfect color balance, oceanic mood with soft studio key light and crisp accents.${bokehEnhancement}`;
            case 'designer':
              return `Use designer aesthetic with creative lighting and artistic atmosphere. Sharp focus, perfect color balance, creative mood with soft studio key light and crisp accents.${bokehEnhancement}`;
            case 'coffee':
              return `Use coffee shop aesthetic with warm lighting and cozy atmosphere. Sharp focus, perfect color balance, warm mood with soft studio key light and crisp accents.${bokehEnhancement}`;
            case 'business':
              return `Use business professional aesthetic with clean, corporate lighting and professional atmosphere. Sharp focus, perfect color balance, professional mood with soft studio key light and crisp accents.${bokehEnhancement}`;
            case 'artsy':
              return `Use music venue aesthetic with dynamic lighting and musical atmosphere. Include recording studio elements, musical instruments, sound equipment, and vibrant stage lighting. Sharp focus, perfect color balance, musical mood with dynamic studio lighting and crisp accents.${bokehEnhancement}`;
            case 'abstract':
              return `Use creative, expressive, and modern UI design themes with inventive lighting and professional, design-forward mood. Focus on imaginative and stylized interpretation rather than realism. Sharp focus, perfect color balance, modern aesthetic with soft studio key light and crisp accents.${bokehEnhancement}`;
            case 'sport':
              return `Use bold lighting and vibrant color balance to evoke an active, competitive atmosphere. Captures the excitement and sharpness found in sports-related contexts. Sharp focus, perfect color balance, energetic mood with soft studio key light and crisp accents.${bokehEnhancement}`;
            case 'nature':
              return `Use aesthetics rooted in the outdoors, natural surfaces, or aquatic scenes with natural or organic lighting, textures from stone or water, and scenic or environmental moods. Promotes tranquility and connection to the natural world. Sharp focus, perfect color balance, natural mood with soft studio key light and crisp accents.${bokehEnhancement}`;
            case 'luxury':
              return `${screenPreservationInstruction}Create a luxury environment with opulent materials: polished marble surfaces, brushed gold accents, crystal chandeliers casting warm light, and rich velvet textures. Use dramatic chiaroscuro lighting with deep shadows and golden highlights. Professional composition with shallow depth of field. Include luxury elements like vintage champagne glasses, designer watches, premium leather goods, elegant table settings with fine china, crystal wine glasses, and fresh flowers. Include background elements like wine cellars, open kitchens, and sophisticated diners. Color grading with warm golden tones, rich contrast, and warm amber tones with soft shadows.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'space':
              return `${screenPreservationInstruction}Create a futuristic space station environment with sleek metallic surfaces, holographic displays, and ambient blue lighting. Include advanced technology elements like holographic displays, floating panels, and energy conduits. Use cool blue and white lighting with subtle neon accents. Professional composition with shallow depth of field. Include space station elements like control panels, viewports showing starfields, and futuristic architecture. Color grading with cool blues, whites, and subtle cyan highlights.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'octopus':
              return `${screenPreservationInstruction}Create an underwater scene with deep oceanic blues and mysterious lighting filtering through water. Include highly detailed, realistic octopus tentacles with natural texture, subtle color variations, and organic movement patterns. Show tentacles with authentic sucker details, natural curvature, and lifelike positioning. Include flowing seaweed, bioluminescent particles, and marine life silhouettes. Use dramatic underwater lighting with blue-green tones and ethereal glow effects. Include coral formations and realistic underwater atmosphere. Color grading with deep blues and teal accents.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'paraglide':
              return `${screenPreservationInstruction}Create a high-altitude adventure scene with vast mountain landscapes and dramatic cloud formations. Include paragliding equipment, harnesses, and safety gear. Use natural lighting with dramatic sky conditions and mountain shadows. Include elements like wind-blown fabric, mountain peaks, and endless sky. Color grading with cool blues and natural earth tones.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'rooftop':
              return `${screenPreservationInstruction}Create an urban rooftop scene with city skyline backdrop and golden hour lighting. Include modern rooftop elements like potted plants, outdoor furniture, and city views. Use shallow depth of field, capturing the urban atmosphere. Include elements like string lights, city traffic below, and architectural details. Color grading with warm sunset tones and urban contrast.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'car':
              return `${screenPreservationInstruction}Create a cinematic automotive showroom scene with dramatic lighting reminiscent of luxury car commercials. Include sleek luxury automobile elements: polished metallic surfaces, leather seats, chrome details, and glossy paint reflections. Use professional automotive photography lighting with dramatic rim lights and key lights at 45-degree angles, shot with 85mm lens at f/1.4 for shallow depth of field. Include showroom elements like spotlights, reflective floors, and modern architecture. Create a high-performance, aspirational atmosphere with cool blue and warm amber accent lighting. Color grading with deep blacks, metallic highlights, and cinematic contrast.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'bar':
              return `${screenPreservationInstruction}Create an upscale cocktail bar environment with moody, atmospheric lighting like a premium spirits advertisement. Include sophisticated bar elements: crystal glassware, premium liquor bottles with amber backlighting, polished mahogany bar surfaces, brass fixtures, and leather bar stools. Use cinematic low-key lighting with warm tungsten practicals and subtle neon accents, shot with 50mm lens at f/1.8 for dreamy bokeh. Include atmospheric elements like smoke wisps, ice cubes catching light, garnishes, and bottles with dramatic backlighting. Create an intimate, sophisticated atmosphere with warm golden hour tones mixed with cool blue shadows. Color grading with rich amber warmth, deep shadows, and elegant contrast.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'restaurant':
              return `${screenPreservationInstruction}Create an elegant fine dining restaurant scene with warm, inviting cinematography like a premium culinary magazine. Include sophisticated restaurant elements: pristine white tablecloths, polished silverware, wine glasses catching candlelight, fresh plated cuisine with artistic presentation, elegant table settings, and soft ambient lighting from overhead pendants. Use warm, diffused lighting with golden hour tones and soft shadows, shot with 50mm lens at f/1.8 for shallow depth of field. Include culinary elements like fresh ingredients, artisanal bread baskets, sommelier wine selections, open kitchen glimpses with chef stations, and soft bokeh from ambient candles. Create a refined, sophisticated, appetizing atmosphere with warm rustic tones. Color grading with warm amber lighting, soft highlights, rich wood tones, and that editorial food photography aesthetic.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'skatepark':
              return `${screenPreservationInstruction}Create a dynamic urban skatepark scene with energetic, action-sports cinematography. Include authentic skate culture elements: concrete ramps, graffiti art, worn grip tape, skateboard decks, urban textures, and street art. Use dramatic natural lighting with strong shadows and golden hour sun rays, shot with 24mm wide-angle lens at f/2.8 to capture the environment. Include action elements like motion blur, dust particles in sunbeams, urban decay, and vibrant street art colors. Create a rebellious, youthful, high-energy atmosphere with saturated colors and gritty urban aesthetics. Color grading with punchy contrast, vibrant graffiti colors, and film-like grain for authentic street photography feel.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'duck':
              return `${screenPreservationInstruction}Create a playful, quirky scene featuring a large bright yellow rubber duck positioned prominently next to the screens. The rubber duck should be oversized and whimsical, creating a fun contrast with the technology. Use bright, cheerful lighting with soft shadows, shot with 50mm lens at f/2.8 for professional product photography. Include playful elements: clean modern desk surface, subtle bathroom/spa aesthetic touches, water droplets, and bright clean backgrounds. Create a lighthearted, creative, unexpected atmosphere with vibrant yellow as the hero color. Color grading with bright, saturated yellows, clean whites, and playful contrast. The duck should be a key focal point that adds personality and humor to the composition.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'rave':
              return `${screenPreservationInstruction}Create an electrifying electronic music festival scene with intense, dynamic lighting like a concert film. Include authentic rave elements: laser beams cutting through atmospheric haze, LED panels, strobe effects, DJ equipment, crowds in motion, and dramatic fog machines. Use dramatic multi-colored lighting with vibrant neon RGB effects—electric blues, hot pinks, laser greens, and UV purples, shot with 35mm lens at f/1.4 to capture the energy. Include festival elements like light trails, bokeh from stage lights, silhouettes in colored fog, and dynamic energy. Create an exhilarating, high-energy, futuristic atmosphere with pulsing colors and movement. Color grading with saturated neon colors, deep contrast, and cinematic bloom effects for that festival night aesthetic.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'garage':
              return `${screenPreservationInstruction}Create a gritty automotive workshop scene with industrial, documentary-style cinematography. Include authentic garage elements: tool chests with worn tools, oil stains on concrete floors, engine parts, work lights hanging from ceiling, vintage automotive posters, and industrial shelving. Use dramatic directional lighting from work lamps and garage door sunlight streaming in, shot with 35mm lens at f/2.0 for depth. Include workshop elements like mechanic's hands with tools, spare parts, vintage motorcycles or cars in background, and authentic wear and tear. Create a hardworking, authentic, masculine atmosphere with industrial aesthetics. Color grading with desaturated industrial tones, warm work-light orange, cool shadows, and gritty texture overlays for that workshop documentary feel.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'paris':
              return `${screenPreservationInstruction}Create a romantic Parisian scene with cinematic European aesthetic like a luxury travel film. Include iconic Paris elements: wrought iron Juliet balconies, classic bistro furniture, fresh croissants and coffee, Eiffel Tower visible in dreamy background bokeh, cobblestone textures, and vintage Parisian architecture. Use soft, diffused natural lighting reminiscent of French cinema's golden hour, shot with 50mm lens at f/1.4 for creamy bokeh. Include atmospheric elements like morning mist, café culture, fresh flowers, vintage books, and classic French design. Create a romantic, elegant, timeless atmosphere with European sophistication. Color grading with warm sepia undertones, soft contrast, muted pastels, and that classic French film aesthetic.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'nyc':
              return `${screenPreservationInstruction}Create a bold New York City scene with gritty urban cinematography like a blockbuster film. Include iconic NYC elements: yellow taxi cabs, steam rising from manhole covers, towering skyscrapers, Times Square neon signs, fire escapes, and urban architecture. Use dramatic lighting with strong contrast between artificial city lights and natural light, shot with 35mm lens at f/2.0 to capture the urban energy. Include metropolitan elements like coffee cups, subway maps, city newspapers, street photography aesthetic, and constant motion. Create an energetic, fast-paced, ambitious atmosphere with urban grit and metropolitan sophistication. Color grading with high contrast, desaturated with pops of neon, deep blacks, and that New York street photography look with film grain.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'tokyo':
              return `${screenPreservationInstruction}Create a futuristic Tokyo scene with cyberpunk-meets-modern-Japan cinematography like a sci-fi film. Include authentic Tokyo elements: vibrant neon kanji signs, vending machines glowing in the night, sakura blossoms, traditional paper lanterns mixed with modern tech, and reflective rain-slicked streets. Use dramatic neon lighting with pink, blue, and purple accents mixed with traditional warm lantern light, shot with 35mm lens at f/1.4 for neon bokeh. Include Japanese elements like bullet trains in motion blur, anime-style aesthetics, technology contrasting with tradition, and vertical neon text. Create a high-tech, mysterious, culturally rich atmosphere blending ancient tradition with cutting-edge future. Color grading with saturated neon colors (hot pink, electric blue, neon purple), cinematic teal shadows, and that Tokyo-night cyberpunk aesthetic.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'store':
              return `${screenPreservationInstruction}Create a minimalist Apple Store environment with clean, modern retail cinematography like an iconic tech product launch. Include signature Apple Store elements: seamless white walls, blonde wooden tables displaying products in perfect alignment, glowing Apple logos, large glass windows with natural light flooding in, and geometric simplicity. Use bright, even lighting with soft natural illumination and subtle spotlights on products, shot with 50mm lens at f/2.8 for crisp clarity. Include retail elements like precisely arranged product displays, minimalist signage with Helvetica typography, clean architectural lines, floating glass staircases, and the distinctive warm glow of backlit displays. Create an innovative, aspirational, premium atmosphere with perfect symmetry and breathing room. Color grading with clean whites, warm wood tones, subtle cool highlights, and that signature Apple retail aesthetic with immaculate precision.${bokehEnhancement} Fill entire canvas - no black bars or letterboxing.`;
            case 'custom':
              return (customPrompt || 'Use commercial catalog aesthetic with shallow depth of field and realistic texture fidelity. High-end, clean, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter.') + bokehEnhancement;
            case 'auto':
              return 'INTELLIGENT CONTENT ANALYSIS: Analyze each screenshot to identify industry, brand identity, and visual themes. Extract key elements: logos, colors, typography. Create an environment that perfectly matches the identified themes. Include relevant props, industry elements, and subtle branding (neon signs, wall displays, ambient lighting with brand names/logos). Match atmosphere to target audience. Examples: Fashion = retail store with brand neon | Food = kitchen/dining with restaurant name neon | Tech = office with brand signage | Travel = destination theme with brand elements.';
            default:
              return `Use commercial catalog aesthetic with shallow depth of field and realistic texture fidelity. High-end, clean, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter.${bokehEnhancement}`;
          }
        };
        
        const noDeviceStylePrompt = getNoDeviceStylePrompt(selectedCategory);
        
        if (fileCount >= 3) {
          if (fileCount >= 5) {
            // 5+ files - use isometric layout without devices
            return `${screenPreservationInstruction}Create a single composite mockup image showing ${fileCount} floating screens arranged in an isometric 3D layout. Each screen should display one of the attached images. Only add realistic perspective and depth. Arrange the screens in a dynamic isometric composition with proper depth and perspective. Use ${effectiveViewAngleDesc} composition with 3D depth and proper angles. Create an engaging isometric arrangement with varying depths and angles. ${noDeviceStylePrompt} ${viewPrompt} Show all ${fileCount} screens in ONE single composite image.`;
          } else {
            // 3-4 files - use flat, frontal layout without devices
            return `${screenPreservationInstruction}Create a single composite mockup image showing ${fileCount} floating screens arranged in a flat, frontal layout. Each screen should display one of the attached images. Only add realistic perspective. Arrange the screens side by side in a straight line with proper spacing. Use ${effectiveViewAngleDesc} composition - if Front is selected, show screens from the front view, not angled. The screens should be positioned flat on a surface with no angles or tilting. CRITICAL: The ENTIRE image must be straight and level - NO tilting, NO rotation, NO angled composition. Create a perfectly horizontal, straight-on view. ${noDeviceStylePrompt} ${viewPrompt} Show all ${fileCount} screens in ONE single composite image.`;
          }
        } else {
          // 2 files - show 2 screens side by side without devices
          return `${screenPreservationInstruction}Create a single composite mockup image showing 2 floating screens side by side. Each screen should display one of the attached images. Only add realistic perspective. Use ${effectiveViewAngleDesc} composition. ${noDeviceStylePrompt} ${viewPrompt} Show both screens in ONE single composite image.`;
        }
      } else {
        // With devices case - recognize each screenshot's device type
        if (fileCount >= 3) {
          if (fileCount >= 5) {
            // 5+ files - use isometric layout with mixed devices
            return `${screenPreservationInstruction}Create a single composite mockup image showing ${fileCount} devices arranged in an isometric 3D layout. Each device should display one of the attached images. Based on the aspect ratio and dimensions of each screenshot, use the appropriate device frame: mobile screenshots (aspect ratio 0.4-0.6 or 1.6-2.5, small dimensions) should be shown in iPhone 17 Pro mockups (SMALLEST device - approximately 6 inches), tablet screenshots (medium dimensions, moderate aspect ratios) should be shown in iPad Pro mockups (MEDIUM size - approximately 11 inches), and desktop screenshots (large dimensions, wide aspect ratios) should be shown in MacBook Pro mockups (LARGEST device - approximately 14 inches). CRITICAL: Maintain realistic device proportions - iPhone 17 Pro must be significantly smaller than iPad Pro, iPad Pro must be smaller than MacBook Pro. The iPhone should be the smallest device in the composition. Arrange the devices in a dynamic isometric composition with proper depth and perspective. Use ${effectiveViewAngleDesc} composition with 3D depth and proper angles. Create an engaging isometric arrangement with varying depths and angles. ${stylePrompt} ${viewPrompt} Show all ${fileCount} devices in ONE single composite image.`;
          } else {
            // 3-4 files - use flat, frontal layout with mixed devices
            return `${screenPreservationInstruction}Create a single composite mockup image showing ${fileCount} devices arranged in a flat, frontal layout. Each device should display one of the attached images. Based on the aspect ratio and dimensions of each screenshot, use the appropriate device frame: mobile screenshots (aspect ratio 0.4-0.6 or 1.6-2.5, small dimensions) should be shown in iPhone 17 Pro mockups (SMALLEST device - approximately 6 inches), tablet screenshots (medium dimensions, moderate aspect ratios) should be shown in iPad Pro mockups (MEDIUM size - approximately 11 inches), and desktop screenshots (large dimensions, wide aspect ratios) should be shown in MacBook Pro mockups (LARGEST device - approximately 14 inches). CRITICAL: Maintain realistic device proportions - iPhone 17 Pro must be significantly smaller than iPad Pro, iPad Pro must be smaller than MacBook Pro. The iPhone should be the smallest device in the composition. Arrange the devices side by side in a straight line with proper spacing. Use ${effectiveViewAngleDesc} composition - if Front is selected, show devices from the front view, not angled. The devices should be positioned flat on a surface with no angles or tilting. CRITICAL: The ENTIRE image must be straight and level - NO tilting, NO rotation, NO angled composition. Create a perfectly horizontal, straight-on view. ${stylePrompt} ${viewPrompt} Show all ${fileCount} devices in ONE single composite image.`;
          }
        } else {
          // 2 files - show 2 devices side by side with appropriate device types
          return `${screenPreservationInstruction}Create a single composite mockup image showing 2 devices side by side. Each device should display one of the attached images. Based on the aspect ratio and dimensions of each screenshot, use the appropriate device frame: mobile screenshots (aspect ratio 0.4-0.6 or 1.6-2.5, small dimensions) should be shown in iPhone 17 Pro mockups (SMALLEST device - approximately 6 inches), tablet screenshots (medium dimensions, moderate aspect ratios) should be shown in iPad Pro mockups (MEDIUM size - approximately 11 inches), and desktop screenshots (large dimensions, wide aspect ratios) should be shown in MacBook Pro mockups (LARGEST device - approximately 14 inches). CRITICAL: Maintain realistic device proportions - iPhone 17 Pro must be significantly smaller than iPad Pro, iPad Pro must be smaller than MacBook Pro. The iPhone should be the smallest device in the composition. Use ${effectiveViewAngleDesc} composition. ${stylePrompt} ${viewPrompt} Show both devices in ONE single composite image.`;
        }
      }
    }

    if (!showDeviceInMockup) {
      switch (selectedCategory) {
        case 'simple':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium minimalist mockup without any device. Present the screenshot as a SINGLE floating screen on a pristine white marble surface with subtle texture, shot with a 50mm equivalent lens at f/2.8, morning studio light with edge diffusion. Only add realistic perspective and lighting. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, clean, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter. Show only ONE screen with professional product placement. Do not show multiple screens.`;
        case 'color':
          const getColorInfoNoDevice = (color) => {
            if (color && color.startsWith('#')) {
              return { hex: color, name: color };
            }
            const colorMap = {
              blue: { hex: '#3B82F6', name: 'blue' },
              red: { hex: '#EF4444', name: 'red' },
              green: { hex: '#10B981', name: 'green' },
              yellow: { hex: '#F59E0B', name: 'yellow' },
              purple: { hex: '#8B5CF6', name: 'purple' },
              pink: { hex: '#EC4899', name: 'pink' },
              orange: { hex: '#F97316', name: 'orange' },
              cyan: { hex: '#06B6D4', name: 'cyan' },
              teal: { hex: '#14B8A6', name: 'teal' },
              indigo: { hex: '#6366F1', name: 'indigo' },
              rose: { hex: '#F43F5E', name: 'rose' },
              emerald: { hex: '#059669', name: 'emerald' }
            };
            return colorMap[color] || colorMap.blue;
          };
          const colorInfoNoDevice = getColorInfoNoDevice(selectedColor);
          return `${screenPreservationInstruction}${conditionalInstructions}Create a clean, solid color background mockup without any device. Present the screenshot as a SINGLE floating screen levitating in mid-air, shot with 50mm lens at f/2.8, professional studio lighting. The entire background should be a uniform solid ${colorInfoNoDevice.hex} color (${colorInfoNoDevice.name}) - no gradients, no fog, no atmospheric effects - just a clean, flat ${colorInfoNoDevice.name} background. The screen should appear to be floating with realistic soft drop shadows cast beneath it. Use ${viewAngleDesc} composition with professional product photography lighting that makes the screen stand out against the ${colorInfoNoDevice.name} background. Commercial catalog aesthetic with shallow depth of field and realistic texture fidelity. High-end, bold, aspirational mood. Sharp focus, perfect color balance. Show only ONE screen with professional product placement. Do not show multiple screens.`;
        case 'office':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a sophisticated corporate startup mockup without any device. Present the screenshot as a SINGLE floating screen on a premium walnut desk with ${viewAngleDesc}, shot with 85mm lens at f/2.8, morning studio light with edge diffusion. Include subtle startup elements: premium notebook, modern office accessories. Use commercial catalog aesthetic with shallow depth of field, negative space on left, and realistic texture fidelity. High-end, bold, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, professional product placement. Show only ONE screen with intentional composition. Do not show multiple screens.`;
        case 'fiori':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium Studio Horizon enterprise mockup without any device. Present the screenshot as a SINGLE floating screen on a pristine white surface with Studio's signature blue accent lighting, shot with 50mm lens at f/2.8, morning studio light with edge diffusion. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, professional, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter. Show only ONE screen with intentional product placement and enterprise-grade aesthetics. Do not show multiple screens.`;
        case 'stones':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a dramatic mockup without any device. Present the screenshot as a SINGLE floating screen on stacked concrete blocks in a dimly-lit studio with a dark grey background. Show only ONE screen. The overall scene is monochrome, with soft shadows highlighting the screen's contours and premium finish. Do not show multiple screens.`;
        case 'gamer':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium gaming setup mockup without any device. Present the screenshot as a SINGLE floating screen in a high-tech gaming environment with RGB neon lighting, shot with 50mm lens at f/2.8, dramatic studio lighting with edge diffusion. Include gaming peripherals: mechanical keyboards, RGB mouse pads, gaming chairs, multiple monitors. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, bold, aspirational mood with vibrant cyan, purple, and pink RGB lighting. Sharp focus, perfect color balance, no distractions or clutter. Show only ONE screen with intentional product placement in premium gaming setup. Do not show multiple screens.`;
        case 'view':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium urban lifestyle mockup without any device. Present the screenshot as a SINGLE floating screen positioned against a breathtaking night cityscape, shot with 85mm lens at f/2.8, dramatic studio lighting with edge diffusion. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, bold, aspirational mood with warm ambient lighting and city lights reflecting on surfaces. Sharp focus, perfect color balance, no distractions or clutter. Show only ONE screen with intentional product placement against premium urban backdrop. Do not show multiple screens.`;
        case 'ocean':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium coastal lifestyle mockup without any device. Present the screenshot as a SINGLE floating screen positioned against a beautiful ocean horizon, shot with 85mm lens at f/2.8, morning studio light with edge diffusion. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, clean, aspirational mood with warm golden tones and soft ocean reflections. Sharp focus, perfect color balance, no distractions or clutter. Show only ONE screen with intentional product placement against premium coastal backdrop. Do not show multiple screens.`;
        case 'designer':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium creative studio mockup without any device. Present the screenshot as a SINGLE floating screen in a sophisticated design studio, shot with 50mm lens at f/2.8, morning studio light with edge diffusion. Include premium materials: designer furniture, mood boards, color swatches, design tools, creative inspiration elements. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, bold, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter. Show only ONE screen with intentional product placement in premium creative environment. Do not show multiple screens.`;
        case 'coffee':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium cafe lifestyle mockup without any device. Present the screenshot as a SINGLE floating screen in a trendy coffee shop environment, shot with 50mm lens at f/2.8, morning studio light with edge diffusion. Include wooden textures, latte art, modern furniture, warm lighting. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, clean, aspirational mood with warm inviting lighting and coffee shop ambiance. Sharp focus, perfect color balance, no distractions or clutter. Show only ONE screen with intentional product placement in premium cafe environment. Do not show multiple screens.`;
        case 'business':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium corporate executive mockup without any device. Present the screenshot as a SINGLE floating screen in a sophisticated boardroom setting, shot with 85mm lens at f/2.8, morning studio light with edge diffusion. Include elegant boardroom furniture, executive desk setup, modern office architecture, business documents. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, professional, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter. Show only ONE screen with intentional product placement in premium corporate environment. Do not show multiple screens.`;
        case 'artsy':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium music venue mockup without any device. Present the screenshot as a SINGLE floating screen in a recording studio or music venue environment, shot with 50mm lens at f/2.8, dynamic studio lighting with edge diffusion. Include musical instruments, sound equipment, recording studio elements, stage lighting, and musical atmosphere. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, dynamic, musical mood with vibrant stage lighting and crisp accents. Sharp focus, perfect color balance, no distractions or clutter. Show only ONE screen with intentional product placement in premium music environment. Do not show multiple screens.`;
        case 'abstract':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium abstract design mockup without any device. Present the screenshot as a SINGLE floating screen in a modern, creative environment with inventive lighting and artistic elements, shot with 50mm lens at f/2.8, morning studio light with edge diffusion. Include abstract design elements, geometric shapes, modern materials, and creative lighting. Use creative, expressive, and modern UI design themes with inventive lighting and professional, design-forward mood. Focus on imaginative and stylized interpretation rather than realism. Sharp focus, perfect color balance, modern aesthetic with soft studio key light and crisp accents. Show only ONE screen with intentional product placement in a premium abstract environment. Do not show multiple screens.`;
        case 'sport':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium sports and fitness mockup without any device. Present the screenshot as a SINGLE floating screen in an active, energetic environment with bold lighting and vibrant colors, shot with 50mm lens at f/2.8, dramatic studio lighting with edge diffusion. Include sports equipment, fitness gear, dynamic lighting, and energetic atmosphere. Use bold lighting and vibrant color balance to evoke an active, competitive atmosphere. Captures the excitement and sharpness found in sports-related contexts. Sharp focus, perfect color balance, energetic mood with soft studio key light and crisp accents. Show only ONE screen with intentional product placement in a premium sports environment. Do not show multiple screens.`;
        case 'nature':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium natural environment mockup without any device. Present the screenshot as a SINGLE floating screen in a serene outdoor setting with natural lighting and organic textures, shot with 85mm lens at f/2.8, morning studio light with edge diffusion. Include natural elements like stone surfaces, wood textures, plants, or water features. Use aesthetics rooted in the outdoors, natural surfaces, or aquatic scenes with natural or organic lighting, textures from stone or water, and scenic or environmental moods. Promotes tranquility and connection to the natural world. Sharp focus, perfect color balance, natural mood with soft studio key light and crisp accents. Show only ONE screen with intentional product placement in a premium natural environment. Do not show multiple screens.`;
        case 'luxury':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a luxury mockup without any device. Present the screenshot as a SINGLE floating screen in an opulent environment with polished marble surfaces, brushed gold accents, and crystal chandeliers casting warm light, dramatic chiaroscuro lighting. Include luxury elements like vintage champagne glasses, designer watches, premium leather goods, and rich velvet textures. Use professional composition with shallow depth of field and dramatic shadows. Color grading with warm golden tones and rich contrast. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium luxury environment. Do not show multiple screens.`;
        case 'space':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a futuristic space station mockup without any device. Present the screenshot as a SINGLE floating screen in a sleek space station environment with metallic surfaces, holographic displays, and ambient blue lighting, cool futuristic ambiance. Include advanced technology elements like holographic displays, floating panels, energy conduits, control panels, and viewports showing starfields. Use professional composition with shallow depth of field and cool lighting. Color grading with cool blues, whites, and subtle cyan highlights. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium space station environment. Do not show multiple screens.`;
        case 'octopus':
          return `${screenPreservationInstruction}${conditionalInstructions}Create an underwater mockup without any device. Present the screenshot as a SINGLE floating screen in a mysterious deep-sea environment with oceanic blues and ethereal lighting filtering through water, dramatic underwater lighting. Include highly detailed, realistic octopus tentacles with natural texture, subtle color variations, and organic movement patterns. Show tentacles with authentic sucker details, natural curvature, and lifelike positioning. Include flowing seaweed, bioluminescent particles, and marine life silhouettes. Use dramatic underwater lighting with blue-green tones and ethereal glow effects. Color grading with deep blues and teal accents. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium underwater environment. Do not show multiple screens.`;
        case 'paraglide':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a high-altitude adventure mockup without any device. Present the screenshot as a SINGLE floating screen in a vast mountain landscape with dramatic cloud formations and natural lighting, capturing the scale and grandeur. Include paragliding equipment, harnesses, wind-blown fabric, mountain peaks, and endless sky. Use natural lighting with dramatic sky conditions and mountain shadows. Color grading with cool blues and natural earth tones. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium adventure environment. Do not show multiple screens.`;
        case 'rooftop':
          return `${screenPreservationInstruction}${conditionalInstructions}Create an urban rooftop mockup without any device. Present the screenshot as a SINGLE floating screen on an urban rooftop with city skyline backdrop and golden hour lighting, capturing the urban atmosphere. Include modern rooftop elements like potted plants, outdoor furniture, string lights, city traffic below, and architectural details. Use shallow depth of field with dramatic city lighting. Color grading with warm sunset tones and urban contrast. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium rooftop environment. Do not show multiple screens.`;
        case 'car':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a cinematic automotive showroom mockup without any device. Present the screenshot as a SINGLE floating screen in a luxury car showroom with dramatic lighting reminiscent of car commercials. Include sleek automobile elements: polished metallic surfaces, leather seats, chrome details, glossy paint reflections, showroom spotlights, reflective floors, and modern architecture. Use professional automotive photography lighting with dramatic rim lights and key lights at 45-degree angles, shot with 85mm lens at f/1.4 for shallow depth of field. Create a high-performance, aspirational atmosphere with cool blue and warm amber accent lighting. Color grading with deep blacks, metallic highlights, and cinematic contrast. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium automotive environment. Do not show multiple screens.`;
        case 'bar':
          return `${screenPreservationInstruction}${conditionalInstructions}Create an upscale cocktail bar mockup without any device. Present the screenshot as a SINGLE floating screen in a sophisticated bar environment with moody, atmospheric lighting like a premium spirits advertisement. Include bar elements: crystal glassware, premium liquor bottles with amber backlighting, polished mahogany surfaces, brass fixtures, leather bar stools, smoke wisps, ice cubes catching light, garnishes, and dramatic bottle backlighting. Use cinematic low-key lighting with warm tungsten practicals and subtle neon accents, shot with 50mm lens at f/1.8 for dreamy bokeh. Create an intimate, sophisticated atmosphere with warm golden hour tones mixed with cool blue shadows. Color grading with rich amber warmth, deep shadows, and elegant contrast. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium bar environment. Do not show multiple screens.`;
        case 'restaurant':
          return `${screenPreservationInstruction}${conditionalInstructions}Create an elegant fine dining restaurant mockup without any device. Present the screenshot as a SINGLE floating screen in a refined restaurant environment with warm, inviting cinematography like a premium culinary magazine. Include restaurant elements: pristine white tablecloths, polished silverware, wine glasses catching candlelight, fresh plated cuisine with artistic presentation, elegant table settings, soft ambient lighting from overhead pendants, fresh ingredients, artisanal bread baskets, sommelier wine selections, open kitchen glimpses with chef stations, and soft bokeh from ambient candles. Use warm, diffused lighting with golden hour tones and soft shadows, shot with 50mm lens at f/1.8 for shallow depth of field. Create a refined, sophisticated, appetizing atmosphere with warm rustic tones. Color grading with warm amber lighting, soft highlights, rich wood tones, and that editorial food photography aesthetic. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium restaurant environment. Do not show multiple screens.`;
        case 'skatepark':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a dynamic urban skatepark mockup without any device. Present the screenshot as a SINGLE floating screen in an energetic skatepark scene with action-sports cinematography. Include skate culture elements: concrete ramps, graffiti art, worn grip tape, skateboard decks, urban textures, street art, motion blur, dust particles in sunbeams, and urban decay. Use dramatic natural lighting with strong shadows and golden hour sun rays, shot with 24mm wide-angle lens at f/2.8 to capture the environment. Create a rebellious, youthful, high-energy atmosphere with saturated colors and gritty urban aesthetics. Color grading with punchy contrast, vibrant graffiti colors, and film-like grain for authentic street photography feel. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium skatepark environment. Do not show multiple screens.`;
        case 'duck':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a playful, quirky mockup without any device. Present the screenshot as a SINGLE floating screen with a large bright yellow rubber duck positioned prominently next to it. The rubber duck should be oversized and whimsical, creating a fun contrast with the technology. Use bright, cheerful lighting with soft shadows, shot with 50mm lens at f/2.8 for professional product photography. Include playful elements: clean modern desk surface, subtle bathroom/spa aesthetic touches, water droplets, and bright clean backgrounds. Create a lighthearted, creative, unexpected atmosphere with vibrant yellow as the hero color. Color grading with bright, saturated yellows, clean whites, and playful contrast. The duck should be a key focal point that adds personality and humor to the composition. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with the duck as a whimsical companion. Do not show multiple screens.`;
        case 'rave':
          return `${screenPreservationInstruction}${conditionalInstructions}Create an electrifying electronic music festival mockup without any device. Present the screenshot as a SINGLE floating screen in an intense rave scene with dynamic lighting like a concert film. Include rave elements: laser beams cutting through atmospheric haze, LED panels, strobe effects, DJ equipment, crowds in motion, dramatic fog machines, light trails, bokeh from stage lights, and silhouettes in colored fog. Use dramatic multi-colored lighting with vibrant neon RGB effects—electric blues, hot pinks, laser greens, and UV purples, shot with 35mm lens at f/1.4 to capture the energy. Create an exhilarating, high-energy, futuristic atmosphere with pulsing colors and movement. Color grading with saturated neon colors, deep contrast, and cinematic bloom effects for that festival night aesthetic. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium rave environment. Do not show multiple screens.`;
        case 'garage':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a gritty automotive workshop mockup without any device. Present the screenshot as a SINGLE floating screen in an industrial garage scene with documentary-style cinematography. Include garage elements: tool chests with worn tools, oil stains on concrete floors, engine parts, work lights hanging from ceiling, vintage automotive posters, industrial shelving, mechanic's tools, spare parts, and vintage motorcycles or cars in background. Use dramatic directional lighting from work lamps and garage door sunlight streaming in, shot with 35mm lens at f/2.0 for depth. Create a hardworking, authentic, masculine atmosphere with industrial aesthetics. Color grading with desaturated industrial tones, warm work-light orange, cool shadows, and gritty texture overlays for that workshop documentary feel. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium garage environment. Do not show multiple screens.`;
        case 'paris':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a romantic Parisian mockup without any device. Present the screenshot as a SINGLE floating screen in a cinematic Paris scene with European aesthetic like a luxury travel film. Include Paris elements: wrought iron Juliet balconies, classic bistro furniture, fresh croissants and coffee, Eiffel Tower visible in dreamy background bokeh, cobblestone textures, vintage Parisian architecture, morning mist, café culture, fresh flowers, vintage books, and classic French design. Use soft, diffused natural lighting reminiscent of French cinema's golden hour, shot with 50mm lens at f/1.4 for creamy bokeh. Create a romantic, elegant, timeless atmosphere with European sophistication. Color grading with warm sepia undertones, soft contrast, muted pastels, and that classic French film aesthetic. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium Parisian environment. Do not show multiple screens.`;
        case 'nyc':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a bold New York City mockup without any device. Present the screenshot as a SINGLE floating screen in a gritty urban NYC scene with blockbuster film cinematography. Include NYC elements: yellow taxi cabs, steam rising from manhole covers, towering skyscrapers, Times Square neon signs, fire escapes, urban architecture, coffee cups, subway maps, city newspapers, and constant motion. Use dramatic lighting with strong contrast between artificial city lights and natural light, shot with 35mm lens at f/2.0 to capture the urban energy. Create an energetic, fast-paced, ambitious atmosphere with urban grit and metropolitan sophistication. Color grading with high contrast, desaturated with pops of neon, deep blacks, and that New York street photography look with film grain. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium NYC environment. Do not show multiple screens.`;
        case 'tokyo':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a futuristic Tokyo mockup without any device. Present the screenshot as a SINGLE floating screen in a cyberpunk-meets-modern-Japan scene like a sci-fi film. Include Tokyo elements: vibrant neon kanji signs, vending machines glowing in the night, sakura blossoms, traditional paper lanterns mixed with modern tech, reflective rain-slicked streets, bullet trains in motion blur, anime-style aesthetics, technology contrasting with tradition, and vertical neon text. Use dramatic neon lighting with pink, blue, and purple accents mixed with traditional warm lantern light, shot with 35mm lens at f/1.4 for neon bokeh. Create a high-tech, mysterious, culturally rich atmosphere blending ancient tradition with cutting-edge future. Color grading with saturated neon colors (hot pink, electric blue, neon purple), cinematic teal shadows, and that Tokyo-night cyberpunk aesthetic. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium Tokyo environment. Do not show multiple screens.`;
        case 'store':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a minimalist Apple Store mockup without any device. Present the screenshot as a SINGLE floating screen in a clean, modern retail environment with iconic tech product launch cinematography. Include Apple Store elements: seamless white walls, blonde wooden tables displaying products in perfect alignment, glowing Apple logos, large glass windows with natural light flooding in, geometric simplicity, precisely arranged product displays, minimalist signage with Helvetica typography, clean architectural lines, floating glass staircases, and the distinctive warm glow of backlit displays. Use bright, even lighting with soft natural illumination and subtle spotlights on products, shot with 50mm lens at f/2.8 for crisp clarity. Create an innovative, aspirational, premium atmosphere with perfect symmetry and breathing room. Color grading with clean whites, warm wood tones, subtle cool highlights, and that signature Apple retail aesthetic with immaculate precision. Fill entire canvas - no black bars or letterboxing. Show only ONE screen with intentional product placement in a premium Apple Store environment. Do not show multiple screens.`;
        case 'custom':
          return `Create a professional mockup showing the uploaded screenshot in an environment featuring: ${customPrompt || 'clean, modern aesthetic'}. 

CRITICAL REQUIREMENTS:
- Preserve all screen content identically - no changes to text, colors, or layout
- Add realistic device frame and professional lighting
- Device should be centered with breathing room around it
- Fill entire canvas - no black bars or letterboxing
- The background environment should prominently feature: ${customPrompt || 'clean, modern aesthetic'}

ENVIRONMENT REQUIREMENTS:
- Background should clearly show: ${customPrompt || 'clean, modern aesthetic'}
- Use professional product photography lighting
- Create depth with foreground/background elements
- Make the environment the main focus while keeping the device prominent`;
        case 'auto':
          return `${screenPreservationInstruction}${conditionalInstructions}INTELLIGENT CONTENT ANALYSIS - Create a completely custom mockup environment by analyzing the screenshot content:

CONTENT ANALYSIS REQUIREMENTS:
- Identify the website/app type, industry, and brand identity from the screenshot
- Extract key visual elements: logos, colors, typography, and design style
- Determine the target audience and use case from the content
- Recognize any specific themes, products, or services being promoted

CONTEXTUAL ENVIRONMENT CREATION:
- Create an environment that perfectly matches the identified industry/theme
- Incorporate the brand's visual identity: use their color palette, logo elements, and design aesthetic
- Add relevant props and environmental elements that relate to the business/industry
- Include subtle branding elements: neon signs, wall displays, or ambient lighting featuring the brand name/logo
- Match the atmosphere to the target audience (professional, casual, luxury, etc.)

SPECIFIC EXAMPLES:
- E-commerce fashion: Modern retail store with mannequins, clothing racks, brand displays with neon logo
- Restaurant/food: Kitchen or dining area with food props, chef elements, warm lighting, restaurant name in neon
- Tech/SaaS: Modern office with laptops, monitors, tech equipment, brand signage with glowing logo
- Travel/hotel: Beach, hotel lobby, or destination-themed environment with brand elements
- Fitness/health: Gym, wellness center, or outdoor fitness environment with brand presence
- Music/entertainment: Recording studio, concert venue, or music-themed space with neon branding
- Finance/banking: Professional office with financial charts, modern banking environment, corporate identity
- Education: Classroom, library, or learning environment with educational materials and brand elements

PRESENTATION:
Present the screenshot as a SINGLE floating screen in an environment that perfectly matches the website/app theme and content. Use ${viewAngleDesc} with cinematic composition and professional lighting. Create depth with relevant props and branding elements that reflect the industry. Fill frame appropriately. Do not show multiple screens.`;
        default:
          return '';
      }
    } else {
      switch (selectedCategory) {
        case 'simple':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium minimalist mockup showing a SINGLE ${deviceName} displaying the attached image. Show only ONE device with its distinctive shape and bezels on a pristine white marble surface, shot with 50mm lens at f/2.8, morning studio light with edge diffusion. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, clean, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter. The single device should fill most of the 4:3 frame with professional product placement. Do not show multiple devices.`;
        case 'color':
          const getColorInfoWithDevice = (color) => {
            if (color && color.startsWith('#')) {
              return { hex: color, name: color };
            }
            const colorMap = {
              blue: { hex: '#3B82F6', name: 'blue' },
              red: { hex: '#EF4444', name: 'red' },
              green: { hex: '#10B981', name: 'green' },
              yellow: { hex: '#F59E0B', name: 'yellow' },
              purple: { hex: '#8B5CF6', name: 'purple' },
              pink: { hex: '#EC4899', name: 'pink' },
              orange: { hex: '#F97316', name: 'orange' },
              cyan: { hex: '#06B6D4', name: 'cyan' },
              teal: { hex: '#14B8A6', name: 'teal' },
              indigo: { hex: '#6366F1', name: 'indigo' },
              rose: { hex: '#F43F5E', name: 'rose' },
              emerald: { hex: '#059669', name: 'emerald' }
            };
            return colorMap[color] || colorMap.blue;
          };
          const colorInfoWithDevice = getColorInfoWithDevice(selectedColor);
          return `${screenPreservationInstruction}${conditionalInstructions}Create a clean, solid color background mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels levitating/floating in mid-air, shot with 50mm lens at f/2.8, professional studio lighting. The entire background should be a uniform solid ${colorInfoWithDevice.hex} color (${colorInfoWithDevice.name}) - no gradients, no fog, no atmospheric effects - just a clean, flat ${colorInfoWithDevice.name} background. The device should appear to be floating with realistic soft drop shadows cast beneath it. Use ${viewAngleDesc} composition with professional product photography lighting that makes the device stand out against the ${colorInfoWithDevice.name} background. Commercial catalog aesthetic with shallow depth of field and realistic texture fidelity. High-end, bold, aspirational mood. Sharp focus, perfect color balance. The levitating device should cast soft, realistic contact shadows beneath it. The single device should fill most of the 4:3 frame with professional product placement. Do not show multiple devices.`;
        case 'office':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium corporate startup mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels on a premium walnut desk, shot with 85mm lens at f/2.8, morning studio light with edge diffusion. Include startup elements: premium notebook, modern office accessories. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, bold, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter. The single device should fill most of the 4:3 frame with professional product placement. Do not show multiple devices.`;
        case 'fiori':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium Studio Horizon enterprise mockup showing a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels on a pristine white surface, shot with 50mm lens at f/2.8, morning studio light with edge diffusion. Use Studio's signature blue accent lighting and commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, professional, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter. The single device should fill most of the 4:3 frame with enterprise-grade aesthetics. Do not show multiple devices.`;
        case 'stones':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a dramatic mockup of a SINGLE sleek ${deviceName} displaying the attached image, positioned on stacked concrete blocks in a dimly-lit studio. Show only ONE physical device with its distinctive shape and premium finish. Use dramatic lighting with soft shadows highlighting the device contours. The scene should fill a 4:3 aspect ratio with the single device as the main subject using ${viewAngleDesc}. Do not show multiple devices.`;
        case 'gamer':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium gaming setup mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a high-tech gaming environment, shot with 50mm lens at f/2.8, dramatic studio lighting with edge diffusion. Include RGB neon lighting, gaming peripherals, mechanical keyboards, RGB mouse pads, gaming chairs, multiple monitors. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, bold, aspirational mood with vibrant cyan, purple, and pink RGB lighting. Sharp focus, perfect color balance, no distractions or clutter. The single device should fill most of the 4:3 frame with professional product placement. Do not show multiple devices.`;
        case 'view':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium urban lifestyle mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels positioned against a breathtaking night cityscape, shot with 85mm lens at f/2.8, dramatic studio lighting with edge diffusion. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, bold, aspirational mood with warm ambient lighting and city lights reflecting on the device. Sharp focus, perfect color balance, no distractions or clutter. The single device should fill most of the 4:3 frame with professional product placement. Do not show multiple devices.`;
        case 'ocean':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium coastal lifestyle mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels positioned against a beautiful ocean horizon, shot with 85mm lens at f/2.8, morning studio light with edge diffusion. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, clean, aspirational mood with warm golden tones and soft ocean reflections. Sharp focus, perfect color balance, no distractions or clutter. The single device should fill most of the 4:3 frame with professional product placement. Do not show multiple devices.`;
        case 'designer':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium creative studio mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a sophisticated design studio, shot with 50mm lens at f/2.8, morning studio light with edge diffusion. Include premium materials: designer furniture, mood boards, color swatches, design tools, creative inspiration elements. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, bold, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter. The single device should fill most of the 4:3 frame with professional product placement. Do not show multiple devices.`;
        case 'coffee':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium cafe lifestyle mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a trendy coffee shop environment, shot with 50mm lens at f/2.8, morning studio light with edge diffusion. Include wooden textures, latte art, modern furniture, warm lighting. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, clean, aspirational mood with warm inviting lighting and coffee shop ambiance. Sharp focus, perfect color balance, no distractions or clutter. The single device should fill most of the 4:3 frame with professional product placement. Do not show multiple devices.`;
        case 'business':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium corporate executive mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a sophisticated boardroom setting, shot with 85mm lens at f/2.8, morning studio light with edge diffusion. Include elegant boardroom furniture, executive desk setup, modern office architecture, business documents. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, professional, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter. The single device should fill most of the 4:3 frame with professional product placement. Do not show multiple devices.`;
        case 'artsy':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium music venue mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a recording studio or music venue environment, shot with 50mm lens at f/2.8, dynamic studio lighting with edge diffusion. Include musical instruments, sound equipment, recording studio elements, stage lighting, and musical atmosphere. Use commercial catalog aesthetic with shallow depth of field, ${viewAngleDesc}, and realistic texture fidelity. High-end, dynamic, musical mood with vibrant stage lighting and crisp accents. Sharp focus, perfect color balance, no distractions or clutter. The single device should fill most of the 4:3 frame with professional product placement in premium music environment. Do not show multiple devices.`;
        case 'abstract':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium abstract design mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a modern, creative environment with inventive lighting and artistic elements, shot with 50mm lens at f/2.8, morning studio light with edge diffusion. Include abstract design elements, geometric shapes, modern materials, and creative lighting. Use creative, expressive, and modern UI design themes with inventive lighting and professional, design-forward mood. Focus on imaginative and stylized interpretation rather than realism. Sharp focus, perfect color balance, modern aesthetic with soft studio key light and crisp accents. The single device should fill most of the 4:3 frame with professional product placement in a premium abstract environment. Do not show multiple devices.`;
        case 'sport':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium sports and fitness mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in an active, energetic environment with bold lighting and vibrant colors, shot with 50mm lens at f/2.8, dramatic studio lighting with edge diffusion. Include sports equipment, fitness gear, dynamic lighting, and energetic atmosphere. Use bold lighting and vibrant color balance to evoke an active, competitive atmosphere. Captures the excitement and sharpness found in sports-related contexts. Sharp focus, perfect color balance, energetic mood with soft studio key light and crisp accents. The single device should fill most of the 4:3 frame with professional product placement in a premium sports environment. Do not show multiple devices.`;
        case 'nature':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium natural environment mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a serene outdoor setting with natural lighting and organic textures, shot with 85mm lens at f/2.8, morning studio light with edge diffusion. Include natural elements like stone surfaces, wood textures, plants, or water features. Use aesthetics rooted in the outdoors, natural surfaces, or aquatic scenes with natural or organic lighting, textures from stone or water, and scenic or environmental moods. Promotes tranquility and connection to the natural world. Sharp focus, perfect color balance, natural mood with soft studio key light and crisp accents. The single device should fill most of the 4:3 frame with professional product placement in a premium natural environment. Do not show multiple devices.`;
        case 'luxury':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a luxury mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in an opulent environment with polished marble surfaces, brushed gold accents, and crystal chandeliers casting warm light, dramatic chiaroscuro lighting. Include luxury elements like vintage champagne glasses, designer watches, premium leather goods, and rich velvet textures. Use professional composition with shallow depth of field and dramatic shadows. Color grading with warm golden tones and rich contrast. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium luxury environment. Do not show multiple devices.`;
        case 'space':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a futuristic space station mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a sleek space station environment with metallic surfaces, holographic displays, and ambient blue lighting, cool futuristic ambiance. Include advanced technology elements like holographic displays, floating panels, energy conduits, control panels, and viewports showing starfields. Use professional composition with shallow depth of field and cool lighting. Color grading with cool blues, whites, and subtle cyan highlights. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium space station environment. Do not show multiple devices.`;
        case 'octopus':
          return `${screenPreservationInstruction}${conditionalInstructions}Create an underwater mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a mysterious deep-sea environment with oceanic blues and ethereal lighting filtering through water, dramatic underwater lighting. Include highly detailed, realistic octopus tentacles with natural texture, subtle color variations, and organic movement patterns. Show tentacles with authentic sucker details, natural curvature, and lifelike positioning. Include flowing seaweed, bioluminescent particles, and marine life silhouettes. Use dramatic underwater lighting with blue-green tones and ethereal glow effects. Color grading with deep blues and teal accents. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium underwater environment. Do not show multiple devices.`;
        case 'paraglide':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a high-altitude adventure mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a vast mountain landscape with dramatic cloud formations and natural lighting, capturing the scale and grandeur. Include paragliding equipment, harnesses, wind-blown fabric, mountain peaks, and endless sky. Use natural lighting with dramatic sky conditions and mountain shadows. Color grading with cool blues and natural earth tones. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium adventure environment. Do not show multiple devices.`;
        case 'rooftop':
          return `${screenPreservationInstruction}${conditionalInstructions}Create an urban rooftop mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels on an urban rooftop with city skyline backdrop and golden hour lighting, capturing the urban atmosphere. Include modern rooftop elements like potted plants, outdoor furniture, string lights, city traffic below, and architectural details. Use shallow depth of field with dramatic city lighting. Color grading with warm sunset tones and urban contrast. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium rooftop environment. Do not show multiple devices.`;
        case 'car':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a cinematic automotive showroom mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a luxury car showroom with dramatic lighting reminiscent of car commercials. Include sleek automobile elements: polished metallic surfaces, leather seats, chrome details, glossy paint reflections, showroom spotlights, reflective floors, and modern architecture. Use professional automotive photography lighting with dramatic rim lights and key lights at 45-degree angles, shot with 85mm lens at f/1.4 for shallow depth of field. Create a high-performance, aspirational atmosphere with cool blue and warm amber accent lighting. Color grading with deep blacks, metallic highlights, and cinematic contrast. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium automotive environment. Do not show multiple devices.`;
        case 'bar':
          return `${screenPreservationInstruction}${conditionalInstructions}Create an upscale cocktail bar mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a sophisticated bar environment with moody, atmospheric lighting like a premium spirits advertisement. Include bar elements: crystal glassware, premium liquor bottles with amber backlighting, polished mahogany surfaces, brass fixtures, leather bar stools, smoke wisps, ice cubes catching light, garnishes, and dramatic bottle backlighting. Use cinematic low-key lighting with warm tungsten practicals and subtle neon accents, shot with 50mm lens at f/1.8 for dreamy bokeh. Create an intimate, sophisticated atmosphere with warm golden hour tones mixed with cool blue shadows. Color grading with rich amber warmth, deep shadows, and elegant contrast. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium bar environment. Do not show multiple devices.`;
        case 'restaurant':
          return `${screenPreservationInstruction}${conditionalInstructions}Create an elegant fine dining restaurant mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a refined restaurant environment with warm, inviting cinematography like a premium culinary magazine. Include restaurant elements: pristine white tablecloths, polished silverware, wine glasses catching candlelight, fresh plated cuisine with artistic presentation, elegant table settings, soft ambient lighting from overhead pendants, fresh ingredients, artisanal bread baskets, sommelier wine selections, open kitchen glimpses with chef stations, and soft bokeh from ambient candles. Use warm, diffused lighting with golden hour tones and soft shadows, shot with 50mm lens at f/1.8 for shallow depth of field. Create a refined, sophisticated, appetizing atmosphere with warm rustic tones. Color grading with warm amber lighting, soft highlights, rich wood tones, and that editorial food photography aesthetic. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium restaurant environment. Do not show multiple devices.`;
        case 'skatepark':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a dynamic urban skatepark mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in an energetic skatepark scene with action-sports cinematography. Include skate culture elements: concrete ramps, graffiti art, worn grip tape, skateboard decks, urban textures, street art, motion blur, dust particles in sunbeams, and urban decay. Use dramatic natural lighting with strong shadows and golden hour sun rays, shot with 24mm wide-angle lens at f/2.8 to capture the environment. Create a rebellious, youthful, high-energy atmosphere with saturated colors and gritty urban aesthetics. Color grading with punchy contrast, vibrant graffiti colors, and film-like grain for authentic street photography feel. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium skatepark environment. Do not show multiple devices.`;
        case 'duck':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a playful, quirky mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels alongside a large bright yellow rubber duck positioned prominently next to it. The rubber duck should be oversized and whimsical, creating a fun contrast with the technology. Use bright, cheerful lighting with soft shadows, shot with 50mm lens at f/2.8 for professional product photography. Include playful elements: clean modern desk surface, subtle bathroom/spa aesthetic touches, water droplets, and bright clean backgrounds. Create a lighthearted, creative, unexpected atmosphere with vibrant yellow as the hero color. Color grading with bright, saturated yellows, clean whites, and playful contrast. The duck should be a key focal point that adds personality and humor to the composition. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with the duck as a whimsical companion. Do not show multiple devices.`;
        case 'rave':
          return `${screenPreservationInstruction}${conditionalInstructions}Create an electrifying electronic music festival mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in an intense rave scene with dynamic lighting like a concert film. Include rave elements: laser beams cutting through atmospheric haze, LED panels, strobe effects, DJ equipment, crowds in motion, dramatic fog machines, light trails, bokeh from stage lights, and silhouettes in colored fog. Use dramatic multi-colored lighting with vibrant neon RGB effects—electric blues, hot pinks, laser greens, and UV purples, shot with 35mm lens at f/1.4 to capture the energy. Create an exhilarating, high-energy, futuristic atmosphere with pulsing colors and movement. Color grading with saturated neon colors, deep contrast, and cinematic bloom effects for that festival night aesthetic. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium rave environment. Do not show multiple devices.`;
        case 'garage':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a gritty automotive workshop mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in an industrial garage scene with documentary-style cinematography. Include garage elements: tool chests with worn tools, oil stains on concrete floors, engine parts, work lights hanging from ceiling, vintage automotive posters, industrial shelving, mechanic's tools, spare parts, and vintage motorcycles or cars in background. Use dramatic directional lighting from work lamps and garage door sunlight streaming in, shot with 35mm lens at f/2.0 for depth. Create a hardworking, authentic, masculine atmosphere with industrial aesthetics. Color grading with desaturated industrial tones, warm work-light orange, cool shadows, and gritty texture overlays for that workshop documentary feel. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium garage environment. Do not show multiple devices.`;
        case 'paris':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a romantic Parisian mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a cinematic Paris scene with European aesthetic like a luxury travel film. Include Paris elements: wrought iron Juliet balconies, classic bistro furniture, fresh croissants and coffee, Eiffel Tower visible in dreamy background bokeh, cobblestone textures, vintage Parisian architecture, morning mist, café culture, fresh flowers, vintage books, and classic French design. Use soft, diffused natural lighting reminiscent of French cinema's golden hour, shot with 50mm lens at f/1.4 for creamy bokeh. Create a romantic, elegant, timeless atmosphere with European sophistication. Color grading with warm sepia undertones, soft contrast, muted pastels, and that classic French film aesthetic. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium Parisian environment. Do not show multiple devices.`;
        case 'nyc':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a bold New York City mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a gritty urban NYC scene with blockbuster film cinematography. Include NYC elements: yellow taxi cabs, steam rising from manhole covers, towering skyscrapers, Times Square neon signs, fire escapes, urban architecture, coffee cups, subway maps, city newspapers, and constant motion. Use dramatic lighting with strong contrast between artificial city lights and natural light, shot with 35mm lens at f/2.0 to capture the urban energy. Create an energetic, fast-paced, ambitious atmosphere with urban grit and metropolitan sophistication. Color grading with high contrast, desaturated with pops of neon, deep blacks, and that New York street photography look with film grain. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium NYC environment. Do not show multiple devices.`;
        case 'tokyo':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a futuristic Tokyo mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a cyberpunk-meets-modern-Japan scene like a sci-fi film. Include Tokyo elements: vibrant neon kanji signs, vending machines glowing in the night, sakura blossoms, traditional paper lanterns mixed with modern tech, reflective rain-slicked streets, bullet trains in motion blur, anime-style aesthetics, technology contrasting with tradition, and vertical neon text. Use dramatic neon lighting with pink, blue, and purple accents mixed with traditional warm lantern light, shot with 35mm lens at f/1.4 for neon bokeh. Create a high-tech, mysterious, culturally rich atmosphere blending ancient tradition with cutting-edge future. Color grading with saturated neon colors (hot pink, electric blue, neon purple), cinematic teal shadows, and that Tokyo-night cyberpunk aesthetic. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium Tokyo environment. Do not show multiple devices.`;
        case 'store':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a minimalist Apple Store mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a clean, modern retail environment with iconic tech product launch cinematography. Include Apple Store elements: seamless white walls, blonde wooden tables displaying products in perfect alignment, glowing Apple logos, large glass windows with natural light flooding in, geometric simplicity, precisely arranged product displays, minimalist signage with Helvetica typography, clean architectural lines, floating glass staircases, and the distinctive warm glow of backlit displays. Use bright, even lighting with soft natural illumination and subtle spotlights on products, shot with 50mm lens at f/2.8 for crisp clarity. Create an innovative, aspirational, premium atmosphere with perfect symmetry and breathing room. Color grading with clean whites, warm wood tones, subtle cool highlights, and that signature Apple retail aesthetic with immaculate precision. Fill entire canvas - no black bars or letterboxing. The single device should fill most of the 4:3 frame with professional product placement in a premium Apple Store environment. Do not show multiple devices.`;
        case 'custom':
          return `${screenPreservationInstruction}${conditionalInstructions}Create a premium custom mockup featuring a SINGLE ${deviceName} displaying the attached image. Show only ONE physical device with its distinctive shape and bezels in a custom environment based on the user's specific requirements, shot with 85mm lens at f/2.8, morning studio light with edge diffusion. ${customPrompt || 'Use commercial catalog aesthetic with shallow depth of field and realistic texture fidelity. High-end, clean, aspirational mood with soft studio key light and crisp accents. Sharp focus, perfect color balance, no distractions or clutter.'} The single device should fill most of the 4:3 frame with professional product placement in a premium custom environment. Do not show multiple devices.`;
        case 'auto':
          return `${screenPreservationInstruction}${conditionalInstructions}INTELLIGENT CONTENT ANALYSIS - Create a completely custom mockup environment by analyzing the screenshot content:

CONTENT ANALYSIS REQUIREMENTS:
- Identify the website/app type, industry, and brand identity from the screenshot
- Extract key visual elements: logos, colors, typography, and design style
- Determine the target audience and use case from the content
- Recognize any specific themes, products, or services being promoted

CONTEXTUAL ENVIRONMENT CREATION:
- Create an environment that perfectly matches the identified industry/theme
- Incorporate the brand's visual identity: use their color palette, logo elements, and design aesthetic
- Add relevant props and environmental elements that relate to the business/industry
- Include subtle branding elements: neon signs, wall displays, or ambient lighting featuring the brand name/logo
- Match the atmosphere to the target audience (professional, casual, luxury, etc.)

SPECIFIC EXAMPLES:
- E-commerce fashion: Modern retail store with mannequins, clothing racks, brand displays with neon logo
- Restaurant/food: Kitchen or dining area with food props, chef elements, warm lighting, restaurant name in neon
- Tech/SaaS: Modern office with laptops, monitors, tech equipment, brand signage with glowing logo
- Travel/hotel: Beach, hotel lobby, or destination-themed environment with brand elements
- Fitness/health: Gym, wellness center, or outdoor fitness environment with brand presence
- Music/entertainment: Recording studio, concert venue, or music-themed space with neon branding
- Finance/banking: Professional office with financial charts, modern banking environment, corporate identity
- Education: Classroom, library, or learning environment with educational materials and brand elements

PRESENTATION:
Create a SINGLE physical ${deviceName} displaying the attached image on a clean surface. Use ${viewAngleDesc} with professional studio lighting. Extract colors from screenshot to create a harmonious but SUBTLE environment. The device is the hero - background should be minimal. Fill the 4:3 frame appropriately. Do not show multiple devices.`;
        default:
          return '';
      }
    }
  }, [customPrompt, selectedColor]);

  // Server-side enhance API call function
  const callServerEnhanceAPI = useCallback(async (files, options = {}) => {
    const { 
      extraPrompt = '', 
      deviceTypes = [], 
      deviceNames = [], 
      showDevice = true, 
      showBokeh = true, 
      showHands = true, 
      showNotch = false, 
      aspectRatio = '4:3' 
    } = options;

    console.log('🚀 Calling server enhance API with options:', options);

    try {
      const formData = new FormData();
      
      // Handle single file or multiple files
      if (Array.isArray(files)) {
        files.forEach((file, index) => {
          formData.append(`image${index}`, file);
        });
        formData.append('multiple', 'true');
      } else {
        formData.append('image', files);
        formData.append('multiple', 'false');
      }

      // Add all the options
      formData.append('extraPrompt', extraPrompt);
      formData.append('aspectRatio', aspectRatio);
      formData.append('deviceTypes', JSON.stringify(deviceTypes));
      formData.append('deviceNames', JSON.stringify(deviceNames));
      formData.append('showDevice', showDevice.toString());
      formData.append('showBokeh', showBokeh.toString());
      formData.append('showHands', showHands.toString());
      formData.append('showNotch', showNotch.toString());
      formData.append('model', selectedModel);

      // Select endpoint based on pipeline selection
      const endpoint = pipeline === PIPELINES.V3 ? API_ENDPOINTS.ENHANCE_V3 : API_ENDPOINTS.ENHANCE;
      const pipelineName = pipeline === PIPELINES.V3 ? 'Phase 3: Mask-Based Inpainting' : 'Phase 1: Direct Generation';
      
      console.log(`📤 Sending request to ${endpoint} (${pipelineName})...`);
      console.log('📤 Request details:', {
        url: endpoint,
        pipeline: pipeline,
        method: 'POST',
        formDataKeys: Array.from(formData.keys()),
        extraPrompt: extraPrompt.substring(0, 100) + '...',
        aspectRatio,
        deviceTypes,
        deviceNames
      });
      
      // Add AbortController for timeout handling (35 seconds to account for Netlify's 26s limit + processing)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 35000);
      
      let response;
      try {
        response = await fetch(endpoint, {
          method: 'POST',
          body: formData,
          signal: controller.signal
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          console.error('⏰ Request timed out after 35 seconds');
          throw new Error('Request timed out. The generation process is taking longer than expected. This may happen with complex images. Please try again with a simpler image or wait a moment and retry.');
        }
        throw fetchError;
      }

      console.log('📥 Response received:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Server enhance API error details:', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText
        });
        
        // Detect Netlify function timeout (504 Gateway Timeout)
        if (response.status === 504 || response.status === 503) {
          throw new Error('Request timed out. The server function exceeded its time limit. This can happen with complex images or high server load. Please try again in a moment.');
        }
        
        throw new Error(`Server enhance API error: ${response.status} - ${errorText}`);
      }

      const blob = await response.blob();
      console.log('✅ Server enhance API response received:', blob.size, 'bytes');
      
      return blob;
    } catch (error) {
      console.error('❌ Server enhance API error:', error);
      throw error;
    }
  }, [pipeline, selectedModel]);

  const onGenerate = useCallback(async () => {
    if (!file && files.length === 0) return;
    
    // Check if user has enough mockups for the selected model
    const requiredCredits = getModelCredits(selectedModel);
    if (!user || mockupCount < requiredCredits) {
      showMockupExhausted(() => {
        trackEvent('view_pricing', {
          source: 'exhausted_credits',
          required_credits: requiredCredits
        });
        setShowPricing(true);
      });
      return;
    }
    
    // Prevent multiple simultaneous generations
    if (processing) {
      console.warn('⚠️ Generation already in progress, ignoring duplicate request');
      return;
    }
    
    setProcessing(true);
    console.log('🚀 Starting generation process...');
    
    // Create a placeholder mockup in the feed at the exact position where the real one will appear
    // This prevents the "jump" effect - the real mockup will replace the placeholder in place
    const placeholderId = `processing-placeholder-${Date.now()}`;
    const placeholderStableKey = `placeholder-${Date.now()}-${category}-${deviceType}`; // Stable key for placeholder
    const placeholderMockup = {
      id: placeholderId,
      stableKey: placeholderStableKey, // Stable key that will be preserved when replaced
      url: null, // Will be replaced with real image
      imageUrl: null,
      imageData: null,
      category: category,
      deviceType: deviceType,
      showDevice: showDevice,
      showBokeh: showBokeh,
      showHands: showHands,
      showNotch: showNotch,
      viewAngle: viewAngle,
      aspectRatio: aspectRatio,
      fileCount: file ? 1 : files.length,
      pipeline: pipeline, // Store pipeline type used for generation
      timestamp: new Date(),
      createdAt: new Date(),
      isPlaceholder: true // Flag to identify placeholder
    };
    
    // Add placeholder to feed immediately at the top
    setGeneratedMockups(prev => [placeholderMockup, ...prev]);
    console.log('✅ Added placeholder mockup to feed:', placeholderId);
    
    // Helper function to clean up placeholder
    const cleanupPlaceholder = () => {
      setGeneratedMockups(prev => prev.filter(m => m.id !== placeholderId));
    };
    
    // Add timeout to prevent infinite loading
    // Use a ref to track if generation completed successfully
    let generationCompleted = false;
    const timeoutId = setTimeout(() => {
      if (!generationCompleted) {
        console.error('⏰ Generation timeout after 120 seconds');
        setProcessing(false);
        // Remove placeholder on timeout
        cleanupPlaceholder();
        alert('Generation timed out. Please try again.');
      }
    }, 120000); // 2 minute timeout
    
    try {
      const fileCount = file ? 1 : files.length;
      console.log('🚀 onGenerate - Starting generation process');
      console.log('🚀 onGenerate - fileCount:', fileCount, 'file:', !!file, 'files.length:', files.length);
      console.log('🚀 onGenerate - files:', files.map(f => f.name));
      console.log('🚀 onGenerate - Environment:', {
        origin: window.location.origin,
        user: !!user,
        db: !!db,
        storage: !!storage
      });
      
      const extraPrompt = buildExtraPrompt(category, deviceType, showDevice, viewAngle, fileCount, showBokeh, showHands, showNotch, aspectRatio, fullPrompt);
      
      let enhanced;
      if (file) {
        // Single file processing with special mobile handling
        console.log('Processing single file:', file.name);
        
        // Check if this is a mobile screenshot that needs special handling
        if (deviceType === 'mobile') {
          console.log('Detected mobile screenshot, checking for portrait orientation...');
          const mobileInfo = await detectMobileScreenshot(file);
          console.log('Mobile screenshot info:', mobileInfo);
          
          // Always use specialized mobile prompt for mobile devices
          console.log('Using specialized mobile prompt...');
          const mobilePrompt = buildMobileScreenshotPrompt(category, viewAngle, mobileInfo.aspectRatio < 1, showDevice, showBokeh, showHands, showNotch);
          enhanced = await callServerEnhanceAPI(file, { extraPrompt: mobilePrompt, aspectRatio });
          console.log('Mobile screenshot processing completed');
        } else {
          console.log('Using standard prompt for non-mobile device...');
          enhanced = await callServerEnhanceAPI(file, { extraPrompt, aspectRatio });
          console.log('Standard processing completed');
        }
      } else {
        // Multiple files processing with device recognition
        console.log('Processing multiple files:', files.map(f => f.name));
        console.log('Device types for multiple files:', deviceTypes);
        console.log('Device names for multiple files:', deviceNames);
        
        // For multiple files, send individual files to server with device info
        // Don't stitch them - let server handle the composition with proper device recognition
        const specificDeviceNames = deviceTypes.map(type => getSpecificDeviceName(type));
        console.log('Calling server enhance API for multiple files...');
        enhanced = await callServerEnhanceAPI(files, { 
          extraPrompt,
          deviceTypes,
          deviceNames: specificDeviceNames,
          showDevice,
          showBokeh,
          showHands,
          showNotch,
          aspectRatio,
        });
        console.log('Server enhance API for multiple files completed');
      }
      
      // Convert blob to base64 for persistent storage
      const base64Image = await blobToBase64(enhanced);
      
      // Create persistent URL instead of blob URL for authenticated users
      let newMockupUrl;
      if (user && db) {
        // For authenticated users, use base64 data URL for immediate preview
        // Firebase Storage URL will be used once uploaded
        // blobToBase64 already returns a full data URL, so use it directly
        newMockupUrl = base64Image;
        console.log('✅ Created base64 data URL for authenticated user');
        console.log('🔍 User authenticated, will upload to Firebase Storage if needed');
        console.log('🔍 Preventing blob URL creation for authenticated users');
      } else {
        // For unauthenticated users, use blob URL (temporary)
        try {
          newMockupUrl = URL.createObjectURL(enhanced);
          console.log('✅ Created blob URL for unauthenticated user:', newMockupUrl);
          console.log('🔍 Current origin:', window.location.origin);
          
          // In development, blob URLs are always valid
          if (process.env.NODE_ENV !== 'production') {
            console.log('🔧 Development mode: Blob URL is valid for localhost');
          } else {
            // In production, validate that the blob URL has the correct origin
            if (newMockupUrl.includes('localhost') && window.location.origin !== 'http://localhost:3000') {
              console.error('❌ Blob URL has localhost origin but we are not on localhost!', {
                blobUrl: newMockupUrl,
                currentOrigin: window.location.origin
              });
            } else {
              console.log('✅ Blob URL created with correct origin:', newMockupUrl);
            }
          }
          
          // Test if the blob URL is accessible
          const testImg = new Image();
          testImg.onload = () => {
            console.log('✅ Blob URL is accessible');
          };
          testImg.onerror = (error) => {
            console.warn('⚠️ Blob URL may not be accessible:', error);
          };
          testImg.src = newMockupUrl;
        } catch (error) {
          console.error('❌ Failed to create blob URL:', error);
          throw new Error('Failed to create image URL: ' + error.message);
        }
      }
      
      // Validate the URL before creating the mockup
      if (!isValidImageUrl(newMockupUrl)) {
        console.error('❌ Generated invalid URL, skipping mockup creation:', newMockupUrl);
        throw new Error('Generated invalid image URL');
      }
      
      // Replace the placeholder mockup with the real one in the same position
      // This ensures smooth transition without jumping
      const tempId = `temp-${Date.now()}`; // Temporary ID until Firestore save completes
      const stableKey = `mockup-${Date.now()}-${category}-${deviceType}`; // Stable key that never changes for React
      
      // Add new mockup to the feed (newest first)
      const newMockup = {
        id: tempId, // Use temporary ID initially, will be replaced with Firestore ID after save
        stableKey: stableKey, // Stable key for React - never changes to prevent remount
        url: newMockupUrl,
        imageUrl: newMockupUrl,
        imageData: base64Image, // Store base64 for immediate display, will be updated if Storage upload succeeds
        category: category,
        deviceType: deviceType,
        showDevice: showDevice,
        showBokeh: showBokeh,
        showHands: showHands,
        showNotch: showNotch,
        viewAngle: viewAngle,
        aspectRatio: aspectRatio,
        fileCount: fileCount,
        pipeline: pipeline, // Store pipeline type used for generation
        model: selectedModel, // Store model ID used for generation
        modelName: getModelDisplayName(selectedModel), // Store model display name for feed
        timestamp: new Date(),
        createdAt: new Date()
      };
      
      console.log('🎯 Created new mockup object:', {
        id: newMockup.id,
        url: newMockup.url,
        category: newMockup.category,
        deviceType: newMockup.deviceType,
        user: !!user,
        db: !!db
      });
      
      // Validate mockup URL for authenticated users
      if (user && newMockupUrl && newMockupUrl.startsWith('blob:')) {
        console.error('❌ Authenticated user mockup has blob URL! This should not happen:', newMockupUrl);
      }
      
      // Replace placeholder with real mockup in the same position
      console.log('✅ Replacing placeholder mockup with real mockup in place');
      setGeneratedMockups(prev => {
        // Find and replace the placeholder by stableKey to maintain React component identity
        const index = prev.findIndex(m => m.id === placeholderId || m.stableKey === placeholderStableKey);
        if (index !== -1) {
          // Replace placeholder at the same position, preserving stableKey for smooth transition
          const updated = [...prev];
          updated[index] = {
            ...newMockup,
            stableKey: prev[index].stableKey || newMockup.stableKey // Preserve placeholder's stableKey
          };
          return updated;
        } else {
          // Placeholder not found (shouldn't happen), add to beginning
          console.warn('⚠️ Placeholder not found, adding to beginning');
          return [newMockup, ...prev];
        }
      });
      
      // Reset processing state immediately after mockup is visible in feed
      // This ensures button stops showing "Generating" as soon as image appears
      setProcessing(false);
      clearTimeout(timeoutId);
      generationCompleted = true;
      
      // Deduct credits immediately after successful generation (before saving to Firestore)
      // This ensures credits are deducted even if Firestore save fails
      if (user) {
        const creditCost = getModelCredits(selectedModel);
        console.log(`💳 Deducting ${creditCost} credit(s) for ${getModelDisplayName(selectedModel)} model...`);
        const success = await deductMockup(creditCost);
        if (success) {
          console.log('✅ Credits deducted successfully');
          // Only show notification for multiple images, not single images
          if (fileCount > 1) {
            showMockupGenerated(mockupCount - creditCost);
          }
        } else {
          console.error('❌ Failed to deduct credits');
        }
      }
      
      // Save to Firestore if user is authenticated and Firebase is available
      if (user && db) {
        try {
          // CRITICAL FIX: Save to Firestore FIRST with base64 (if small) to ensure persistence
          // Then upload to Storage in background and update the document
          // This ensures the document exists immediately, so it persists even if user refreshes
          
          // Check if base64 is too large for Firestore
          const base64SizeBytes = base64Image ? base64Image.length : 0;
          const maxBase64Size = 900 * 1024; // 900KB - Firestore field limit
          
          let imageUrl;
          let firestoreImageData;
          let docRef;
          
          // For small images: Save to Firestore IMMEDIATELY with base64, then upload to Storage in background
          // For large images: Upload to Storage first, then save to Firestore
          if (base64SizeBytes <= maxBase64Size && storage) {
            // Small image - save to Firestore immediately with base64
            // CRITICAL: Always use base64, never blob URLs for authenticated users
            imageUrl = base64Image;
            firestoreImageData = base64Image;
            
            // Safety check: Ensure we never save blob URLs
            if (imageUrl && imageUrl.startsWith('blob:')) {
              console.error('❌ CRITICAL: Attempted to save blob URL to Firestore! Using base64 instead.');
              imageUrl = base64Image;
              firestoreImageData = base64Image;
            }
            
            // Validate that imageUrl is base64 or HTTP(S)
            if (!imageUrl || (!imageUrl.startsWith('data:') && !imageUrl.startsWith('http'))) {
              console.error('❌ Invalid imageUrl format before Firestore save:', imageUrl?.substring(0, 50));
              throw new Error('Invalid image URL format - cannot save to Firestore');
            }
            
            console.log('💾 Saving mockup to Firestore IMMEDIATELY (with base64) to ensure persistence:', {
              userId: user.uid,
              imageUrl: imageUrl.substring(0, 50) + '...',
              imageUrlType: imageUrl.startsWith('data:') ? 'base64' : imageUrl.startsWith('http') ? 'storage' : 'unknown',
              category: category,
              deviceType: deviceType,
              fileCount: fileCount
            });
            
            // Retry logic for Firestore save - save IMMEDIATELY with base64
            let retries = 3;
            let lastError;
            
            while (retries > 0) {
              try {
                // FINAL VALIDATION: Never save blob URLs to Firestore
                if (imageUrl && imageUrl.startsWith('blob:')) {
                  console.error('❌ CRITICAL: Blob URL detected right before Firestore save! Replacing with base64.');
                  imageUrl = base64Image;
                  firestoreImageData = base64Image;
                }
                
                // Ensure we have a valid URL
                if (!imageUrl || (!imageUrl.startsWith('data:') && !imageUrl.startsWith('http'))) {
                  console.error('❌ Invalid imageUrl right before Firestore save:', imageUrl?.substring(0, 50));
                  throw new Error('Invalid image URL - cannot save to Firestore');
                }
                
                docRef = await addDoc(collection(db, 'mockups'), {
                  userId: user.uid,
                  imageUrl: imageUrl, // Base64 data URL initially, will be updated with Storage URL
                  imageData: firestoreImageData, // Base64 for small images
                  category: category,
                  deviceType: deviceType,
                  showDevice: showDevice,
                  showBokeh: showBokeh,
                  showHands: showHands,
                  showNotch: showNotch,
                  viewAngle: viewAngle,
                  aspectRatio: aspectRatio,
                  fileCount: fileCount,
                  // Native resolution from Gemini 3 Pro (2K)
                  nativeResolution: "2K",
                  // Enhanced image fields
                  enhancedImageUrl: null,
                  enhancedImageData: null,
                  enhancedResolution: null,
                  isEnhanced: false,
                  enhancedAt: null,
                  createdAt: serverTimestamp() // Use server timestamp for reliability
                });
                console.log('✅ Mockup document created IMMEDIATELY with ID:', docRef.id, 'imageUrl type:', imageUrl.startsWith('data:') ? 'base64' : 'storage', 'nativeResolution: 2K');
                break; // Success, exit retry loop
              } catch (saveError) {
                lastError = saveError;
                retries--;
                const attemptsRemaining = retries;
                console.warn(`⚠️ Firestore save attempt failed, ${attemptsRemaining} retries remaining:`, saveError);
                if (retries > 0) {
                  // Exponential backoff: wait 500ms, 1000ms, 2000ms
                  const delay = (4 - attemptsRemaining) * 500;
                  await new Promise(resolve => setTimeout(resolve, delay));
                }
              }
            }
            
            if (!docRef) {
              throw lastError || new Error('Failed to save mockup to Firestore after retries');
            }
            
            // Now upload to Storage in the background and update the document
            // This ensures the document exists immediately, even if Storage upload takes time
            // For small images, try to upload to Storage in background (non-blocking)
            // If it succeeds, update the document with Storage URL
            // If it fails, base64 is fine
            (async () => {
              try {
                console.log('📦 Uploading to Firebase Storage in background...');
                const storageUrl = await uploadImageToStorage(enhanced, user.uid);
                
                // Safety check: Ensure Storage URL is valid
                if (!storageUrl || !storageUrl.startsWith('http')) {
                  console.error('❌ Invalid Storage URL from upload:', storageUrl?.substring(0, 50));
                  throw new Error('Invalid Storage URL received');
                }
                
                console.log('✅ Storage upload successful, updating Firestore document...');
                
                // Update the document with Storage URL
                await updateDoc(doc(db, 'mockups', docRef.id), {
                  imageUrl: storageUrl,
                  // Keep base64 for small images for CORS-free download/share
                  imageData: base64Image
                });
                console.log('✅ Document updated with Storage URL:', storageUrl.substring(0, 50));
                
                // Update local state with Storage URL - but only if image is large
                // For small images, keep base64 to avoid reload flash
                setGeneratedMockups(prev => {
                  return prev.map(m => {
                    if (m.id === docRef.id) {
                      // Only update URL if it's actually better (Storage URL for large images)
                      // For small images, keep base64 to prevent reload
                      if (base64SizeBytes > maxBase64Size) {
                        return { ...m, url: storageUrl, imageUrl: storageUrl };
                      } else {
                        // Keep current URL but update imageData reference
                        return { ...m, imageData: base64Image };
                      }
                    }
                    return m;
                  });
                });
              } catch (storageError) {
                console.warn('⚠️ Background Storage upload failed, using base64:', storageError.message);
                // Base64 is already saved, so this is fine
              }
            })();
            
            // Use base64 for now, will be updated when Storage upload completes
            imageUrl = base64Image;
          } else if (storage && base64SizeBytes > maxBase64Size) {
            // For large images, we MUST upload to Storage first (base64 won't fit in Firestore)
            console.log('📦 Large image detected, uploading to Storage first (required)...');
            let storageUploadSuccess = false;
            let storageRetries = 5;
            
            while (storageRetries > 0 && !storageUploadSuccess) {
              try {
                console.log(`📦 Uploading to Firebase Storage (${6 - storageRetries}/5 attempts)...`);
                imageUrl = await uploadImageToStorage(enhanced, user.uid);
                firestoreImageData = null; // Don't store large base64 in Firestore
                
                storageUploadSuccess = true;
                console.log('✅ Firebase Storage upload successful');
                break;
              } catch (storageError) {
                storageRetries--;
                console.warn(`⚠️ Storage upload failed (${storageRetries} retries left):`, storageError.message);
                
                if (storageRetries === 0) {
                  // All retries failed - this is a problem for large images
                  throw new Error(`Storage upload failed and image too large for base64 (${Math.round(base64SizeBytes / 1024)}KB > ${maxBase64Size / 1024}KB). Please try again.`);
                } else {
                  // Wait before retry (exponential backoff: 500ms, 1s, 2s, 4s)
                  const delay = Math.min(500 * Math.pow(2, 5 - storageRetries), 4000);
                  await new Promise(resolve => setTimeout(resolve, delay));
                }
              }
            }
            
            // Validate imageUrl before saving
            if (!imageUrl) {
              console.error('❌ No imageUrl available after Storage upload');
              throw new Error('No image URL available - cannot save mockup');
            }
            
            // Safety check: Ensure we never save blob URLs
            if (imageUrl && imageUrl.startsWith('blob:')) {
              console.error('❌ CRITICAL: Attempted to save blob URL to Firestore for large image!');
              throw new Error('Blob URL detected - Storage upload must have failed');
            }
            
            // Ensure imageUrl is valid (http/https for Storage URLs)
            if (!imageUrl.startsWith('http')) {
              console.error('❌ Invalid imageUrl format:', imageUrl?.substring(0, 50));
              throw new Error('Invalid image URL format - cannot save mockup');
            }
            
            console.log('💾 Saving large mockup to Firestore with Storage URL:', {
              userId: user.uid,
              imageUrl: imageUrl.substring(0, 50) + '...',
              category: category,
              deviceType: deviceType,
              fileCount: fileCount
            });
            
            // Now save to Firestore with Storage URL
            let retries = 3;
            let lastError;
            
            while (retries > 0) {
              try {
                // FINAL VALIDATION: Never save blob URLs to Firestore
                if (imageUrl && imageUrl.startsWith('blob:')) {
                  console.error('❌ CRITICAL: Blob URL detected right before Firestore save for large image!');
                  throw new Error('Blob URL detected - Storage upload must have failed');
                }
                
                // Ensure we have a valid Storage URL
                if (!imageUrl || !imageUrl.startsWith('http')) {
                  console.error('❌ Invalid Storage URL right before Firestore save:', imageUrl?.substring(0, 50));
                  throw new Error('Invalid Storage URL - cannot save to Firestore');
                }
                
                docRef = await addDoc(collection(db, 'mockups'), {
                  userId: user.uid,
                  imageUrl: imageUrl, // Firebase Storage URL
                  imageData: null, // Don't store large base64 in Firestore
                  category: category,
                  deviceType: deviceType,
                  showDevice: showDevice,
                  showBokeh: showBokeh,
                  showHands: showHands,
                  showNotch: showNotch,
                  viewAngle: viewAngle,
                  aspectRatio: aspectRatio,
                  fileCount: fileCount,
                  // Native resolution from Gemini 3 Pro (2K)
                  nativeResolution: "2K",
                  // Enhanced image fields
                  enhancedImageUrl: null,
                  enhancedImageData: null,
                  enhancedResolution: null,
                  isEnhanced: false,
                  enhancedAt: null,
                  createdAt: serverTimestamp() // Use server timestamp for reliability
                });
                console.log('✅ Large mockup document created with ID:', docRef.id, 'Storage URL:', imageUrl.substring(0, 50), 'nativeResolution: 2K');
                break; // Success, exit retry loop
              } catch (saveError) {
                lastError = saveError;
                retries--;
                const attemptsRemaining = retries;
                console.warn(`⚠️ Firestore save attempt failed, ${attemptsRemaining} retries remaining:`, saveError);
                if (retries > 0) {
                  // Exponential backoff: wait 500ms, 1000ms, 2000ms
                  const delay = (4 - attemptsRemaining) * 500;
                  await new Promise(resolve => setTimeout(resolve, delay));
                }
              }
            }
            
            if (!docRef) {
              throw lastError || new Error('Failed to save mockup to Firestore after retries');
            }
          } else if (!storage) {
            // Storage not available - save to Firestore with base64 if small enough
            console.warn('⚠️ Firebase Storage not available, using base64');
            if (base64SizeBytes > maxBase64Size) {
              throw new Error('Firebase Storage not available and image too large for base64');
            }
            
            // Save to Firestore with base64
            imageUrl = base64Image;
            firestoreImageData = base64Image;
            
            // Safety check: Ensure we never save blob URLs
            if (imageUrl && imageUrl.startsWith('blob:')) {
              console.error('❌ CRITICAL: Attempted to save blob URL to Firestore! Using base64 instead.');
              imageUrl = base64Image;
              firestoreImageData = base64Image;
            }
            
            // Validate that imageUrl is base64
            if (!imageUrl || !imageUrl.startsWith('data:')) {
              console.error('❌ Invalid imageUrl format before Firestore save (Storage unavailable):', imageUrl?.substring(0, 50));
              throw new Error('Invalid image URL format - cannot save to Firestore');
            }
            
            console.log('💾 Saving mockup to Firestore with base64 (Storage unavailable):', {
              userId: user.uid,
              imageUrl: imageUrl.substring(0, 50) + '...',
              imageUrlType: 'base64',
              category: category,
              deviceType: deviceType,
              fileCount: fileCount
            });
            
            // Retry logic for Firestore save
            let retries = 3;
            let lastError;
            
            while (retries > 0) {
              try {
                // FINAL VALIDATION: Never save blob URLs to Firestore
                if (imageUrl && imageUrl.startsWith('blob:')) {
                  console.error('❌ CRITICAL: Blob URL detected right before Firestore save (Storage unavailable)! Replacing with base64.');
                  imageUrl = base64Image;
                  firestoreImageData = base64Image;
                }
                
                // Ensure we have a valid base64 URL
                if (!imageUrl || !imageUrl.startsWith('data:')) {
                  console.error('❌ Invalid base64 URL right before Firestore save:', imageUrl?.substring(0, 50));
                  throw new Error('Invalid base64 URL - cannot save to Firestore');
                }
                
                docRef = await addDoc(collection(db, 'mockups'), {
                  userId: user.uid,
                  imageUrl: imageUrl, // Base64 data URL
                  imageData: firestoreImageData, // Base64
                  category: category,
                  deviceType: deviceType,
                  showDevice: showDevice,
                  showBokeh: showBokeh,
                  showHands: showHands,
                  showNotch: showNotch,
                  viewAngle: viewAngle,
                  aspectRatio: aspectRatio,
                  fileCount: fileCount,
                  // Native resolution from Gemini 3 Pro (2K)
                  nativeResolution: "2K",
                  // Enhanced image fields
                  enhancedImageUrl: null,
                  enhancedImageData: null,
                  enhancedResolution: null,
                  isEnhanced: false,
                  enhancedAt: null,
                  createdAt: serverTimestamp() // Use server timestamp for reliability
                });
                console.log('✅ Mockup document created with base64 (Storage unavailable):', docRef.id, 'nativeResolution: 2K');
                break; // Success, exit retry loop
              } catch (saveError) {
                lastError = saveError;
                retries--;
                const attemptsRemaining = retries;
                console.warn(`⚠️ Firestore save attempt failed, ${attemptsRemaining} retries remaining:`, saveError);
                if (retries > 0) {
                  // Exponential backoff: wait 500ms, 1000ms, 2000ms
                  const delay = (4 - attemptsRemaining) * 500;
                  await new Promise(resolve => setTimeout(resolve, delay));
                }
              }
            }
            
            if (!docRef) {
              throw lastError || new Error('Failed to save mockup to Firestore after retries');
            }
          }
          
          // Verify the document was actually saved by reading it back
          try {
            const docRefForGet = doc(db, 'mockups', docRef.id);
            const savedDoc = await getDoc(docRefForGet);
            if (savedDoc.exists()) {
              const savedData = savedDoc.data();
              console.log('✅ Mockup document verified in Firestore:', {
                id: docRef.id,
                imageUrl: savedData.imageUrl ? savedData.imageUrl.substring(0, 50) + '...' : 'MISSING',
                imageData: savedData.imageData ? 'present' : 'null',
                category: savedData.category,
                deviceType: savedData.deviceType,
                createdAt: savedData.createdAt ? 'present' : 'MISSING',
                userId: savedData.userId
              });
            } else {
              console.error('❌ CRITICAL: Document was created but does not exist when read back!');
            }
          } catch (verifyError) {
            console.error('❌ Error verifying saved document:', verifyError);
          }
          
          // Also wait a moment and verify the document exists AND is queryable
          // This helps ensure the listener picks up the new document
          setTimeout(async () => {
            try {
              const verifySnapshot = await getDoc(doc(db, 'mockups', docRef.id));
              if (verifySnapshot.exists()) {
                const verifyData = verifySnapshot.data();
                console.log('✅ Mockup confirmed in Firestore after save, ID:', docRef.id);
                console.log('✅ Mockup data:', {
                  userId: verifyData.userId,
                  imageUrl: verifyData.imageUrl ? verifyData.imageUrl.substring(0, 50) : 'missing',
                  createdAt: verifyData.createdAt ? 'present' : 'missing'
                });
                
                // Also verify it's queryable by checking if it appears in a query
                const verifyQuery = query(
                  collection(db, 'mockups'),
                  where('userId', '==', user.uid),
                  where('__name__', '==', docRef.id)
                );
                const { getDocs } = await import('firebase/firestore');
                const querySnapshot = await getDocs(verifyQuery);
                if (querySnapshot.empty) {
                  console.error('❌ CRITICAL: Mockup exists but is NOT queryable! This means it won\'t appear in the feed.');
                } else {
                  console.log('✅ Mockup is queryable - should appear in feed');
                }
              } else {
                console.error('❌ Mockup not found in Firestore after save!');
              }
            } catch (e) {
              console.warn('Could not verify mockup in Firestore:', e);
            }
          }, 1000); // Increased delay to ensure Firestore has fully committed
          
          console.log('✅ Mockup document saved successfully:', {
            id: docRef.id,
            imageUrl: imageUrl.substring(0, 50) + '...',
            imageData: firestoreImageData ? 'present' : 'null',
            category: category,
            deviceType: deviceType
          });
          
          // Validate that authenticated users don't have blob URLs
          if (imageUrl && imageUrl.startsWith('blob:')) {
            console.error('❌ CRITICAL: Authenticated user has blob URL! This should not happen:', imageUrl);
            console.error('❌ This will cause display issues. Converting to base64 fallback...');
            
            // Convert blob URL to base64 as emergency fallback
            try {
              const response = await fetch(imageUrl);
              const blob = await response.blob();
              const base64 = await blobToBase64(blob);
              // blobToBase64 already returns a full data URL, so use it directly
              imageUrl = base64;
              firestoreImageData = base64;
              console.log('✅ Converted blob URL to base64 data URL');
            } catch (conversionError) {
              console.error('❌ Failed to convert blob URL to base64:', conversionError);
              throw new Error('Blob URL detected for authenticated user - this should not happen');
            }
          }
          
          // Update the existing local mockup with Firestore document ID and Storage URL
          // This replaces the temporary mockup we added earlier
          console.log('✅ Mockup saved to Firebase, updating local state with Firestore ID');
          
          // Final safety check: Ensure imageUrl is never a blob URL
          if (imageUrl && imageUrl.startsWith('blob:')) {
            console.error('❌ CRITICAL: Blob URL in imageUrl before updating local state! Replacing with base64.');
            imageUrl = base64Image;
            firestoreImageData = base64Image;
          }
          
          // Update the mockup object with the Firebase Storage URL and Firestore document ID
          // CRITICAL: Use the Firestore document ID so the listener can properly match and merge
          // IMPORTANT: ALWAYS preserve the current displayed URL (base64) to prevent image reload
          // The Storage URL is only used for Firestore persistence, NOT for display
          // This prevents the image from reloading after Firebase Storage upload
          const currentDisplayUrl = newMockup.imageUrl || newMockup.url;
          
          const updatedMockup = {
            ...newMockup,
            id: docRef.id, // Use Firestore document ID instead of temporary ID
            stableKey: newMockup.stableKey || `mockup-${Date.now()}-${category}-${deviceType}`, // Preserve stable key
            // CRITICAL: Always preserve the current displayed URL to prevent reload
            // Keep using base64 data URL for display, Storage URL is only for Firestore persistence
            url: currentDisplayUrl, // Keep current base64 URL to prevent reload
            imageUrl: currentDisplayUrl, // Keep current base64 URL to prevent reload
            // Store Storage URL separately for reference, but don't use it for display
            storageUrl: imageUrl.startsWith('http') ? imageUrl : null, // Store URL separately for future use
            imageData: firestoreImageData || newMockup.imageData, // Preserve imageData for enhancement capability
            createdAt: new Date(), // Use Date for local display (Firestore has serverTimestamp)
            userId: user.uid // Ensure userId is set for proper filtering
          };
          
          console.log('✅ Updating local mockup with Firestore ID:', docRef.id, 'imageUrl type:', imageUrl.startsWith('data:') ? 'base64' : imageUrl.startsWith('http') ? 'storage' : 'unknown', 'preview:', imageUrl.substring(0, 50));
          
          // Update the existing temporary mockup in local state
          // Find and replace the temporary mockup with the Firestore-saved version
          setGeneratedMockups(prev => {
            // CRITICAL: Find the temporary mockup BEFORE filtering it out
            // It might have replaced a placeholder, so check both tempId and stableKey
            const tempMockup = prev.find(m => m.id === tempId || m.stableKey === updatedMockup.stableKey);
            const tempIndex = tempMockup ? prev.findIndex(m => m.id === tempId || m.stableKey === updatedMockup.stableKey) : -1;
            
            // Check if listener already added this mockup (shouldn't happen immediately, but possible)
            const listenerVersion = prev.find(m => m.id === docRef.id);
            if (listenerVersion) {
              console.log('⚠️ Mockup already in state (listener added it), keeping listener version');
              // Keep the listener version but ensure it has the correct data and preserve current URL
              return prev.map(m => {
                if (m.id === docRef.id) {
                  // Preserve the current displayed URL if it's base64
                  const currentUrl = listenerVersion.url || listenerVersion.imageUrl;
                  const shouldPreserve = currentUrl && currentUrl.startsWith('data:');
                  return {
                    ...listenerVersion,
                    ...updatedMockup,
                    url: shouldPreserve ? currentUrl : updatedMockup.url,
                    imageUrl: shouldPreserve ? currentUrl : updatedMockup.imageUrl
                  };
                }
                return m;
              })
                .filter(m => m.id !== tempId && m.id !== placeholderId);
            }
            
            // If we found the temporary mockup, replace it at the same position
            if (tempIndex !== -1 && tempMockup) {
              // Replace at the same position to maintain visual continuity
              // CRITICAL: ALWAYS preserve the current displayed URL to prevent reload
              // Never change the URL - keep using base64 data URL for display
              const smoothUpdate = {
                ...updatedMockup,
                stableKey: tempMockup.stableKey || updatedMockup.stableKey, // Preserve stable key from current mockup
                // ALWAYS preserve current displayed URL to prevent image reload
                url: tempMockup.url || updatedMockup.url, // Keep current base64 URL
                imageUrl: tempMockup.imageUrl || updatedMockup.imageUrl, // Keep current base64 URL
                // Store Storage URL separately for reference, but don't use it for display
                storageUrl: imageUrl.startsWith('http') ? imageUrl : (tempMockup.storageUrl || null)
              };
              
              // Replace at the same position, removing duplicates
              const updated = prev.map((m, idx) => {
                // Skip duplicates (but keep the one we're replacing)
                if (m.id === docRef.id || (m.id === tempId && idx !== tempIndex) || m.id === placeholderId) {
                  return null; // Mark for removal
                }
                // Replace the temp mockup at its position
                if (idx === tempIndex) {
                  return smoothUpdate;
                }
                return m;
              }).filter(m => m !== null); // Remove nulls
              
              console.log('✅ Replacing temporary mockup with Firestore-saved version at position', tempIndex, '(preserving base64 URL to prevent reload)');
              return updated;
            }
            
            // Neither temp nor Firestore version found, add to beginning
            // But first remove duplicates
            const withoutDuplicates = prev.filter(m => m.id !== docRef.id && m.id !== tempId && m.id !== placeholderId);
            console.log('⚠️ Temporary mockup not found, adding Firestore-saved version to beginning');
            return [updatedMockup, ...withoutDuplicates];
          });
          
          // Also ensure the listener will pick it up by checking if it's in the query
          // This is a safety check to ensure the mockup appears even if listener misses it
          setTimeout(async () => {
            try {
              const { getDocs } = await import('firebase/firestore');
              const checkQuery = query(
                collection(db, 'mockups'),
                where('userId', '==', user.uid),
                limit(1)
              );
              const checkSnapshot = await getDocs(checkQuery);
              const hasMockup = checkSnapshot.docs.some(d => d.id === docRef.id);
              if (!hasMockup) {
                console.warn('⚠️ New mockup not found in simple query - this might cause issues on refresh');
              } else {
                console.log('✅ New mockup confirmed in query - will appear on refresh');
              }
            } catch (e) {
              console.warn('Could not verify mockup in query:', e);
            }
          }, 2000);
          
          // Verify the document was saved correctly by waiting a moment for Firestore to sync
          // This ensures the listener will pick it up on refresh
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Track successful mockup generation
          trackEvent('generate_mockup', {
            category: category,
            device_type: deviceType,
            file_count: fileCount,
            mockup_number: generatedMockups.length + 1
          });
          
          // Processing state already reset when mockup was added to feed
          // Generation already marked as completed above
          // Just ensure timeout is cleared (already done above, but safety check)
          if (!generationCompleted) {
            generationCompleted = true;
          }
        } catch (error) {
          console.error('❌ Error saving mockup to Firestore:', error);
          console.error('❌ Error details:', {
            name: error.name,
            message: error.message,
            code: error.code,
            stack: error.stack
          });
          
          // Check for specific error types and provide more helpful messages
          let errorMessage = 'Failed to save mockup. Please try again.';
          
          if (error.code === 'invalid-argument') {
            if (error.message.includes('longer than') || error.message.includes('bytes')) {
              errorMessage = 'Image is too large to save. The mockup was generated but cannot be saved to the cloud. It will only be available locally.';
            } else {
              errorMessage = 'Invalid data format. Please try generating the mockup again.';
            }
          } else if (error.code === 'permission-denied') {
            errorMessage = 'Permission denied. Please sign in again and try again.';
          } else if (error.code === 'unavailable' || error.code === 'deadline-exceeded') {
            errorMessage = 'Service temporarily unavailable. The mockup was generated but cannot be saved right now. It will only be available locally.';
          } else if (error.message.includes('Storage')) {
            errorMessage = 'Storage service error. The mockup was generated but cannot be saved. It will only be available locally.';
          }
          
          console.error('❌ Final error message for user:', errorMessage);
          
          // Show user-friendly error message
          alert(errorMessage);
          
          // If Firebase save fails, fall back to local state update
          // But warn the user that it won't persist across refreshes
          console.warn('⚠️ Saving to local state only - mockup will not persist across page refreshes');
          
          // Check if mockup already exists (from placeholder replacement) before adding
          setGeneratedMockups(prev => {
            // Check if newMockup already exists (replaced placeholder)
            const exists = prev.some(m => m.id === newMockup.id || 
              (m.id === tempId) || 
              (m.url === newMockup.url && m.category === newMockup.category));
            
            if (exists) {
              console.log('⚠️ Mockup already in state, not adding duplicate');
              return prev;
            }
            return [newMockup, ...prev];
          });
          
          // Mark generation as completed to prevent timeout
          generationCompleted = true;
          
          // Track successful mockup generation (even if Firebase save failed)
          trackEvent('generate_mockup', {
            category: category,
            device_type: deviceType,
            file_count: fileCount,
            mockup_number: generatedMockups.length + 1,
            save_failed: true,
            error_code: error.code || 'unknown',
            error_message: error.message?.substring(0, 100) || 'unknown'
          });
        }
      } else if (user && !db) {
        console.warn('⚠️ User authenticated but Firestore not available, skipping save');
        // Update local state when Firebase is not available
        // Check if mockup already exists (from placeholder replacement) before adding
        setGeneratedMockups(prev => {
          const exists = prev.some(m => m.id === newMockup.id || 
            (m.id === tempId) || 
            (m.url === newMockup.url && m.category === newMockup.category));
          
          if (exists) {
            console.log('⚠️ Mockup already in state, not adding duplicate');
            return prev;
          }
          return [newMockup, ...prev];
        });
        
        // Track successful mockup generation
        trackEvent('generate_mockup', {
          category: category,
          device_type: deviceType,
          file_count: fileCount,
          mockup_number: generatedMockups.length + 1
        });
        
        // Mark generation as completed to prevent timeout
        generationCompleted = true;
      } else {
        // Update local state when user is not authenticated
        // Check if mockup already exists (from placeholder replacement) before adding
        setGeneratedMockups(prev => {
          const exists = prev.some(m => m.id === newMockup.id || 
            (m.id === tempId) || 
            (m.url === newMockup.url && m.category === newMockup.category));
          
          if (exists) {
            console.log('⚠️ Mockup already in state, not adding duplicate');
            return prev;
          }
          return [newMockup, ...prev];
        });
        
        // Track successful mockup generation
        trackEvent('generate_mockup', {
          category: category,
          device_type: deviceType,
          file_count: fileCount,
          mockup_number: generatedMockups.length + 1
        });
        
        // Mark generation as completed to prevent timeout
        generationCompleted = true;
      }
      // setImageUrl(newMockupUrl); // Keep current image for display
      } catch (error) {
      console.error('❌ Error during generation:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        origin: window.location.origin
      });
      
      // Mark generation as completed to prevent timeout
      generationCompleted = true;
      
      // Remove placeholder on error
      setGeneratedMockups(prev => prev.filter(m => m.id !== placeholderId));
      
      // Provide more specific error messages
      let errorMessage = 'Generation failed';
      if (error.message.includes('Failed to create image URL')) {
        errorMessage = 'Failed to process image. Please try again.';
      } else if (error.message.includes('Request timed out') || error.message.includes('exceeded its time limit') || error.message.includes('taking longer than expected')) {
        errorMessage = error.message; // Use the detailed timeout message from the API call
      } else if (error.message.includes('Server error')) {
        errorMessage = 'Server error. Please check your connection and try again.';
      } else if (error.message.includes('timed out')) {
        errorMessage = error.message.includes('server function') ? error.message : 'Generation timed out. The server may be busy. Please try again.';
      } else if (error.message.includes('aborted') || error.message.includes('AbortError')) {
        errorMessage = 'Request was cancelled or timed out. Please try again.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else {
        errorMessage = `Generation failed: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      // Always clear timeout and reset processing state
      clearTimeout(timeoutId);
      setProcessing(false);
      
      // Final cleanup: remove placeholder if it still exists
      if (!generationCompleted) {
        console.warn('⚠️ Generation completed but completion flag not set, cleaning up placeholder');
        setGeneratedMockups(prev => prev.filter(m => m.id !== placeholderId && !m.isPlaceholder));
      }
    }
  }, [file, files, showMockupExhausted, setShowPricing, setProcessing, user, category, deviceType, showDevice, showBokeh, showHands, showNotch, viewAngle, aspectRatio, fullPrompt, buildExtraPrompt, deductMockup, mockupCount, showMockupGenerated, setGeneratedMockups, deviceNames, deviceTypes, processing, generatedMockups.length, callServerEnhanceAPI, pipeline, selectedModel]);

  // Handle Enter key to trigger Generate Mockup
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Check if Enter key is pressed and no modal is open
      if (event.key === 'Enter' && !showCustomModal && !showDeleteModal && !showPricing && !showSuccessModal) {
        // Check if we can generate (same conditions as button)
        if (canGenerate && !processing) {
          event.preventDefault();
          onGenerate();
        }
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyPress);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [canGenerate, processing, showCustomModal, showDeleteModal, showPricing, showSuccessModal, onGenerate]);

  // Custom Prompt Modal Component
  const CustomPromptModal = () => {
    const textareaRef = useRef(null);
    
    // Auto-focus when modal opens
    useEffect(() => {
      if (showCustomModal && textareaRef.current) {
        textareaRef.current.focus();
        // Set cursor to end of text
        const length = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(length, length);
      }
    }, [showCustomModal]); // eslint-disable-line react-hooks/exhaustive-deps
    
    const handleChange = (e) => {
      setCustomPrompt(e.target.value);
    };
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-surface-3 rounded-lg p-6 w-full max-w-md mx-4">
          <h3 className="text-lg font-semibold text-foreground mb-4">Custom Style Prompt</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Enter your custom style description. This will be used to create a unique mockup environment.
          </p>
          <textarea
            ref={textareaRef}
            defaultValue={customPrompt}
            onChange={handleChange}
            placeholder="e.g., A futuristic space station with neon lights and metallic surfaces..."
            className="w-full h-32 p-3 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-ring bg-surface-2 text-foreground"
            autoFocus
          />
          <div className="flex gap-3 mt-6">
            <Button
              onClick={() => {
                setShowCustomModal(false);
                setCustomPrompt('');
              }}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const currentValue = textareaRef.current?.value || '';
                if (currentValue.trim()) {
                  setCustomPrompt(currentValue);
                  setCategory('custom');
                  setShowCustomModal(false);
                }
              }}
              className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
              disabled={!customPrompt.trim()}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Edit Prompt Modal Component
  const EditPromptModal = () => {
    const textareaRef = useRef(null);
    
    // Auto-focus when modal opens and clear textarea
    useEffect(() => {
      if (showEditPromptModal && textareaRef.current) {
        textareaRef.current.value = '';
        textareaRef.current.focus();
      }
    }, [showEditPromptModal]); // eslint-disable-line react-hooks/exhaustive-deps
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-surface-3 rounded-lg p-6 w-full max-w-md mx-4">
          <h3 className="text-lg font-semibold text-foreground mb-4">Edit Mockup with Prompt</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Enter a custom prompt to edit this mockup. The AI will use your prompt to modify the existing mockup.
          </p>
          <textarea
            ref={textareaRef}
            data-edit-prompt-textarea
            placeholder="e.g., Add Rubber duck or other object to your scene"
            className="w-full h-32 p-3 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-ring bg-surface-2 text-foreground"
            style={{ 
              direction: 'ltr', 
              textAlign: 'left',
              unicodeBidi: 'embed'
            }}
            dir="ltr"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            autoFocus
            onKeyDown={(e) => {
              // Trigger Generate on Enter (but allow Shift+Enter for new line)
              if (e.key === 'Enter' && !e.shiftKey && !processing) {
                e.preventDefault();
                handleEditPromptGenerate();
              }
            }}
          />
          <div className="flex gap-3 mt-6">
            <Button
              onClick={() => {
                if (textareaRef.current) {
                  textareaRef.current.value = '';
                }
                setShowEditPromptModal(false);
                setEditingMockup(null);
              }}
              variant="outline"
              className="flex-1"
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditPromptGenerate}
              className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
              disabled={processing}
            >
              {processing ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                  <span>Generating...</span>
                </div>
              ) : (
                'Generate'
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Color Modal Component
  const ColorModal = () => {
    const [customHex, setCustomHex] = useState('');
    const [isCustomHex, setIsCustomHex] = useState(false);
    
    const colors = [
      { value: 'blue', label: 'Blue', hex: '#3B82F6', gradient: 'from-blue-400 to-blue-600' },
      { value: 'red', label: 'Red', hex: '#EF4444', gradient: 'from-red-400 to-red-600' },
      { value: 'green', label: 'Green', hex: '#10B981', gradient: 'from-green-400 to-green-600' },
      { value: 'yellow', label: 'Yellow', hex: '#F59E0B', gradient: 'from-yellow-400 to-yellow-600' },
      { value: 'purple', label: 'Purple', hex: '#8B5CF6', gradient: 'from-purple-400 to-purple-600' },
      { value: 'pink', label: 'Pink', hex: '#EC4899', gradient: 'from-pink-400 to-pink-600' },
      { value: 'orange', label: 'Orange', hex: '#F97316', gradient: 'from-orange-400 to-orange-600' },
      { value: 'cyan', label: 'Cyan', hex: '#06B6D4', gradient: 'from-cyan-400 to-cyan-600' },
      { value: 'teal', label: 'Teal', hex: '#14B8A6', gradient: 'from-teal-400 to-teal-600' },
      { value: 'indigo', label: 'Indigo', hex: '#6366F1', gradient: 'from-indigo-400 to-indigo-600' },
      { value: 'rose', label: 'Rose', hex: '#F43F5E', gradient: 'from-rose-400 to-rose-600' },
      { value: 'emerald', label: 'Emerald', hex: '#059669', gradient: 'from-emerald-400 to-emerald-600' },
    ];

    const handleColorClick = (colorValue) => {
      setSelectedColor(colorValue);
      setIsCustomHex(false);
      setCustomHex('');
    };

    const handleCustomHexChange = (e) => {
      const value = e.target.value;
      setCustomHex(value);
      if (value.match(/^#[0-9A-Fa-f]{6}$/)) {
        setSelectedColor(value);
        setIsCustomHex(true);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-surface-3 rounded-lg p-6 w-full max-w-md mx-4">
          <h3 className="text-lg font-semibold text-foreground mb-4">Choose Color Environment</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Select a color for the mockup environment. The device will be shown levitating with realistic shadows in a clean colored background.
          </p>
          <div className="grid grid-cols-4 gap-4 mb-4">
            {colors.map((color) => (
              <button
                key={color.value}
                onClick={() => handleColorClick(color.value)}
                className={`relative flex flex-col items-center gap-2 p-2 rounded-lg transition-all ${
                  selectedColor === color.value && !isCustomHex
                    ? 'ring-2 ring-ring ring-offset-2 ring-offset-background'
                    : 'hover:bg-muted'
                }`}
              >
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${color.gradient} shadow-lg`}></div>
                <span className="text-xs font-medium text-foreground">{color.label}</span>
                {selectedColor === color.value && !isCustomHex && (
                  <div className="absolute top-1 right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">Or enter custom hex color:</label>
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={customHex}
                onChange={handleCustomHexChange}
                placeholder="#3B82F6"
                maxLength={7}
                className={`flex-1 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-surface-2 text-foreground ${
                  customHex && customHex.match(/^#[0-9A-Fa-f]{6}$/)
                    ? 'ring-2 ring-green-500'
                    : customHex
                    ? 'ring-2 ring-red-500'
                    : ''
                }`}
              />
              {customHex && customHex.match(/^#[0-9A-Fa-f]{6}$/) && (
                <div 
                  className="w-10 h-10 rounded-lg shadow-lg"
                  style={{ backgroundColor: customHex }}
                ></div>
              )}
            </div>
            {customHex && !customHex.match(/^#[0-9A-Fa-f]{6}$/) && (
              <p className="text-xs text-red-500 mt-1">Please enter a valid hex color (e.g., #3B82F6)</p>
            )}
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={() => {
                setShowColorModal(false);
              }}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setCategory('color');
                setShowColorModal(false);
              }}
              className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
              disabled={!selectedColor}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    );
  };


  // Landing page component
  const LandingPage = () => {
    const { isDarkMode } = useDarkMode();
    return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-surface-1/80 backdrop-blur-md flex-shrink-0 sticky top-0 z-50">
        <div className="w-full pl-6 pr-4 sm:pl-6 sm:pr-6 lg:pl-6 lg:pr-6">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center">
              <img 
                src={isDarkMode ? "/RandomMockups-Logo-LightMode.png" : "/RandomMockups-Logo.png"} 
                alt="RandomMockups Logo" 
                className="mr-1.5"
                style={{ height: '40px', width: 'auto' }}
              />
              <div 
                className="text-xl font-semibold text-foreground cursor-pointer hover:text-muted-foreground transition-colors"
                onClick={() => window.location.reload()}
              >
                RandomMockups
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <DarkModeToggle />
              <button
                onClick={signInWithGoogleHeader}
                disabled={headerAuthLoading}
                className={`h-9 px-3 rounded-md text-sm font-medium transition-colors ${
                  headerAuthLoading 
                    ? 'cursor-not-allowed text-muted-foreground bg-muted' 
                    : 'text-foreground hover:text-foreground bg-surface-2 hover:bg-muted'
                }`}
              >
                {headerAuthLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin"></div>
                    <span>Signing in...</span>
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Hero Section */}
      <div className="relative">
        <div className="max-w-[1200px] mx-auto px-6 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left Section - Marketing Content */}
            <div className="space-y-4 md:space-y-8 flex flex-col items-center lg:items-start">
              {/* Subtitle */}
              <div>
                <p className="text-base md:text-lg text-muted-foreground mb-2 md:mb-4">You do not need to be a designer to create random mockups</p>
              </div>
              
              {/* Main Headline */}
              <div>
                <h1 className="text-[clamp(2.5rem,8vw,5rem)] lg:text-8xl font-bold text-foreground leading-tight tracking-tight w-full text-center lg:text-left">
                  Create Pro Mockups
                </h1>
              </div>
              
              {/* Features List */}
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                    <svg className="w-3 h-3 md:w-4 md:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm md:text-lg text-muted-foreground">Generate random mockups of your website instantly</p>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                    <svg className="w-3 h-3 md:w-4 md:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm md:text-lg text-muted-foreground">Showcase your SaaS and Website templates</p>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 md:w-6 md:h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                    <svg className="w-3 h-3 md:w-4 md:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm md:text-lg text-muted-foreground">Add multiple screenshots to create one scene</p>
                </div>
              </div>
              
              {/* CTA Button */}
              <div className="pt-2 md:pt-4">
                <div className="inline-block">
                  <button
                    onClick={() => {
                      trackEvent('cta_click', {
                        button_location: 'upper',
                        button_text: 'Generate AI Mockups now'
                      });
                      signInWithGoogle();
                    }}
                    disabled={authLoading}
                    className={`lickable-btn px-8 py-4 text-lg font-medium ${
                      authLoading 
                        ? 'cursor-not-allowed' 
                        : ''
                    }`}
                  >
                    {authLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                        <span>Signing in...</span>
                      </div>
                    ) : (
                      'Generate AI Mockups now'
                    )}
                  </button>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    First 3 credits free
                  </p>
                </div>
              </div>
            </div>
            
            {/* Right Section - Interactive Card Stack */}
            <div className="relative mt-8 lg:mt-0 flex justify-center">
              <CardStack />
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-background py-16 lg:py-24">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center md:text-center" style={{ marginBottom: '104px' }}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 text-left md:text-center">
              How to generate Random Mockups
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto text-left md:text-center">
              Transform your screenshots into stunning random mockups in just three simple steps with Random Mockups
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12 md:gap-8 lg:gap-12">
            {/* Step 1 */}
            <div className="flex flex-col md:flex-col items-start md:items-center text-left md:text-center">
              <div className="flex md:flex-col items-start md:items-center gap-6 md:gap-0 w-full">
                <div className="relative w-32 h-32 md:w-full md:max-w-sm md:h-48 flex items-start md:items-center justify-center flex-shrink-0">
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-10 w-16 h-16 bg-gray-800 text-white rounded-full flex items-center justify-center text-2xl font-bold z-10">
                    1
                  </div>
                  <img 
                    src="/steps/Step1@2x.png"
                    alt="Upload your images"
                    className="w-full h-full object-contain relative z-20"
                  />
                </div>
                <div className="flex-1 md:text-center flex flex-col justify-start md:justify-center">
                  <h4 className="text-xl font-semibold text-foreground mb-4 text-left md:text-center -mt-4 md:mt-0">Upload your images</h4>
                  <p className="text-muted-foreground text-left md:text-center">
                    Simply drag and drop your website screenshots or upload them from your device. We support all common image formats.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Step 2 */}
            <div className="flex flex-col md:flex-col items-start md:items-center text-left md:text-center">
              <div className="flex md:flex-col items-start md:items-center gap-6 md:gap-0 w-full">
                <div className="relative w-32 h-32 md:w-full md:max-w-sm md:h-48 flex items-start md:items-center justify-center flex-shrink-0">
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-10 w-16 h-16 bg-gray-800 text-white rounded-full flex items-center justify-center text-2xl font-bold z-10">
                    2
                  </div>
                  <img 
                    src="/steps/Step2@2x.png"
                    alt="Choose the style and settings"
                    className="w-full h-full object-contain relative z-20"
                  />
                </div>
                <div className="flex-1 md:text-center flex flex-col justify-start md:justify-center">
                  <h4 className="text-xl font-semibold text-foreground mb-4 text-left md:text-center -mt-4 md:mt-0">Choose the style and settings</h4>
                  <p className="text-muted-foreground text-left md:text-center">
                    Select from our curated collection of device frames, backgrounds, and styles to match your brand aesthetic.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Step 3 */}
            <div className="flex flex-col md:flex-col items-start md:items-center text-left md:text-center">
              <div className="flex md:flex-col items-start md:items-center gap-6 md:gap-0 w-full">
                <div className="relative w-32 h-32 md:w-full md:max-w-sm md:h-48 flex items-start md:items-center justify-center flex-shrink-0">
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-10 w-16 h-16 bg-gray-800 text-white rounded-full flex items-center justify-center text-2xl font-bold z-10">
                    3
                  </div>
                  <img 
                    src="/steps/Step3@2x.png"
                    alt="Generate mockups"
                    className="w-full h-full object-contain relative z-20"
                  />
                </div>
                <div className="flex-1 md:text-center flex flex-col justify-start md:justify-center">
                  <h4 className="text-xl font-semibold text-foreground mb-4 text-left md:text-center -mt-4 md:mt-0">Generate random mockups</h4>
                  <p className="text-muted-foreground text-left md:text-center">
                    Watch as our AI transforms your screenshots into professional random mockups in seconds. Download and share your random mockups instantly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Cards Section */}
      <div className="bg-background py-16 lg:py-24">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Create Custom Mockups fast
            </h3>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Experience the next generation of AI-powered mockup generation
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr">
            {/* Feature Card 1 - Cinematic Quality */}
            <div className="bg-card text-card-foreground rounded-[24px] shadow-lg overflow-hidden relative lg:row-span-1 min-h-[280px]">
              <img 
                src="/mockupsConvertedResized/mockup_1760831308420s.png"
                alt="Cinematic Quality"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Light gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              {/* White text overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h4 className="text-lg font-bold text-white mb-3">
                  Cinematic Quality
                </h4>
                <p className="text-white/90 text-sm leading-relaxed">
                  Much better than from NanoBanana or other image gen models by default.
                </p>
              </div>
            </div>

            {/* Feature Card 2 - Automatic Mode */}
            <div className="bg-card text-card-foreground rounded-[24px] shadow-lg overflow-hidden relative lg:row-span-1 min-h-[280px]">
              <img 
                src="/bento/mockup_1760815254425.png"
                alt="Automatic Mode"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Light gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              {/* White text overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h4 className="text-lg font-bold text-white mb-3">
                  Automatic Mode
                </h4>
                <p className="text-white/90 text-sm leading-relaxed">
                  Recognizes content of the screenshot and generates environment accordingly.
                </p>
              </div>
            </div>

            {/* Feature Card 3 - Multi Compositions (spans 2 columns, 2 rows) */}
            <div className="bg-card text-card-foreground rounded-[24px] shadow-lg overflow-hidden lg:col-span-2 lg:row-span-2 relative min-h-[280px] lg:min-h-[580px]">
              <img 
                src="/bento/mockup_1760717561993.png"
                alt="Multi Compositions"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Light gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              {/* White text overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h4 className="text-lg font-bold text-white mb-3">
                  Multi Compositions
                </h4>
                <p className="text-white/90 text-sm leading-relaxed">
                  Create stunning compositions from multiple screenshots.
                </p>
              </div>
            </div>

            {/* Feature Card 4 - All Devices (spans 2 columns) */}
            <div className="bg-card text-card-foreground rounded-[24px] shadow-lg overflow-hidden lg:col-span-2 relative min-h-[280px]">
              <img 
                src="/bento/mockup_1760436092913.png"
                alt="All Possible Devices"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Light gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              {/* White text overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h4 className="text-lg font-bold text-white mb-3">
                  All Possible Devices
                </h4>
                <p className="text-white/90 text-sm leading-relaxed">
                  Including computers, phones, wide displays, and more.
                </p>
              </div>
            </div>

            {/* Feature Card 5 - Reasonable Pricing */}
            <div className="bg-card text-card-foreground rounded-[24px] shadow-lg overflow-hidden relative lg:row-span-1 min-h-[280px]">
              <img 
                src="/bento/mockup_1760567757293.png"
                alt="Affordable Pricing"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Light gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              {/* White text overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h4 className="text-lg font-bold text-white mb-3">
                  Reasonable Pricing
                </h4>
                <p className="text-white/90 text-sm leading-relaxed">
                  No subscription necessary, mockups for few cents €.
                </p>
              </div>
            </div>

            {/* Feature Card 6 - Enhance to 2K/4K */}
            <div className="bg-card text-card-foreground rounded-[24px] shadow-lg overflow-hidden relative lg:row-span-1 min-h-[280px]">
              <img 
                src="/bento/mockup_1760614194891.png"
                alt="Enhance to 2K/4K"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Light gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              {/* White text overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h4 className="text-lg font-bold text-white mb-3">
                  Enhance to 2K or 4K
                </h4>
                <p className="text-white/90 text-sm leading-relaxed">
                  Upscale when you like the picture and need it in high res.
                </p>
              </div>
            </div>

            {/* Feature Card 7 - Change your prompt */}
            <div className="bg-card text-card-foreground rounded-[24px] shadow-lg overflow-hidden lg:col-span-2 relative min-h-[280px]">
              <img 
                src="/bento/mockup_1760650685053.png"
                alt="Change your prompt"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Light gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              {/* White text overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h4 className="text-lg font-bold text-white mb-3">
                  Change your prompt
                </h4>
                <p className="text-white/90 text-sm leading-relaxed">
                  Customize the environment and style to match your vision.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Try It Section */}
      <div className="bg-background py-16 lg:py-24">
        <div className="max-w-[1200px] mx-auto px-6">
          {/* Transformation Section */}
          <div className="mb-16">
            <div className="text-center">
              {/* Combined Headlines with Image */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-8 mb-8">
                <h3 className="text-4xl md:text-4xl lg:text-5xl font-bold text-foreground text-center sm:text-left">
                  Screenshots
                </h3>
                <div className="relative max-w-[120px] md:max-w-[160px] lg:max-w-[200px]">
                  <img 
                    src="/Fileformockup.png"
                    alt="Original screenshot"
                    className="w-full h-auto rounded-lg"
                  />
                </div>
                <h3 className="text-4xl md:text-4xl lg:text-5xl font-bold text-foreground text-center sm:text-left">
                  into Mockups
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Width Carousel Section */}
      <div className="w-full -mt-56">
        <AutoCarousel />
      </div>

      {/* CTA Section */}
      <div className="bg-background py-16 lg:py-24">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center py-16 md:py-20 lg:py-24">
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Create Realistic Mockups in seconds
            </h3>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Transform your screenshots into stunning mockups
            </p>
            <button
              onClick={() => {
                trackEvent('cta_click', {
                  button_location: 'lower',
                  button_text: 'Start generating now'
                });
                signInWithGoogle();
              }}
              disabled={authLoading}
              className={`lickable-btn px-8 py-4 text-lg font-medium ${
                authLoading 
                  ? 'cursor-not-allowed' 
                  : ''
              }`}
            >
              {authLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                  <span>Signing in...</span>
                </div>
              ) : (
                'Start generating now'
              )}
            </button>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              First 3 credits free
            </p>
          </div>
        </div>
      </div>

      {/* App Screenshot Section */}
      <div className="bg-background -mt-30 lg:-mt-40 pt-0 pb-0 mb-0">
        <div className="max-w-[1200px] mx-auto px-6 pb-0 -mb-0">
          <div className="flex justify-center">
            <img 
              ref={imageRef}
              src="/APP@2x.png"
              alt="RandomMockups App Interface"
              className="w-full max-w-[1200px] h-auto"
              style={{
                transform: 'perspective(1000px) rotateX(30deg) translateY(10px)',
                transformStyle: 'preserve-3d',
                backfaceVisibility: 'hidden',
                WebkitTransform: 'perspective(1000px) rotateX(30deg) translateY(10px)',
                WebkitTransformStyle: 'preserve-3d',
                WebkitBackfaceVisibility: 'hidden',
                transformOrigin: 'center center',
                WebkitTransformOrigin: 'center center'
              }}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-surface-1 py-8">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-muted-foreground space-y-2 md:space-y-0">
            <div>
              All rights reserved
            </div>
            <div className="flex space-x-4">
              <button 
                onClick={() => {
                  setShowUpdates(true);
                  window.history.pushState({}, '', '/updates');
                }}
                className="hover:text-foreground transition-colors"
              >
                Updates
              </button>
              <button 
                onClick={() => {
                  setShowPricing(true);
                  window.history.pushState({}, '', '/pricing');
                }}
                className="hover:text-foreground transition-colors"
              >
                Pricing
              </button>
              <button 
                onClick={() => {
                  setShowTerms(true);
                  window.history.pushState({}, '', '/terms');
                }}
                className="hover:text-foreground transition-colors"
              >
                Terms
              </button>
              <button 
                onClick={() => {
                  setShowPrivacy(true);
                  window.history.pushState({}, '', '/privacy');
                }}
                className="hover:text-foreground transition-colors"
              >
                Privacy
              </button>
              <button 
                onClick={onShowCookiePreferences}
                className="hover:text-foreground transition-colors"
              >
                Cookie Settings
              </button>
            </div>
            <div>
              Made with 🍌 by <a href="https://x.com/txh" target="_blank" rel="noopener noreferrer" className="font-medium hover:text-foreground transition-colors">@txh</a>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-foreground border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show updates page if requested (accessible to everyone)
  if (showUpdates) {
    return (
      <UpdatesPage 
        onClose={() => {
          setShowUpdates(false);
          window.history.pushState({}, '', '/');
        }}
        onSignIn={signInWithGoogle}
        user={user}
        onShowPricing={() => {
          setShowUpdates(false);
          setShowPricing(true);
        }}
        onShowTerms={() => {
          setShowUpdates(false);
          setShowTerms(true);
        }}
        onShowPrivacy={() => {
          setShowUpdates(false);
          setShowPrivacy(true);
        }}
        onShowCookiePreferences={onShowCookiePreferences}
        mockupCount={mockupCount}
        onLogout={handleLogout}
      />
    );
  }

  // Show terms page if requested (accessible to everyone)
  if (showTerms) {
    return (
      <TermsPage 
        onClose={() => {
          setShowTerms(false);
          window.history.pushState({}, '', '/');
        }}
        onSignIn={signInWithGoogle}
        user={user}
        onShowPricing={() => {
          setShowTerms(false);
          setShowPricing(true);
        }}
        onShowPrivacy={() => {
          setShowTerms(false);
          setShowPrivacy(true);
        }}
        onShowCookiePreferences={onShowCookiePreferences}
        onShowUpdates={() => {
          setShowTerms(false);
          setShowUpdates(true);
        }}
        mockupCount={mockupCount}
        onLogout={handleLogout}
      />
    );
  }

  // Show privacy page if requested (accessible to everyone)
  if (showPrivacy) {
    return (
      <PrivacyPolicyPage 
        onClose={() => {
          setShowPrivacy(false);
          window.history.pushState({}, '', '/');
        }}
        onSignIn={signInWithGoogle}
        user={user}
        onShowPricing={() => {
          setShowPrivacy(false);
          setShowPricing(true);
        }}
        onShowTerms={() => {
          setShowPrivacy(false);
          setShowTerms(true);
        }}
        onShowCookiePreferences={onShowCookiePreferences}
        onShowUpdates={() => {
          setShowPrivacy(false);
          setShowUpdates(true);
        }}
        mockupCount={mockupCount}
        onLogout={handleLogout}
      />
    );
  }

  // Show success page if payment was completed
  if (showSuccessPage) {
    return (
      <SuccessPage 
        onClose={() => {
          setShowSuccessPage(false);
          window.history.pushState({}, '', '/');
        }} 
      />
    );
  }

  // Show pricing page if requested (accessible to everyone)
  if (showPricing) {
    return (
      <PricingPage 
        user={user} 
        onClose={() => {
          setShowPricing(false);
          window.history.pushState({}, '', '/');
        }} 
        onPurchase={handlePurchase}
        mockupCount={mockupCount}
        onLogout={handleLogout}
        onSignIn={signInWithGoogle}
        fromLandingPage={!user}
        onShowCookiePreferences={onShowCookiePreferences}
        onShowUpdates={() => {
          setShowPricing(false);
          setShowUpdates(true);
        }}
        onShowTerms={() => {
          setShowPricing(false);
          setShowTerms(true);
        }}
        onShowPrivacy={() => {
          setShowPricing(false);
          setShowPrivacy(true);
        }}
        showSuccessModal={showSuccessModal}
        setShowSuccessModal={setShowSuccessModal}
        successModalData={successModalData}
        setSuccessModalData={setSuccessModalData}
        addMockups={addMockups}
      />
    );
  }

  // Show landing page if not authenticated
  if (!user) {
    return <LandingPage />;
  }


  return (
    <div 
      className="w-full min-h-screen flex flex-col bg-background"
      onDragEnter={handleGlobalDragEnter}
      onDragOver={handleGlobalDragOver}
      onDragLeave={handleGlobalDragLeave}
      onDrop={handleGlobalDrop}
    >
      {/* Custom Prompt Modal */}
      {showCustomModal && <CustomPromptModal />}
      
      {/* Color Modal */}
      {showColorModal && <ColorModal />}
      
      {/* Edit Prompt Modal */}
      {showEditPromptModal && <EditPromptModal />}
      
      {/* Feedback Modal */}
      <FeedbackModal 
        showFeedbackModal={showFeedbackModal}
        setShowFeedbackModal={setShowFeedbackModal}
        feedbackCategory={feedbackCategory}
        setFeedbackCategory={setFeedbackCategory}
        feedbackName={feedbackName}
        setFeedbackName={setFeedbackName}
        feedbackEmail={feedbackEmail}
        setFeedbackEmail={setFeedbackEmail}
        feedbackSubject={feedbackSubject}
        setFeedbackSubject={setFeedbackSubject}
        hasFeedbackText={hasFeedbackText}
        setHasFeedbackText={setHasFeedbackText}
        feedbackLoading={feedbackLoading}
        setFeedbackLoading={setFeedbackLoading}
        user={user}
        showError={showError}
        showSuccess={showSuccess}
        setSuccessModalData={setSuccessModalData}
        setShowSuccessModal={setShowSuccessModal}
      />
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div 
          ref={deleteModalRef}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleDeleteConfirm();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              handleDeleteCancel();
            }
          }}
          tabIndex={-1}
        >
          <div className="bg-surface-3 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">Delete Mockup</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to delete this mockup? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-sm font-medium text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Image Confirmation Modal */}
      {showDeleteImageModal && imageToDelete && (
        <div 
          ref={deleteImageModalRef}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              confirmDeleteImage();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setShowDeleteImageModal(false);
              setImageToDelete(null);
            }
          }}
          tabIndex={-1}
        >
          <div className="bg-surface-3 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Delete {imageToDelete.imageType === 'enhanced' ? 'Enhanced' : 'Original'} Image
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              {imageToDelete.imageType === 'enhanced' 
                ? 'Are you sure you want to delete the enhanced version? The original will be kept.'
                : imageToDelete.mockup.enhancedImageUrl || imageToDelete.mockup.enhancedImageData
                  ? 'Are you sure you want to delete the original? The enhanced version will become the new original.'
                  : 'Are you sure you want to delete this mockup? This action cannot be undone.'
              }
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteImageModal(false);
                  setImageToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteImage}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Header with user profile */}
      <div className="bg-surface-1/80 backdrop-blur-md flex-shrink-0 sticky top-0 z-50">
        <div className="w-full pl-6 pr-4 sm:pl-6 sm:pr-6 lg:pl-6 lg:pr-6">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center">
              <img 
                src={isDarkMode ? "/RandomMockups-Logo-LightMode.png" : "/RandomMockups-Logo.png"} 
                alt="RandomMockups Logo" 
                className="mr-1.5"
                style={{ height: '40px', width: 'auto' }}
              />
              <div 
                className="text-xl font-semibold text-foreground cursor-pointer hover:text-muted-foreground transition-colors"
                onClick={() => window.location.reload()}
              >
                RandomMockups
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <DarkModeToggle />
              <button
                onClick={() => {
                  setShowPricing(true);
                  window.history.pushState({}, '', '/pricing');
                }}
                className="flex items-center gap-2 text-sm font-medium text-foreground hover:bg-muted transition-colors p-2 rounded-lg"
              >
                <ProfilePicture user={user} className="w-6 h-6 rounded-full" />
                <span className={`bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center ${
                  mockupCount >= 10 
                    ? 'rounded-full px-2 py-1 min-w-8 h-6' 
                    : 'rounded-full w-5 h-5'
                }`}>
                  {mockupCount}
                </span>
              </button>
              <div className="relative dropdown-container">
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="w-9 h-9 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                
                {showUserDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-auto min-w-fit bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 focus:outline-none">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowPricing(true);
                          window.history.pushState({}, '', '/pricing');
                          setShowUserDropdown(false);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground transition-colors whitespace-nowrap"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                        </svg>
                        <span>Credits</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowFeedbackModal(true);
                          setShowUserDropdown(false);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground transition-colors whitespace-nowrap"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        <span>Feedback</span>
                      </button>
                      <button
                        onClick={() => {
                          handleLogout();
                          setShowUserDropdown(false);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors whitespace-nowrap"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="block md:hidden flex flex-col flex-1 overflow-hidden">
        <div className="px-4 pt-2 pb-20 flex-1 overflow-y-auto">
          <div className="mb-6">
          </div>
          
          <Card>
            <CardContent className="p-6 pt-6">
              <div className="space-y-6">
                <UploadArea 
                  key={`upload-${file ? file.name : files.length > 0 ? files.map(f => f.name).join('-') : 'empty'}`}
                  onFile={handleFileSelected} 
                  onFiles={handleFilesSelected}
                  onAppendFiles={handleAppendFiles}
                  selectedFile={file} 
                  selectedFiles={files}
                  onRemoveFile={handleRemoveFile}
                  onReorderFiles={handleReorderFiles}
                  deviceNames={deviceNames}
                  isGlobalDragging={isGlobalDragging}
                />
                
                <div className="space-y-4">
                  <div>
                    <button
                      onClick={() => setIsStyleExpanded(!isStyleExpanded)}
                      className="flex items-center justify-between w-full p-3 text-sm font-medium text-foreground bg-surface-2 hover:bg-surface-1 rounded-lg transition-all"
                    >
                      <span>Style</span>
                      <div className="flex items-center gap-2">
                        <span>{CATEGORIES.find(c => c.value === category)?.label}</span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isStyleExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    
                    {isStyleExpanded && (
                      <div className="mt-2 grid grid-cols-3 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {CATEGORIES.map((c) => (
                          <button
                            key={c.value}
                            onClick={() => {
                              if (c.value === 'custom') {
                                setShowCustomModal(true);
                              } else if (c.value === 'color') {
                                setShowColorModal(true);
                              } else {
                                setCategory(c.value);
                              }
                            }}
                            className={`p-2 text-xs font-medium rounded-lg transition-all ${
                              category === c.value
                                ? 'bg-surface-1 text-foreground'
                                : 'bg-surface-2 text-muted-foreground/60 hover:bg-surface-1 hover:text-foreground'
                            }`}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <button
                      onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
                      className="flex items-center justify-between w-full p-3 text-sm font-medium text-foreground bg-surface-2 hover:bg-surface-1 rounded-lg transition-all"
                    >
                      <span>Settings</span>
                      <div className="flex items-center gap-2">
                        <span>{getDeviceTypeDisplayName(deviceType)} & {getViewAngleDisplayName(viewAngle)}</span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isSettingsExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    
                    {isSettingsExpanded && (
                      <div className="mt-2 space-y-4">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-foreground">Device Type</label>
                          <Select value={deviceType} onValueChange={setDeviceType}>
                            <SelectTrigger className="w-24 h-8 px-2 border-0 shadow-none focus:ring-0 focus:ring-offset-0" style={{width: '6rem', minWidth: '6rem', maxWidth: '6rem'}}>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent side="bottom" align="end" className="dark:bg-black dark:border-gray-700 focus:outline-none">
                              <SelectItem value="desktop">Desktop</SelectItem>
                              <SelectItem value="ultrawide">Wide</SelectItem>
                              <SelectItem value="tablet">Tablet</SelectItem>
                              <SelectItem value="mobile">Mobile</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-foreground">View angle</label>
                          <Select value={viewAngle} onValueChange={setViewAngle}>
                            <SelectTrigger className="w-20 h-8 px-2 border-0 shadow-none focus:ring-0 focus:ring-offset-0" style={{width: '5rem', minWidth: '5rem', maxWidth: '5rem'}}>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent side="bottom" align="end" className="dark:bg-black dark:border-gray-700 focus:outline-none">
                              <SelectItem value="right-angled">Right</SelectItem>
                              <SelectItem value="left-angled">Left</SelectItem>
                              <SelectItem value="frontal">Front</SelectItem>
                              <SelectItem value="isometric">Isometric</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-4">
                          <div className="flex flex-row items-center justify-between gap-3">
                            <label className="text-sm font-medium text-foreground whitespace-nowrap flex-shrink-0">Show Device</label>
                            <Switch
                              checked={showDevice}
                              onCheckedChange={setShowDevice}
                            />
                          </div>
                          
                          <div className="flex flex-row items-center justify-between gap-3">
                            <label className="text-sm font-medium text-foreground whitespace-nowrap flex-shrink-0">Show Bokeh</label>
                            <Switch
                              checked={showBokeh}
                              onCheckedChange={setShowBokeh}
                            />
                          </div>
                          
                          <div className="flex flex-row items-center justify-between gap-3">
                            <label className="text-sm font-medium text-foreground whitespace-nowrap flex-shrink-0">Show Hands</label>
                            <Switch
                              checked={showHands}
                              onCheckedChange={setShowHands}
                            />
                          </div>
                          
                          <div className="flex flex-row items-center justify-between gap-3">
                            <label className="text-sm font-medium text-foreground whitespace-nowrap flex-shrink-0">Show Notch</label>
                            <Switch
                              checked={showNotch}
                              onCheckedChange={setShowNotch}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <button
                      onClick={() => setIsExportExpanded(!isExportExpanded)}
                      className="flex items-center justify-between w-full p-3 text-sm font-medium text-foreground bg-surface-2 hover:bg-surface-1 rounded-lg transition-all"
                    >
                      <span>Export</span>
                      <div className="flex items-center gap-2">
                        <span>{aspectRatio === '4:3' ? 'Landscape' : aspectRatio === '1:1' ? 'Square' : 'Portrait'}</span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isExportExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    
                    {isExportExpanded && (
                      <div className="mt-2">
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { value: '4:3', label: 'Landscape' },
                            { value: '1:1', label: 'Square' },
                            { value: '3:4', label: 'Portrait' },
                          ].map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setAspectRatio(option.value)}
                              className={`p-2 text-xs font-medium rounded-lg transition-all ${
                                aspectRatio === option.value
                                  ? 'bg-surface-1 text-foreground'
                                  : 'bg-surface-2 text-muted-foreground/60 hover:bg-surface-1 hover:text-foreground'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <button
                      onClick={() => setIsPromptExpanded(!isPromptExpanded)}
                      className="flex items-center justify-between w-full p-3 text-sm font-medium text-foreground bg-surface-2 hover:bg-surface-1 rounded-lg transition-all"
                    >
                      <span>Prompt</span>
                      <div className="flex items-center gap-2">
                        <span>{fullPrompt ? 'Custom' : 'Auto'}</span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isPromptExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    
                    {isPromptExpanded && (
                      <div className="mt-2">
                        <textarea
                          value={fullPrompt || buildExtraPrompt(category, deviceType, showDevice, viewAngle, file ? 1 : files.length, showBokeh, showHands, showNotch, aspectRatio)}
                          onChange={(e) => setFullPrompt(e.target.value)}
                          placeholder="Enter your custom prompt here..."
                          className="w-full p-3 text-sm bg-surface-1 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          rows={8}
                        />
                        <div className="mt-2 text-xs text-muted-foreground">
                          {fullPrompt ? fullPrompt.length : buildExtraPrompt(category, deviceType, showDevice, viewAngle, file ? 1 : files.length, showBokeh, showHands, showNotch, aspectRatio).length} characters
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <button
                      onClick={() => setIsModelExpanded(!isModelExpanded)}
                      className="flex items-center justify-between w-full p-3 text-sm font-medium text-foreground bg-surface-2 hover:bg-surface-1 rounded-lg transition-all"
                    >
                      <span>Model</span>
                      <div className="flex items-center gap-2">
                        <span>{getModelDisplayName(selectedModel)}</span>
                        <span className="text-xs bg-background text-foreground w-5 h-5 flex items-center justify-center rounded-full font-medium">
                          {getModelCredits(selectedModel)}
                        </span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isModelExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    
                    {isModelExpanded && (
                      <div className="mt-2 space-y-2">
                        <button
                          onClick={() => {
                            setSelectedModel(MODELS.GEMINI_3_1_FLASH.id);
                            localStorage.setItem('selectedModel', MODELS.GEMINI_3_1_FLASH.id);
                            setIsModelExpanded(false);
                            trackEvent('select_model', { model: MODELS.GEMINI_3_1_FLASH.id });
                          }}
                          className={`w-full p-3 text-xs font-medium rounded-lg transition-all ${
                            selectedModel === MODELS.GEMINI_3_1_FLASH.id
                              ? 'bg-surface-1 text-foreground'
                              : 'bg-surface-2 text-muted-foreground/60 hover:bg-surface-1 hover:text-foreground'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex flex-col items-start text-left">
                              <span className="text-left">{MODELS.GEMINI_3_1_FLASH.fullName}</span>
                              <span className="text-xs text-muted-foreground/70 mt-0.5 text-left">{MODELS.GEMINI_3_1_FLASH.bestFor}</span>
                            </div>
                            <span className="text-xs bg-background text-foreground w-5 h-5 flex items-center justify-center rounded-full font-medium">1</span>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setSelectedModel(MODELS.GEMINI_2_5_FLASH.id);
                            localStorage.setItem('selectedModel', MODELS.GEMINI_2_5_FLASH.id);
                            setIsModelExpanded(false);
                            trackEvent('select_model', { model: MODELS.GEMINI_2_5_FLASH.id });
                          }}
                          className={`w-full p-3 text-xs font-medium rounded-lg transition-all ${
                            selectedModel === MODELS.GEMINI_2_5_FLASH.id
                              ? 'bg-surface-1 text-foreground'
                              : 'bg-surface-2 text-muted-foreground/60 hover:bg-surface-1 hover:text-foreground'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex flex-col items-start text-left">
                              <span className="text-left">{MODELS.GEMINI_2_5_FLASH.fullName}</span>
                              <span className="text-xs text-muted-foreground/70 mt-0.5 text-left">{MODELS.GEMINI_2_5_FLASH.bestFor}</span>
                            </div>
                            <span className="text-xs bg-background text-foreground w-5 h-5 flex items-center justify-center rounded-full font-medium">1</span>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Pipeline Selector - Hidden for now */}
                  {false && (
                    <div>
                      <button
                        onClick={() => setIsPipelineExpanded(!isPipelineExpanded)}
                        className="flex items-center justify-between w-full p-3 text-sm font-medium text-foreground bg-surface-2 hover:bg-surface-1 rounded-lg transition-all"
                      >
                        <span>Pipeline</span>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1.5">
                            <span>{pipeline === PIPELINES.V1 ? 'Original' : 'Experimental'}</span>
                            <span className="text-xs bg-background text-foreground w-5 h-5 flex items-center justify-center rounded-full font-medium">
                              {pipeline === PIPELINES.V1 ? 1 : 2}
                            </span>
                          </div>
                          <svg
                            className={`w-4 h-4 transition-transform ${isPipelineExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>
                      
                      {isPipelineExpanded && (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          <button
                            onClick={() => {
                              setPipeline(PIPELINES.V1);
                              localStorage.setItem('pipelineSelection', PIPELINES.V1);
                              trackEvent('select_pipeline', { pipeline: PIPELINES.V1 });
                            }}
                            className={`p-2 text-xs font-medium rounded-lg transition-all ${
                              pipeline === PIPELINES.V1
                                ? 'bg-surface-1 text-foreground'
                                : 'bg-surface-2 text-muted-foreground/60 hover:bg-surface-1 hover:text-foreground'
                            }`}
                          >
                            <div className="flex items-center justify-center gap-1.5">
                              <span>Original</span>
                              <span className="text-xs bg-background text-foreground w-5 h-5 flex items-center justify-center rounded-full font-medium">1</span>
                            </div>
                          </button>
                          {/* Experimental pipeline hidden for now */}
                          {false && (
                            <button
                              onClick={() => {
                                setPipeline(PIPELINES.V3);
                                localStorage.setItem('pipelineSelection', PIPELINES.V3);
                                trackEvent('select_pipeline', { pipeline: PIPELINES.V3 });
                              }}
                              className={`p-2 text-xs font-medium rounded-lg transition-all ${
                                pipeline === PIPELINES.V3
                                  ? 'bg-surface-1 text-foreground'
                                  : 'bg-surface-2 text-muted-foreground/60 hover:bg-surface-1 hover:text-foreground'
                              }`}
                            >
                              <div className="flex items-center justify-center gap-1.5">
                                <span>Experimental</span>
                                <span className="text-xs bg-background text-foreground w-5 h-5 flex items-center justify-center rounded-full font-medium">2</span>
                              </div>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {generatedMockups.length > 0 && (
            <div className="mt-6">
              <div className="grid gap-6">
                {generatedMockups.map((mockup) => (
                  <div key={mockup.id} className="animate-fadeIn">
                    <div className="mb-6 text-sm text-muted-foreground flex justify-between items-start">
                      <div className="relative flex flex-col gap-2">
                        <button
                          onClick={() => setExpandedMockupDetails(expandedMockupDetails === mockup.id ? null : mockup.id)}
                          className={`flex items-center gap-1 text-sm font-medium text-foreground hover:bg-muted transition-colors p-2 rounded-lg w-fit ${expandedMockupDetails === mockup.id ? 'bg-muted' : ''}`}
                        >
                          <span className="text-gray-600 dark:text-white whitespace-nowrap">{CATEGORIES.find(c => c.value === mockup.category)?.label}</span>
                          <svg className={`w-4 h-4 transition-transform ${expandedMockupDetails === mockup.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {expandedMockupDetails === mockup.id && (
                          <div className="absolute left-4 bottom-[68px] flex flex-row flex-wrap items-center gap-2 z-50">
                            <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Angle: {mockup.viewAngle === 'right-angled' ? 'Right' : mockup.viewAngle === 'left-angled' ? 'Left' : mockup.viewAngle === 'frontal' ? 'Frontal' : mockup.viewAngle === 'isometric' ? 'Isometric' : 'Right'}</span>
                            <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Device: {mockup.deviceType === 'mobile' ? 'Mobile' : mockup.deviceType === 'tablet' ? 'Tablet' : mockup.deviceType === 'ultrawide' ? 'Wide' : 'Desktop'}</span>
                            <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Bokeh: {mockup.showBokeh ? 'Yes' : 'No'}</span>
                            <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Hands: {mockup.showHands ? 'Yes' : 'No'}</span>
                            <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Notch: {mockup.showNotch ? 'Yes' : 'No'}</span>
                            <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Show Device: {mockup.showDevice ? 'Yes' : 'No'}</span>
                            <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Aspect: {mockup.aspectRatio === '4:3' ? 'Landscape' : mockup.aspectRatio === '3:4' ? 'Portrait' : mockup.aspectRatio === '1:1' ? 'Square' : (mockup.aspectRatio || 'Landscape')}</span>
                            {mockup.modelName && (
                              <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Model: {mockup.modelName}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* More Dropdown */}
                        <div className="relative dropdown-container">
                          <button
                            onClick={() => setOpenDropdown(openDropdown === mockup.id ? null : mockup.id)}
                            className={`flex items-center gap-1 text-sm font-medium text-foreground hover:bg-muted transition-colors p-2 rounded-lg ${openDropdown === mockup.id ? 'bg-muted' : ''}`}
                          >
                            <span>More</span>
                            <svg className={`w-4 h-4 transition-transform ${openDropdown === mockup.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          
                          {openDropdown === mockup.id && (
                            <div className="absolute right-4 bottom-[68px] w-48 bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                              <div className="py-1">
                                {/* Delete mockup - always first */}
                                <button
                                  onClick={() => {
                                    handleDeleteClick(mockup);
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  <span>Delete</span>
                                </button>
                                
                                {/* Enhance to 2K */}
                                <button
                                  onClick={() => {
                                    handleEnhanceClick(mockup, '2k');
                                    setOpenDropdown(null);
                                  }}
                                  disabled={enhancingMockups.has(mockup.id)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                  </svg>
                                  <span>Enhance 2K</span>
                                  {enhancingMockups.get(mockup.id) === '2k' ? (
                                    <span className="ml-auto flex items-center justify-center w-5 h-5">
                                      <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                    </span>
                                  ) : (
                                    <span className="ml-auto text-xs bg-foreground text-background w-5 h-5 flex items-center justify-center rounded-full font-medium">1</span>
                                  )}
                                </button>
                                
                                {/* Enhance to 4K */}
                                <button
                                  onClick={() => {
                                    handleEnhanceClick(mockup, '4k');
                                    setOpenDropdown(null);
                                  }}
                                  disabled={enhancingMockups.has(mockup.id)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                  </svg>
                                  <span>Enhance 4K</span>
                                  {enhancingMockups.get(mockup.id) === '4k' ? (
                                    <span className="ml-auto flex items-center justify-center w-5 h-5">
                                      <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                    </span>
                                  ) : (
                                    <span className="ml-auto text-xs bg-foreground text-background w-5 h-5 flex items-center justify-center rounded-full font-medium">2</span>
                                  )}
                                </button>
                                
                                {/* Separator */}
                                <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                                
                                {/* Zoom In */}
                                <button
                                  onClick={() => {
                                    handleZoomIn(mockup.id);
                                  }}
                                  disabled={processing}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                                  </svg>
                                  <span>Zoom In</span>
                                  <span className="ml-auto text-xs bg-foreground text-background w-5 h-5 flex items-center justify-center rounded-full font-medium">1</span>
                                </button>
                                
                                {/* Zoom Out */}
                                <button
                                  onClick={() => {
                                    handleZoomOut(mockup.id);
                                  }}
                                  disabled={processing}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                                  </svg>
                                  <span>Zoom Out</span>
                                  <span className="ml-auto text-xs bg-foreground text-background w-5 h-5 flex items-center justify-center rounded-full font-medium">1</span>
                                </button>
                                
                                {/* Edit with Prompt */}
                                <button
                                  onClick={() => {
                                    handleEditWithPrompt(mockup);
                                  }}
                                  disabled={processing}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  <span>Edit with Prompt</span>
                                  <span className="ml-auto text-xs bg-foreground text-background w-5 h-5 flex items-center justify-center rounded-full font-medium">1</span>
                                </button>
                                
                                {/* Separator */}
                                <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                                
                                {/* Download */}
                                <button
                                  onClick={async () => {
                                    setOpenDropdown(null);
                                    try {
                                      const response = await fetch(mockup.url);
                                      if (!response.ok) throw new Error('Download failed');
                                      
                                      const blob = await response.blob();
                                      const blobUrl = window.URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = blobUrl;
                                      link.download = `mockup-${mockup.id}.png`;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      window.URL.revokeObjectURL(blobUrl);
                                    } catch (error) {
                                      console.error('Download failed:', error);
                                      window.open(mockup.url, '_blank');
                                    }
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                  <span>Download</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <MockupCanvas 
                      imageUrl={mockup.url} 
                      category={mockup.category} 
                      deviceType={mockup.deviceType} 
                      showDevice={mockup.showDevice}
                      fileCount={mockup.fileCount || 1}
                      enhancedImageUrl={mockup.enhancedImageUrl}
                      enhancedImageData={mockup.enhancedImageData}
                      isEnhanced={mockup.isEnhanced}
                      enhancedResolution={mockup.enhancedResolution}
                      nativeResolution={mockup.nativeResolution}
                      onDeleteImage={(imageType) => handleDeleteImageClick(mockup, imageType)}
                      mockupId={mockup.id}
                      zoomLevel={getZoomLevel(mockup.id)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Mobile Fixed Generate Button (glossy, no white wrapper) */}
        <div className="fixed bottom-0 left-0 right-0 p-4 z-40 md:hidden">
          {shouldShowGenerateButton && (
            <Button onClick={onGenerate} disabled={!canGenerate} className="lickable-btn w-full h-14 !text-lg font-semibold">
              {processing ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                  <span>Generating</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span>Generate Mockup</span>
                  <span className="text-xs bg-background text-foreground w-5 h-5 flex items-center justify-center rounded-full font-medium">
                    {getModelCredits(selectedModel)}
                  </span>
                </div>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Desktop/Tablet Layout */}
      <div className="hidden md:flex max-w-[1480px] mx-auto w-full flex-1 overflow-hidden">
        {/* Left Sidebar - Fixed width - sits on surface-0 (background) */}
        <div className="w-80 min-w-80 max-w-80 flex-shrink-0">
        <div className="w-80 fixed top-0 bottom-0 flex flex-col pt-[60px]">
          <div ref={sidebarScrollRef} className="px-6 pb-[112px] flex-1 overflow-y-auto max-h-full">
            <div className="space-y-6">
              <div>
              </div>
              
              <div className="space-y-6">
                <UploadArea 
                  key={`upload-${file ? file.name : files.length > 0 ? files.map(f => f.name).join('-') : 'empty'}`}
                  onFile={handleFileSelected} 
                  onFiles={handleFilesSelected}
                  onAppendFiles={handleAppendFiles}
                  selectedFile={file} 
                  selectedFiles={files}
                  onRemoveFile={handleRemoveFile}
                  onReorderFiles={handleReorderFiles}
                  deviceNames={deviceNames}
                  isGlobalDragging={isGlobalDragging}
                />
                
                <div className="space-y-4">
                  <div>
                    <button
                      onClick={() => setIsStyleExpanded(!isStyleExpanded)}
                      className="flex items-center justify-between w-full p-3 text-sm font-medium text-foreground bg-surface-2 hover:bg-surface-1 rounded-lg transition-all"
                    >
                      <span>Style</span>
                      <div className="flex items-center gap-2">
                        <span>{CATEGORIES.find(c => c.value === category)?.label}</span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isStyleExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    
                    {isStyleExpanded && (
                      <div className="mt-2 grid grid-cols-3 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {CATEGORIES.map((c) => (
                          <button
                            key={c.value}
                            onClick={() => {
                              if (c.value === 'custom') {
                                setShowCustomModal(true);
                              } else if (c.value === 'color') {
                                setShowColorModal(true);
                              } else {
                                setCategory(c.value);
                              }
                            }}
                            className={`p-2 text-xs font-medium rounded-lg transition-all ${
                              category === c.value
                                ? 'bg-surface-1 text-foreground'
                                : 'bg-surface-2 text-muted-foreground/60 hover:bg-surface-1 hover:text-foreground'
                            }`}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <button
                      onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
                      className="flex items-center justify-between w-full p-3 text-sm font-medium text-foreground bg-surface-2 hover:bg-surface-1 rounded-lg transition-all"
                    >
                      <span>Settings</span>
                      <div className="flex items-center gap-2">
                        <span>{getDeviceTypeDisplayName(deviceType)} & {getViewAngleDisplayName(viewAngle)}</span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isSettingsExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    
                    {isSettingsExpanded && (
                      <div className="mt-2 space-y-4">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-foreground">Device Type</label>
                          <Select value={deviceType} onValueChange={setDeviceType}>
                            <SelectTrigger className="w-24 h-8 px-2 border-0 shadow-none focus:ring-0 focus:ring-offset-0" style={{width: '6rem', minWidth: '6rem', maxWidth: '6rem'}}>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent side="bottom" align="end" className="dark:bg-black dark:border-gray-700 focus:outline-none">
                              <SelectItem value="desktop">Desktop</SelectItem>
                              <SelectItem value="ultrawide">Wide</SelectItem>
                              <SelectItem value="tablet">Tablet</SelectItem>
                              <SelectItem value="mobile">Mobile</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-foreground">View angle</label>
                          <Select value={viewAngle} onValueChange={setViewAngle}>
                            <SelectTrigger className="w-20 h-8 px-2 border-0 shadow-none focus:ring-0 focus:ring-offset-0" style={{width: '5rem', minWidth: '5rem', maxWidth: '5rem'}}>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent side="bottom" align="end" className="dark:bg-black dark:border-gray-700 focus:outline-none">
                              <SelectItem value="right-angled">Right</SelectItem>
                              <SelectItem value="left-angled">Left</SelectItem>
                              <SelectItem value="frontal">Front</SelectItem>
                              <SelectItem value="isometric">Isometric</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-4">
                          <div className="flex flex-row items-center justify-between gap-3">
                            <label className="text-sm font-medium text-foreground whitespace-nowrap flex-shrink-0">Show Device</label>
                            <Switch
                              checked={showDevice}
                              onCheckedChange={setShowDevice}
                            />
                          </div>
                          
                          <div className="flex flex-row items-center justify-between gap-3">
                            <label className="text-sm font-medium text-foreground whitespace-nowrap flex-shrink-0">Show Bokeh</label>
                            <Switch
                              checked={showBokeh}
                              onCheckedChange={setShowBokeh}
                            />
                          </div>
                          
                          <div className="flex flex-row items-center justify-between gap-3">
                            <label className="text-sm font-medium text-foreground whitespace-nowrap flex-shrink-0">Show Hands</label>
                            <Switch
                              checked={showHands}
                              onCheckedChange={setShowHands}
                            />
                          </div>
                          
                          <div className="flex flex-row items-center justify-between gap-3">
                            <label className="text-sm font-medium text-foreground whitespace-nowrap flex-shrink-0">Show Notch</label>
                            <Switch
                              checked={showNotch}
                              onCheckedChange={setShowNotch}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <button
                      onClick={() => setIsExportExpanded(!isExportExpanded)}
                      className="flex items-center justify-between w-full p-3 text-sm font-medium text-foreground bg-surface-2 hover:bg-surface-1 rounded-lg transition-all"
                    >
                      <span>Export</span>
                      <div className="flex items-center gap-2">
                        <span>{aspectRatio === '4:3' ? 'Landscape' : aspectRatio === '1:1' ? 'Square' : 'Portrait'}</span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isExportExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    
                    {isExportExpanded && (
                      <div className="mt-2">
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { value: '4:3', label: 'Landscape' },
                            { value: '1:1', label: 'Square' },
                            { value: '3:4', label: 'Portrait' },
                          ].map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setAspectRatio(option.value)}
                              className={`p-2 text-xs font-medium rounded-lg transition-all ${
                                aspectRatio === option.value
                                  ? 'bg-surface-1 text-foreground'
                                  : 'bg-surface-2 text-muted-foreground/60 hover:bg-surface-1 hover:text-foreground'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <button
                      onClick={() => setIsPromptExpanded(!isPromptExpanded)}
                      className="flex items-center justify-between w-full p-3 text-sm font-medium text-foreground bg-surface-2 hover:bg-surface-1 rounded-lg transition-all"
                    >
                      <span>Prompt</span>
                      <div className="flex items-center gap-2">
                        <span>{fullPrompt ? 'Custom' : 'Auto'}</span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isPromptExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    
                    {isPromptExpanded && (
                      <div className="mt-2">
                        <textarea
                          value={fullPrompt || buildExtraPrompt(category, deviceType, showDevice, viewAngle, file ? 1 : files.length, showBokeh, showHands, showNotch, aspectRatio)}
                          onChange={(e) => setFullPrompt(e.target.value)}
                          placeholder="Enter your custom prompt here..."
                          className="w-full p-3 text-sm bg-surface-1 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          rows={8}
                        />
                        <div className="mt-2 text-xs text-muted-foreground">
                          {fullPrompt ? fullPrompt.length : buildExtraPrompt(category, deviceType, showDevice, viewAngle, file ? 1 : files.length, showBokeh, showHands, showNotch, aspectRatio).length} characters
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <button
                      onClick={() => setIsModelExpanded(!isModelExpanded)}
                      className="flex items-center justify-between w-full p-3 text-sm font-medium text-foreground bg-surface-2 hover:bg-surface-1 rounded-lg transition-all"
                    >
                      <span>Model</span>
                      <div className="flex items-center gap-2">
                        <span>{getModelDisplayName(selectedModel)}</span>
                        <span className="text-xs bg-background text-foreground w-5 h-5 flex items-center justify-center rounded-full font-medium">
                          {getModelCredits(selectedModel)}
                        </span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isModelExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    
                    {isModelExpanded && (
                      <div className="mt-2 space-y-2">
                        <button
                          onClick={() => {
                            setSelectedModel(MODELS.GEMINI_3_1_FLASH.id);
                            localStorage.setItem('selectedModel', MODELS.GEMINI_3_1_FLASH.id);
                            setIsModelExpanded(false);
                            trackEvent('select_model', { model: MODELS.GEMINI_3_1_FLASH.id });
                          }}
                          className={`w-full p-3 text-xs font-medium rounded-lg transition-all ${
                            selectedModel === MODELS.GEMINI_3_1_FLASH.id
                              ? 'bg-surface-1 text-foreground'
                              : 'bg-surface-2 text-muted-foreground/60 hover:bg-surface-1 hover:text-foreground'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex flex-col items-start text-left">
                              <span className="text-left">{MODELS.GEMINI_3_1_FLASH.fullName}</span>
                              <span className="text-xs text-muted-foreground/70 mt-0.5 text-left">{MODELS.GEMINI_3_1_FLASH.bestFor}</span>
                            </div>
                            <span className="text-xs bg-background text-foreground w-5 h-5 flex items-center justify-center rounded-full font-medium">1</span>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setSelectedModel(MODELS.GEMINI_2_5_FLASH.id);
                            localStorage.setItem('selectedModel', MODELS.GEMINI_2_5_FLASH.id);
                            setIsModelExpanded(false);
                            trackEvent('select_model', { model: MODELS.GEMINI_2_5_FLASH.id });
                          }}
                          className={`w-full p-3 text-xs font-medium rounded-lg transition-all ${
                            selectedModel === MODELS.GEMINI_2_5_FLASH.id
                              ? 'bg-surface-1 text-foreground'
                              : 'bg-surface-2 text-muted-foreground/60 hover:bg-surface-1 hover:text-foreground'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex flex-col items-start text-left">
                              <span className="text-left">{MODELS.GEMINI_2_5_FLASH.fullName}</span>
                              <span className="text-xs text-muted-foreground/70 mt-0.5 text-left">{MODELS.GEMINI_2_5_FLASH.bestFor}</span>
                            </div>
                            <span className="text-xs bg-background text-foreground w-5 h-5 flex items-center justify-center rounded-full font-medium">1</span>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Pipeline Selector - Hidden for now */}
                  {false && (
                    <div>
                      <button
                        onClick={() => setIsPipelineExpanded(!isPipelineExpanded)}
                        className="flex items-center justify-between w-full p-3 text-sm font-medium text-foreground bg-surface-2 hover:bg-surface-1 rounded-lg transition-all"
                      >
                        <span>Pipeline</span>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1.5">
                            <span>{pipeline === PIPELINES.V1 ? 'Original' : 'Experimental'}</span>
                            <span className="text-xs bg-background text-foreground w-5 h-5 flex items-center justify-center rounded-full font-medium">
                              {pipeline === PIPELINES.V1 ? 1 : 2}
                            </span>
                          </div>
                          <svg
                            className={`w-4 h-4 transition-transform ${isPipelineExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>
                      
                      {isPipelineExpanded && (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          <button
                            onClick={() => {
                              setPipeline(PIPELINES.V1);
                              localStorage.setItem('pipelineSelection', PIPELINES.V1);
                              trackEvent('select_pipeline', { pipeline: PIPELINES.V1 });
                            }}
                            className={`p-2 text-xs font-medium rounded-lg transition-all ${
                              pipeline === PIPELINES.V1
                                ? 'bg-surface-1 text-foreground'
                                : 'bg-surface-2 text-muted-foreground/60 hover:bg-surface-1 hover:text-foreground'
                            }`}
                          >
                            <div className="flex items-center justify-center gap-1.5">
                              <span>Original</span>
                              <span className="text-xs bg-background text-foreground w-5 h-5 flex items-center justify-center rounded-full font-medium">1</span>
                            </div>
                          </button>
                          {/* Experimental pipeline hidden for now */}
                          {false && (
                            <button
                              onClick={() => {
                                setPipeline(PIPELINES.V3);
                                localStorage.setItem('pipelineSelection', PIPELINES.V3);
                                trackEvent('select_pipeline', { pipeline: PIPELINES.V3 });
                              }}
                              className={`p-2 text-xs font-medium rounded-lg transition-all ${
                                pipeline === PIPELINES.V3
                                  ? 'bg-surface-1 text-foreground'
                                  : 'bg-surface-2 text-muted-foreground/60 hover:bg-surface-1 hover:text-foreground'
                              }`}
                            >
                              <div className="flex items-center justify-center gap-1.5">
                                <span>Experimental</span>
                                <span className="text-xs bg-background text-foreground w-5 h-5 flex items-center justify-center rounded-full font-medium">2</span>
                              </div>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Inline Generate button directly under Aspect Ratio when sidebar fits */}
                  {!isSidebarOverflowing && (
                    <div className="!mt-8">
                      {shouldShowGenerateButton && (
                        <Button 
                          onClick={onGenerate} 
                          disabled={!canGenerate} 
                          className={`lickable-btn w-full h-14 !text-lg font-semibold ${
                            !canGenerate 
                              ? 'opacity-80' 
                              : ''
                          }`}
                        >
                          {processing ? (
                            <div className="flex items-center gap-2">
                              <span>Generating</span>
                              <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span>Generate Mockup</span>
                              <span className="ml-1 text-xs bg-primary-foreground text-primary dark:bg-foreground dark:text-background w-5 h-5 flex items-center justify-center rounded-full font-medium">
                                {getModelCredits(selectedModel)}
                              </span>
                            </div>
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        

        {/* Fixed Generate button when sidebar overflows - positioned outside scrollable area */}
        {isSidebarOverflowing && (
          <div className="fixed bottom-0 left-0 w-80 px-6 pb-6 z-50 bg-transparent">
            {shouldShowGenerateButton && (
              <Button 
                onClick={onGenerate} 
                disabled={!canGenerate} 
                className={`lickable-btn w-full h-14 !text-lg font-semibold ${
                  !canGenerate 
                    ? 'opacity-80' 
                    : ''
                }`}
              >
                {processing ? (
                  <div className="flex items-center gap-2">
                    <span>Generating</span>
                    <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>Generate Mockup</span>
                    <span className="ml-1 text-xs bg-primary-foreground text-primary dark:bg-foreground dark:text-background w-5 h-5 flex items-center justify-center rounded-full font-medium">
                      {pipeline === PIPELINES.V3 ? 2 : 1}
                    </span>
                  </div>
                )}
              </Button>
            )}
          </div>
        )}
        </div>

        {/* Right Main Area - Elevated content sheet */}
        <div className="flex-1 flex flex-col mr-6 pt-6 pb-6">
          <div className="bg-surface-1 rounded-[24px] shadow-[0_8px_16px_rgba(0,0,0,0.08),0_2px_8px_rgba(0,0,0,0.04)] border border-border/40 flex-1 flex flex-col overflow-hidden">
            <div className="p-6 flex-1 overflow-y-auto">
            {(generatedMockups.length > 0 || processing) ? (
            <div>
              <div className="grid gap-16">
                {generatedMockups.map((mockup) => {
                  // console.log('Rendering mockup:', mockup.id, 'url:', mockup.url, 'imageUrl:', mockup.imageUrl);
                  
                  // Render skeleton for placeholder mockups
                  if (mockup.isPlaceholder) {
                    return (
                      <div key={mockup.id} className="animate-fadeIn">
                        <div className="relative w-full bg-surface-2 rounded-none overflow-hidden animate-pulse-subtle" style={{ aspectRatio: '4/3' }}>
                          {/* Skeleton shimmer effect - darker in light mode, lighter in dark mode */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-400/40 dark:via-surface-3/30 to-transparent animate-shimmer" style={{ 
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 3.5s infinite'
                          }}></div>
                          
                          {/* Generating text - no spinner */}
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                            <span className="text-sm font-medium text-muted-foreground">Generating mockup...</span>
                          </div>
                        </div>
                        <div className="mt-4 text-sm text-muted-foreground flex justify-between items-center">
                          <span>
                            <div className="flex items-center gap-2">
                              <div className="h-5 w-16 bg-surface-2 rounded animate-pulse"></div>
                              <div className="h-5 w-20 bg-surface-2 rounded animate-pulse"></div>
                              <div className="h-5 w-24 bg-surface-2 rounded animate-pulse"></div>
                            </div>
                          </span>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                  <div key={mockup.stableKey || mockup.id} className="animate-fadeIn transition-opacity duration-300">
                    <MockupCanvas 
                      imageUrl={mockup.url} 
                      category={mockup.category} 
                      deviceType={mockup.deviceType} 
                      showDevice={mockup.showDevice}
                      fileCount={mockup.fileCount || 1}
                      enhancedImageUrl={mockup.enhancedImageUrl}
                      enhancedImageData={mockup.enhancedImageData}
                      isEnhanced={mockup.isEnhanced}
                      enhancedResolution={mockup.enhancedResolution}
                      nativeResolution={mockup.nativeResolution}
                      onDeleteImage={(imageType) => handleDeleteImageClick(mockup, imageType)}
                      mockupId={mockup.id}
                      zoomLevel={getZoomLevel(mockup.id)}
                    />
                    <div className="mt-4 text-sm text-muted-foreground flex justify-between items-start">
                      <div className="relative flex flex-col gap-2">
                        <button
                          onClick={() => setExpandedMockupDetails(expandedMockupDetails === mockup.id ? null : mockup.id)}
                          className={`flex items-center gap-1 text-sm font-medium text-foreground hover:bg-muted transition-colors p-2 rounded-lg w-fit ${expandedMockupDetails === mockup.id ? 'bg-muted' : ''}`}
                        >
                          <span className="text-gray-600 dark:text-white whitespace-nowrap">{CATEGORIES.find(c => c.value === mockup.category)?.label}</span>
                          <svg className={`w-4 h-4 transition-transform ${expandedMockupDetails === mockup.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {expandedMockupDetails === mockup.id && (
                          <div className="absolute left-4 bottom-[68px] flex flex-row flex-wrap items-center gap-2 z-50">
                            <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Angle: {mockup.viewAngle === 'right-angled' ? 'Right' : mockup.viewAngle === 'left-angled' ? 'Left' : mockup.viewAngle === 'frontal' ? 'Frontal' : mockup.viewAngle === 'isometric' ? 'Isometric' : 'Right'}</span>
                            <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Device: {mockup.deviceType === 'mobile' ? 'Mobile' : mockup.deviceType === 'tablet' ? 'Tablet' : mockup.deviceType === 'ultrawide' ? 'Wide' : 'Desktop'}</span>
                            <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Bokeh: {mockup.showBokeh ? 'Yes' : 'No'}</span>
                            <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Hands: {mockup.showHands ? 'Yes' : 'No'}</span>
                            <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Notch: {mockup.showNotch ? 'Yes' : 'No'}</span>
                            <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Show Device: {mockup.showDevice ? 'Yes' : 'No'}</span>
                            <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Aspect: {mockup.aspectRatio === '4:3' ? 'Landscape' : mockup.aspectRatio === '3:4' ? 'Portrait' : mockup.aspectRatio === '1:1' ? 'Square' : (mockup.aspectRatio || 'Landscape')}</span>
                            {mockup.modelName && (
                              <span className="bg-gray-200 text-gray-700 dark:bg-black dark:text-gray-300 px-2 py-1 rounded text-xs whitespace-nowrap">Model: {mockup.modelName}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* More Dropdown */}
                        <div className="relative dropdown-container">
                          <button
                            onClick={() => setOpenDropdown(openDropdown === mockup.id ? null : mockup.id)}
                            className={`flex items-center gap-1 text-sm font-medium text-foreground hover:bg-muted transition-colors p-2 rounded-lg ${openDropdown === mockup.id ? 'bg-muted' : ''}`}
                          >
                            <span>More</span>
                            <svg className={`w-4 h-4 transition-transform ${openDropdown === mockup.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          
                          {openDropdown === mockup.id && (
                            <div className="absolute right-4 bottom-[68px] w-48 bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                              <div className="py-1">
                                {/* Delete mockup - always first */}
                                <button
                                  onClick={() => {
                                    handleDeleteClick(mockup);
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  <span>Delete</span>
                                </button>
                                
                                {/* Enhance to 2K */}
                                <button
                                  onClick={() => {
                                    handleEnhanceClick(mockup, '2k');
                                    setOpenDropdown(null);
                                  }}
                                  disabled={enhancingMockups.has(mockup.id)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                  </svg>
                                  <span>Enhance 2K</span>
                                  {enhancingMockups.get(mockup.id) === '2k' ? (
                                    <span className="ml-auto flex items-center justify-center w-5 h-5">
                                      <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                    </span>
                                  ) : (
                                    <span className="ml-auto text-xs bg-foreground text-background w-5 h-5 flex items-center justify-center rounded-full font-medium">1</span>
                                  )}
                                </button>
                                
                                {/* Enhance to 4K */}
                                <button
                                  onClick={() => {
                                    handleEnhanceClick(mockup, '4k');
                                    setOpenDropdown(null);
                                  }}
                                  disabled={enhancingMockups.has(mockup.id)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                  </svg>
                                  <span>Enhance 4K</span>
                                  {enhancingMockups.get(mockup.id) === '4k' ? (
                                    <span className="ml-auto flex items-center justify-center w-5 h-5">
                                      <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                    </span>
                                  ) : (
                                    <span className="ml-auto text-xs bg-foreground text-background w-5 h-5 flex items-center justify-center rounded-full font-medium">2</span>
                                  )}
                                </button>
                                
                                {/* Separator */}
                                <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                                
                                {/* Zoom In */}
                                <button
                                  onClick={() => {
                                    handleZoomIn(mockup.id);
                                  }}
                                  disabled={processing}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                                  </svg>
                                  <span>Zoom In</span>
                                  <span className="ml-auto text-xs bg-foreground text-background w-5 h-5 flex items-center justify-center rounded-full font-medium">1</span>
                                </button>
                                
                                {/* Zoom Out */}
                                <button
                                  onClick={() => {
                                    handleZoomOut(mockup.id);
                                  }}
                                  disabled={processing}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                                  </svg>
                                  <span>Zoom Out</span>
                                  <span className="ml-auto text-xs bg-foreground text-background w-5 h-5 flex items-center justify-center rounded-full font-medium">1</span>
                                </button>
                                
                                {/* Edit with Prompt */}
                                <button
                                  onClick={() => {
                                    handleEditWithPrompt(mockup);
                                  }}
                                  disabled={processing}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  <span>Edit with Prompt</span>
                                  <span className="ml-auto text-xs bg-foreground text-background w-5 h-5 flex items-center justify-center rounded-full font-medium">1</span>
                                </button>
                                
                                {/* Separator */}
                                <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                                
                                {/* Download */}
                                <button
                                  onClick={async () => {
                                    setOpenDropdown(null);
                                    try {
                                      const response = await fetch(mockup.url);
                                      if (!response.ok) throw new Error('Download failed');
                                      
                                      const blob = await response.blob();
                                      const blobUrl = window.URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = blobUrl;
                                      link.download = `mockup-${mockup.id}.png`;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      window.URL.revokeObjectURL(blobUrl);
                                    } catch (error) {
                                      console.error('Download failed:', error);
                                      window.open(mockup.url, '_blank');
                                    }
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                  <span>Download</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
              
              {/* Load More Button */}
              {hasMoreMockups && (
                <div className="mt-8 flex justify-center">
                  <Button
                    onClick={loadMoreMockups}
                    disabled={loadingMore}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-8"
                  >
                    {loadingMore ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                        <span>Loading...</span>
                      </div>
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center flex-1 py-32">
              <div className="text-center">
                <div className="w-64 h-48 mx-auto mb-6">
                  <img 
                    src="/browser-window-banana.png" 
                    alt="Browser Window" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">Ready to create magic?</h3>
                <p className="text-muted-foreground max-w-sm mx-auto">Upload your screenshot and watch it transform<br />into a professional mockup with AI</p>
              </div>
            </div>
          )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-surface-1 py-8">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-muted-foreground space-y-2 md:space-y-0">
            <div>
              All rights reserved
            </div>
            <div className="flex space-x-4">
              <button 
                onClick={() => {
                  setShowUpdates(true);
                  window.history.pushState({}, '', '/updates');
                }}
                className="hover:text-foreground transition-colors"
              >
                Updates
              </button>
              <button 
                onClick={() => {
                  setShowPricing(true);
                  window.history.pushState({}, '', '/pricing');
                }}
                className="hover:text-foreground transition-colors"
              >
                Pricing
              </button>
              <button 
                onClick={() => {
                  setShowTerms(true);
                  window.history.pushState({}, '', '/terms');
                }}
                className="hover:text-foreground transition-colors"
              >
                Terms
              </button>
              <button 
                onClick={() => {
                  setShowPrivacy(true);
                  window.history.pushState({}, '', '/privacy');
                }}
                className="hover:text-foreground transition-colors"
              >
                Privacy
              </button>
              <button 
                onClick={onShowCookiePreferences}
                className="hover:text-foreground transition-colors"
              >
                Cookie Settings
              </button>
            </div>
            <div>
              Made with 🍌 by <a href="https://x.com/txh" target="_blank" rel="noopener noreferrer" className="font-medium hover:text-foreground transition-colors">@txh</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Cookie Preferences Modal Component
const CookiePreferencesModal = ({ isOpen, onClose }) => {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  const handleSavePreferences = () => {
    const consent = {
      functional: true, // Always true
      analytics: analyticsEnabled
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: analyticsEnabled ? 'granted' : 'denied'
      });
    }
    
    onClose();
  };

  const handleAcceptAll = () => {
    setAnalyticsEnabled(true);
    const consent = {
      functional: true,
      analytics: true
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      });
    }
    
    onClose();
  };

  const handleRejectAll = () => {
    setAnalyticsEnabled(false);
    const consent = {
      functional: true,
      analytics: false
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied'
      });
    }
    
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-surface-2 shadow-lg z-50">
      <div className="max-w-[1480px] mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <h1 className="text-lg font-semibold text-foreground">Cookie Preferences</h1>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Functional Cookies */}
        <div className="bg-surface-1 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-foreground mb-1">Functional Cookies</h4>
              <p className="text-sm text-muted-foreground">Essential for the website to function properly</p>
            </div>
            <span className="text-sm text-muted-foreground">Always Active</span>
          </div>
        </div>

        {/* Analytics Cookies */}
        <div className="bg-surface-1 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-foreground mb-1">Analytics Cookies</h4>
              <p className="text-sm text-muted-foreground">Help us understand how visitors interact with our website</p>
            </div>
            <Switch
              checked={analyticsEnabled}
              onCheckedChange={setAnalyticsEnabled}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 justify-end">
          <button
            onClick={handleRejectAll}
            className="px-4 py-2 rounded-lg text-foreground bg-surface-1 hover:bg-muted transition-colors"
          >
            Reject All
          </button>
          <button
            onClick={handleSavePreferences}
            className="px-4 py-2 rounded-lg text-foreground bg-surface-1 hover:bg-muted transition-colors"
          >
            Save Preferences
          </button>
          <button
            onClick={handleAcceptAll}
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};

// Main App component with providers
function App() {
  const [showCookiePreferences, setShowCookiePreferences] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState(null);
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const successModalRef = useRef(null);

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setSuccessModalData(null);
  };

  // Auto-dismiss modal after 3 seconds and start confetti
  useEffect(() => {
    if (showSuccessModal) {
      // Start confetti animation immediately
      try {
        // Create multiple bursts of black confetti
        const duration = 3000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

        function randomInRange(min, max) {
          return Math.random() * (max - min) + min;
        }

        const interval = setInterval(function() {
          const timeLeft = animationEnd - Date.now();

          if (timeLeft <= 0) {
            return clearInterval(interval);
          }

          const particleCount = 50 * (timeLeft / duration);
          
          // Create confetti bursts from behind the modal (center-bottom)
          confetti({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
            colors: ['#000000']
          });
          confetti({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
            colors: ['#000000']
          });
        }, 250);
      } catch (error) {
        console.log('Confetti animation failed:', error);
      }
      
      const dismissTimer = setTimeout(() => {
        handleSuccessModalClose();
      }, 3000);
      
      return () => {
        clearTimeout(dismissTimer);
      };
    }
  }, [showSuccessModal]);

  // Focus management for success modal
  useEffect(() => {
    if (showSuccessModal && successModalRef.current) {
      successModalRef.current.focus();
    }
  }, [showSuccessModal]);
  
  return (
    <ErrorBoundary>
      <UserProvider>
        <DarkModeProvider>
          <NotificationProvider>
            <AppContent 
              onShowCookiePreferences={() => setShowCookiePreferences(true)}
              showSuccessModal={showSuccessModal}
              setShowSuccessModal={setShowSuccessModal}
              successModalData={successModalData}
              setSuccessModalData={setSuccessModalData}
              showSuccessPage={showSuccessPage}
              setShowSuccessPage={setShowSuccessPage}
            />
            <CookiePreferencesModal 
              isOpen={showCookiePreferences} 
              onClose={() => setShowCookiePreferences(false)} 
            />
            
            {/* Success Modal with Confetti */}
            {showSuccessModal && successModalData && (
              <div 
                ref={successModalRef}
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] backdrop-blur-sm"
                onClick={handleSuccessModalClose}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    e.preventDefault();
                    handleSuccessModalClose();
                  }
                }}
                tabIndex={-1}
              >
                <div 
                  className="bg-background border-2 border-green-500/20 rounded-xl p-8 w-full max-w-md mx-4 relative z-10 shadow-2xl animate-in fade-in zoom-in duration-300"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-center">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-foreground mb-3">{successModalData.headline}</h3>
                    <p className="text-muted-foreground mb-6">
                      {successModalData.text}
                    </p>
                    {successModalData.credits && (
                      <div className="mb-6">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 rounded-full">
                          <span className="text-2xl font-bold text-green-600 dark:text-green-400">+{successModalData.credits}</span>
                          <span className="text-sm text-muted-foreground">Credits Added</span>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={handleSuccessModalClose}
                      className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
                    >
                      Get Started
                    </button>
                  </div>
                </div>
              </div>
            )}
          </NotificationProvider>
        </DarkModeProvider>
      </UserProvider>
    </ErrorBoundary>
  );
}

export default App;
